import { BaseEntity, EntitySaveOptions, EntityDeleteOptions, CompositeKey, ValidationResult, ValidationErrorInfo, ValidationErrorType, Metadata, ProviderType, DatabaseProviderBase, RunView } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { z } from "zod";

     
 
/**
 * zod schema definition for the entity Accounting Allocation Rules
 */
export const soundpostAccountingAllocationRuleSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    RuleName: z.string().describe(`
        * * Field Name: RuleName
        * * Display Name: Rule Name
        * * SQL Data Type: nvarchar(200)
        * * Description: Human-readable name for the allocation rule.`),
    RuleType: z.union([z.literal('ContraRevenue'), z.literal('DeferredRevenueCarveOut'), z.literal('DimensionOverride'), z.literal('LiabilityCarveOut'), z.literal('RevenueSplit')]).describe(`
        * * Field Name: RuleType
        * * Display Name: Rule Type
        * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * ContraRevenue
    *   * DeferredRevenueCarveOut
    *   * DimensionOverride
    *   * LiabilityCarveOut
    *   * RevenueSplit
        * * Description: Type of allocation: RevenueSplit, DeferredRevenueCarveOut, DimensionOverride, ContraRevenue, LiabilityCarveOut.`),
    Priority: z.number().describe(`
        * * Field Name: Priority
        * * Display Name: Priority
        * * SQL Data Type: int
        * * Default Value: 100`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    ConditionExpression: z.string().nullable().describe(`
        * * Field Name: ConditionExpression
        * * Display Name: Condition Expression
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON condition tree that determines when this rule applies. Supports AND/OR operators and field-level conditions on product, order, and tag attributes.`),
    AllocationMethod: z.union([z.literal('FixedAmount'), z.literal('Percentage'), z.literal('Remainder')]).describe(`
        * * Field Name: AllocationMethod
        * * Display Name: Allocation Method
        * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * FixedAmount
    *   * Percentage
    *   * Remainder
        * * Description: How to calculate the allocation amount: Percentage (0-100), FixedAmount (dollar amount), or Remainder.`),
    AllocationValue: z.number().describe(`
        * * Field Name: AllocationValue
        * * Display Name: Allocation Value
        * * SQL Data Type: decimal(18, 4)
        * * Default Value: 0
        * * Description: The numeric value for the allocation: percentage (0-100) or fixed dollar amount, depending on AllocationMethod.`),
    TargetGLAccountID: z.string().nullable().describe(`
        * * Field Name: TargetGLAccountID
        * * Display Name: Target GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
        * * Description: The GL account that receives the allocation amount.`),
    TargetDimensionMappings: z.string().nullable().describe(`
        * * Field Name: TargetDimensionMappings
        * * Display Name: Target Dimension Mappings
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON object specifying dimension overrides for the allocation entries, e.g. {"Department": "DimensionID"}.`),
    AffectsSchedule: z.boolean().describe(`
        * * Field Name: AffectsSchedule
        * * Display Name: Affects Schedule
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether this allocation should reduce the revenue recognition schedule amount.`),
    ScheduleAdjustmentMethod: z.union([z.literal('ReducePerPeriod'), z.literal('ReduceTotal')]).nullable().describe(`
        * * Field Name: ScheduleAdjustmentMethod
        * * Display Name: Schedule Adjustment Method
        * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * ReducePerPeriod
    *   * ReduceTotal
        * * Description: How to adjust the schedule: ReduceTotal (subtract from TotalAmount) or ReducePerPeriod (spread reduction across periods).`),
    SourceAccountType: z.union([z.literal('DeferredRevenue'), z.literal('Revenue')]).describe(`
        * * Field Name: SourceAccountType
        * * Display Name: Source Account Type
        * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * DeferredRevenue
    *   * Revenue
        * * Description: Which account type on the standard entry this allocation draws from: Revenue or DeferredRevenue.`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    EffectiveStartDate: z.date().nullable().describe(`
        * * Field Name: EffectiveStartDate
        * * Display Name: Effective Start Date
        * * SQL Data Type: date`),
    EffectiveEndDate: z.date().nullable().describe(`
        * * Field Name: EffectiveEndDate
        * * Display Name: Effective End Date
        * * SQL Data Type: date`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    TargetGLAccount: z.string().nullable().describe(`
        * * Field Name: TargetGLAccount
        * * Display Name: Target GL Account
        * * SQL Data Type: varchar(100)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostAccountingAllocationRuleEntityType = z.infer<typeof soundpostAccountingAllocationRuleSchema>;

/**
 * zod schema definition for the entity Accounting Entries
 */
export const soundpostAccountingEntrySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    EntryDate: z.date().describe(`
        * * Field Name: EntryDate
        * * Display Name: Entry Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date when the accounting entry is effective.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    SourceType: z.string().describe(`
        * * Field Name: SourceType
        * * Display Name: Source Type
        * * SQL Data Type: varchar(50)
        * * Description: Type of source transaction (Order, Payment, Schedule, etc.).`),
    SourceID: z.string().describe(`
        * * Field Name: SourceID
        * * Display Name: Source ID
        * * SQL Data Type: varchar(100)`),
    CurrencyID: z.string().nullable().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)`),
    ExchangeRate: z.number().nullable().describe(`
        * * Field Name: ExchangeRate
        * * Display Name: Exchange Rate
        * * SQL Data Type: decimal(12, 6)
        * * Default Value: 1.0
        * * Description: Exchange rate applied for foreign currency entries.`),
    IsReversal: z.boolean().describe(`
        * * Field Name: IsReversal
        * * Display Name: Is Reversal
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether this is a reversal entry.`),
    ReversalOfEntryID: z.string().nullable().describe(`
        * * Field Name: ReversalOfEntryID
        * * Display Name: Reversal Of Entry ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Entries (vwAccountingEntries.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    PostingGroupID: z.string().nullable().describe(`
        * * Field Name: PostingGroupID
        * * Display Name: Posting Group ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Posting Groups (vwPostingGroups.ID)
        * * Description: Links the accounting entry to its posting group, if posted.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    EntryNumber: z.number().describe(`
        * * Field Name: EntryNumber
        * * Display Name: Entry Number
        * * SQL Data Type: int`),
    AccountingAllocationRuleID: z.string().nullable().describe(`
        * * Field Name: AccountingAllocationRuleID
        * * Display Name: Accounting Allocation Rule ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Allocation Rules (vwAccountingAllocationRules.ID)
        * * Description: Links this accounting entry to the allocation rule that generated it. NULL for standard product-level entries.`),
    ExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System Record ID
        * * SQL Data Type: varchar(100)
        * * Description: External system record identifier.`),
    Currency: z.string().nullable().describe(`
        * * Field Name: Currency
        * * Display Name: Currency
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    RootReversalOfEntryID: z.string().nullable().describe(`
        * * Field Name: RootReversalOfEntryID
        * * Display Name: Root Reversal Of Entry ID
        * * SQL Data Type: uniqueidentifier`),
    ReversalOfEntryIDDepth: z.number().nullable().describe(`
        * * Field Name: ReversalOfEntryIDDepth
        * * Display Name: Reversal Of Entry ID Depth
        * * SQL Data Type: int`),
    ReversalOfEntryIDPath: z.string().nullable().describe(`
        * * Field Name: ReversalOfEntryIDPath
        * * Display Name: Reversal Of Entry ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    ReversalOfEntryIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: ReversalOfEntryIDIsLeaf
        * * Display Name: Reversal Of Entry ID Is Leaf
        * * SQL Data Type: bit`),
    ReversalOfEntryIDChildCount: z.number().nullable().describe(`
        * * Field Name: ReversalOfEntryIDChildCount
        * * Display Name: Reversal Of Entry ID Child Count
        * * SQL Data Type: int`),
});

export type soundpostAccountingEntryEntityType = z.infer<typeof soundpostAccountingEntrySchema>;

/**
 * zod schema definition for the entity Accounting Entry Line Dimensions
 */
export const soundpostAccountingEntryLineDimensionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AccountingEntryLineID: z.string().describe(`
        * * Field Name: AccountingEntryLineID
        * * Display Name: Accounting Entry Line ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Entry Lines (vwAccountingEntryLines.ID)`),
    DimensionTypeID: z.string().describe(`
        * * Field Name: DimensionTypeID
        * * Display Name: Dimension Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Dimension Types (vwDimensionTypes.ID)`),
    DimensionID: z.string().describe(`
        * * Field Name: DimensionID
        * * Display Name: Dimension ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Dimensions (vwDimensions.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    DimensionType: z.string().describe(`
        * * Field Name: DimensionType
        * * Display Name: Dimension Type
        * * SQL Data Type: varchar(50)`),
    Dimension: z.string().describe(`
        * * Field Name: Dimension
        * * Display Name: Dimension
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostAccountingEntryLineDimensionEntityType = z.infer<typeof soundpostAccountingEntryLineDimensionSchema>;

/**
 * zod schema definition for the entity Accounting Entry Lines
 */
export const soundpostAccountingEntryLineSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AccountingEntryID: z.string().describe(`
        * * Field Name: AccountingEntryID
        * * Display Name: Accounting Entry ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Entries (vwAccountingEntries.ID)`),
    LineNumber: z.number().describe(`
        * * Field Name: LineNumber
        * * Display Name: Line Number
        * * SQL Data Type: int
        * * Default Value: 1
        * * Description: Sequential line number within the entry.`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    DebitAmount: z.number().describe(`
        * * Field Name: DebitAmount
        * * Display Name: Debit Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Debit amount (must be 0 if credit amount is > 0).`),
    CreditAmount: z.number().describe(`
        * * Field Name: CreditAmount
        * * Display Name: Credit Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Credit amount (must be 0 if debit amount is > 0).`),
    CurrencyID: z.string().nullable().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)`),
    ExchangeRate: z.number().nullable().describe(`
        * * Field Name: ExchangeRate
        * * Display Name: Exchange Rate
        * * SQL Data Type: decimal(12, 6)
        * * Default Value: 1.0`),
    BaseCurrencyDebitAmount: z.number().nullable().describe(`
        * * Field Name: BaseCurrencyDebitAmount
        * * Display Name: Base Currency Debit Amount
        * * SQL Data Type: decimal(18, 2)
        * * Description: Base currency equivalent of debit amount.`),
    BaseCurrencyCreditAmount: z.number().nullable().describe(`
        * * Field Name: BaseCurrencyCreditAmount
        * * Display Name: Base Currency Credit Amount
        * * SQL Data Type: decimal(18, 2)
        * * Description: Base currency equivalent of credit amount.`),
    SourceLineID: z.string().nullable().describe(`
        * * Field Name: SourceLineID
        * * Display Name: Source Line ID
        * * SQL Data Type: varchar(100)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    GLAccount: z.string().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
    Currency: z.string().nullable().describe(`
        * * Field Name: Currency
        * * Display Name: Currency
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostAccountingEntryLineEntityType = z.infer<typeof soundpostAccountingEntryLineSchema>;

/**
 * zod schema definition for the entity Accounting Periods
 */
export const soundpostAccountingPeriodSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PeriodName: z.string().describe(`
        * * Field Name: PeriodName
        * * Display Name: Period Name
        * * SQL Data Type: varchar(50)
        * * Description: Display name for the accounting period (e.g., January 2025).`),
    StartDate: z.date().describe(`
        * * Field Name: StartDate
        * * Display Name: Start Date
        * * SQL Data Type: datetimeoffset
        * * Description: Start date of the accounting period.`),
    EndDate: z.date().describe(`
        * * Field Name: EndDate
        * * Display Name: End Date
        * * SQL Data Type: datetimeoffset
        * * Description: End date of the accounting period.`),
    IsClosed: z.boolean().describe(`
        * * Field Name: IsClosed
        * * Display Name: Is Closed
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether this period has been closed for accounting.`),
    ClosedDate: z.date().nullable().describe(`
        * * Field Name: ClosedDate
        * * Display Name: Closed Date
        * * SQL Data Type: datetimeoffset`),
    ClosedBy: z.string().nullable().describe(`
        * * Field Name: ClosedBy
        * * Display Name: Closed By
        * * SQL Data Type: varchar(100)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostAccountingPeriodEntityType = z.infer<typeof soundpostAccountingPeriodSchema>;

/**
 * zod schema definition for the entity Accounting Schedules
 */
export const soundpostAccountingScheduleSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrderLineID: z.string().describe(`
        * * Field Name: OrderLineID
        * * Display Name: Order Line ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Order Lines (vwOrderLines.ID)`),
    RecognitionMethod: z.union([z.literal('Fixed Date'), z.literal('Manual'), z.literal('Straight Line')]).describe(`
        * * Field Name: RecognitionMethod
        * * Display Name: Recognition Method
        * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * Fixed Date
    *   * Manual
    *   * Straight Line
        * * Description: Recognition pattern: Fixed Date, Straight Line, or Manual.`),
    StartDate: z.date().describe(`
        * * Field Name: StartDate
        * * Display Name: Start Date
        * * SQL Data Type: datetimeoffset
        * * Description: Start date for revenue recognition.`),
    EndDate: z.date().describe(`
        * * Field Name: EndDate
        * * Display Name: End Date
        * * SQL Data Type: datetimeoffset
        * * Description: End date for revenue recognition.`),
    TotalAmount: z.number().describe(`
        * * Field Name: TotalAmount
        * * Display Name: Total Amount
        * * SQL Data Type: decimal(18, 2)
        * * Description: Total amount to be recognized over the schedule period.`),
    PeriodsTotal: z.number().nullable().describe(`
        * * Field Name: PeriodsTotal
        * * Display Name: Periods Total
        * * SQL Data Type: int
        * * Description: Total number of recognition periods for this schedule.`),
    PeriodsProcessed: z.number().describe(`
        * * Field Name: PeriodsProcessed
        * * Display Name: Periods Processed
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Number of periods already processed (tracks progress).`),
    CurrencyID: z.string().nullable().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)`),
    LastProcessedDate: z.date().nullable().describe(`
        * * Field Name: LastProcessedDate
        * * Display Name: Last Processed Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date when this schedule was last processed.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Status: z.union([z.literal('Cancelled'), z.literal('Complete'), z.literal('In Progress'), z.literal('Pending')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(20)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Cancelled
    *   * Complete
    *   * In Progress
    *   * Pending
        * * Description: Current status of the schedule: Pending, In Progress, Complete, or Cancelled.`),
    RelatedScheduleID: z.string().nullable().describe(`
        * * Field Name: RelatedScheduleID
        * * Display Name: Related Schedule ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Schedules (vwAccountingSchedules.ID)
        * * Description: Links reversal schedules to the original schedule.`),
    IsReversal: z.boolean().describe(`
        * * Field Name: IsReversal
        * * Display Name: Is Reversal
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Indicates if this schedule is a reversal of another schedule.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    ExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System Record ID
        * * SQL Data Type: varchar(100)
        * * Description: System identifier for this record in the external source system.`),
    Currency: z.string().nullable().describe(`
        * * Field Name: Currency
        * * Display Name: Currency
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    RootRelatedScheduleID: z.string().nullable().describe(`
        * * Field Name: RootRelatedScheduleID
        * * Display Name: Root Related Schedule ID
        * * SQL Data Type: uniqueidentifier`),
    RelatedScheduleIDDepth: z.number().nullable().describe(`
        * * Field Name: RelatedScheduleIDDepth
        * * Display Name: Related Schedule ID Depth
        * * SQL Data Type: int`),
    RelatedScheduleIDPath: z.string().nullable().describe(`
        * * Field Name: RelatedScheduleIDPath
        * * Display Name: Related Schedule ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    RelatedScheduleIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: RelatedScheduleIDIsLeaf
        * * Display Name: Related Schedule ID Is Leaf
        * * SQL Data Type: bit`),
    RelatedScheduleIDChildCount: z.number().nullable().describe(`
        * * Field Name: RelatedScheduleIDChildCount
        * * Display Name: Related Schedule ID Child Count
        * * SQL Data Type: int`),
});

export type soundpostAccountingScheduleEntityType = z.infer<typeof soundpostAccountingScheduleSchema>;

/**
 * zod schema definition for the entity Agreement Lines
 */
export const soundpostAgreementLineSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AgreementID: z.string().describe(`
        * * Field Name: AgreementID
        * * Display Name: Agreement ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Agreements (vwAgreements.ID)`),
    LineNumber: z.number().describe(`
        * * Field Name: LineNumber
        * * Display Name: Line Number
        * * SQL Data Type: int
        * * Default Value: 1`),
    ProductID: z.string().describe(`
        * * Field Name: ProductID
        * * Display Name: Product ID
        * * SQL Data Type: uniqueidentifier`),
    AllocatedAmount: z.number().describe(`
        * * Field Name: AllocatedAmount
        * * Display Name: Allocated Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    AdjustmentAmount: z.number().describe(`
        * * Field Name: AdjustmentAmount
        * * Display Name: Adjustment Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    Comments: z.string().nullable().describe(`
        * * Field Name: Comments
        * * Display Name: Comments
        * * SQL Data Type: nvarchar(1000)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostAgreementLineEntityType = z.infer<typeof soundpostAgreementLineSchema>;

/**
 * zod schema definition for the entity Agreement Status
 */
export const soundpostAgreementStatusSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(200)
        * * Description: Display name of the agreement status.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(1000)
        * * Description: Description of the agreement status.`),
    DisplayOrder: z.number().describe(`
        * * Field Name: DisplayOrder
        * * Display Name: Display Order
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Sort/display order for UI lists.`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Indicates if this status is active/available.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostAgreementStatusEntityType = z.infer<typeof soundpostAgreementStatusSchema>;

/**
 * zod schema definition for the entity Agreement Types
 */
export const soundpostAgreementTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(200)
        * * Description: Display name of the agreement type.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(1000)
        * * Description: Description of the agreement type.`),
    DisplayOrder: z.number().describe(`
        * * Field Name: DisplayOrder
        * * Display Name: Display Order
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Sort/display order for UI lists.`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Indicates if this type is active/available.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostAgreementTypeEntityType = z.infer<typeof soundpostAgreementTypeSchema>;

/**
 * zod schema definition for the entity Agreements
 */
export const soundpostAgreementSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AccountName: z.string().describe(`
        * * Field Name: AccountName
        * * Display Name: Account Name
        * * SQL Data Type: nvarchar(255)`),
    ContactName: z.string().nullable().describe(`
        * * Field Name: ContactName
        * * Display Name: Contact Name
        * * SQL Data Type: nvarchar(255)`),
    StartDate: z.date().describe(`
        * * Field Name: StartDate
        * * Display Name: Start Date
        * * SQL Data Type: datetimeoffset`),
    EndDate: z.date().nullable().describe(`
        * * Field Name: EndDate
        * * Display Name: End Date
        * * SQL Data Type: datetimeoffset`),
    AgreementTotal: z.number().describe(`
        * * Field Name: AgreementTotal
        * * Display Name: Agreement Total
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    TermSequence: z.number().describe(`
        * * Field Name: TermSequence
        * * Display Name: Term Sequence
        * * SQL Data Type: int
        * * Default Value: 1`),
    Comments: z.string().nullable().describe(`
        * * Field Name: Comments
        * * Display Name: Comments
        * * SQL Data Type: nvarchar(2000)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    AgreementTypeID: z.string().nullable().describe(`
        * * Field Name: AgreementTypeID
        * * Display Name: Agreement Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Agreement Types (vwAgreementTypes.ID)
        * * Description: Foreign key to AgreementType lookup.`),
    AgreementStatusID: z.string().nullable().describe(`
        * * Field Name: AgreementStatusID
        * * Display Name: Agreement Status ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Agreement Status (vwAgreementStatus.ID)
        * * Description: Foreign key to AgreementStatus lookup.`),
    AgreementCode: z.string().nullable().describe(`
        * * Field Name: AgreementCode
        * * Display Name: Agreement Code
        * * SQL Data Type: varchar(20)`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    AgreementType: z.string().nullable().describe(`
        * * Field Name: AgreementType
        * * Display Name: Agreement Type
        * * SQL Data Type: nvarchar(200)`),
    AgreementStatus: z.string().nullable().describe(`
        * * Field Name: AgreementStatus
        * * Display Name: Agreement Status
        * * SQL Data Type: nvarchar(200)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
});

export type soundpostAgreementEntityType = z.infer<typeof soundpostAgreementSchema>;

/**
 * zod schema definition for the entity Company Sequences
 */
export const soundpostCompanySequenceSchema = z.object({
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier`),
    SequenceName: z.string().describe(`
        * * Field Name: SequenceName
        * * Display Name: Sequence Name
        * * SQL Data Type: varchar(50)`),
    CurrentValue: z.number().describe(`
        * * Field Name: CurrentValue
        * * Display Name: Current Value
        * * SQL Data Type: int`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type soundpostCompanySequenceEntityType = z.infer<typeof soundpostCompanySequenceSchema>;

/**
 * zod schema definition for the entity Config Keys
 */
export const soundpostConfigKeySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ConfigKey: z.string().describe(`
        * * Field Name: ConfigKey
        * * Display Name: Config Key
        * * SQL Data Type: nvarchar(200)
        * * Description: Unique configuration key (e.g., woocommerce.baseUrl).`),
    ConfigValue: z.string().nullable().describe(`
        * * Field Name: ConfigValue
        * * Display Name: Config Value
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Configuration value (stored as string, parsed based on DataType).`),
    DataType: z.string().describe(`
        * * Field Name: DataType
        * * Display Name: Data Type
        * * SQL Data Type: nvarchar(20)
        * * Default Value: string
        * * Description: Data type of the configuration value (string, number, boolean, json).`),
    Category: z.string().describe(`
        * * Field Name: Category
        * * Display Name: Category
        * * SQL Data Type: nvarchar(50)
        * * Description: Configuration category for grouping (core, integration, woocommerce, etc.).`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    IsSecure: z.boolean().describe(`
        * * Field Name: IsSecure
        * * Display Name: Is Secure
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether this setting contains sensitive information.`),
    IsRequired: z.boolean().describe(`
        * * Field Name: IsRequired
        * * Display Name: Is Required
        * * SQL Data Type: bit
        * * Default Value: 0`),
    DefaultValue: z.string().nullable().describe(`
        * * Field Name: DefaultValue
        * * Display Name: Default Value
        * * SQL Data Type: nvarchar(MAX)`),
    ValidationPattern: z.string().nullable().describe(`
        * * Field Name: ValidationPattern
        * * Display Name: Validation Pattern
        * * SQL Data Type: nvarchar(500)`),
    DisplayOrder: z.number().describe(`
        * * Field Name: DisplayOrder
        * * Display Name: Display Order
        * * SQL Data Type: int
        * * Default Value: 100`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Links config to a specific company integration if applicable.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Links config key to a specific company if applicable.`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostConfigKeyEntityType = z.infer<typeof soundpostConfigKeySchema>;

/**
 * zod schema definition for the entity Control Account Types
 */
export const soundpostControlAccountTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    Code: z.string().describe(`
        * * Field Name: Code
        * * Display Name: Code
        * * SQL Data Type: varchar(30)
        * * Description: Unique short code (e.g., AR, DEFREV).`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: varchar(100)
        * * Description: Display name.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)
        * * Description: Optional description.`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Active flag.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC created timestamp.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC updated timestamp.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostControlAccountTypeEntityType = z.infer<typeof soundpostControlAccountTypeSchema>;

/**
 * zod schema definition for the entity Currencies
 */
export const soundpostCurrencySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Unique identifier for the currency.`),
    CurrencyCode: z.string().describe(`
        * * Field Name: CurrencyCode
        * * Display Name: Currency Code
        * * SQL Data Type: varchar(3)
        * * Description: ISO 4217 three-letter currency code (e.g., USD, EUR).`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: varchar(100)
        * * Description: Full name of the currency.`),
    Symbol: z.string().nullable().describe(`
        * * Field Name: Symbol
        * * Display Name: Symbol
        * * SQL Data Type: varchar(10)
        * * Description: Currency symbol (e.g., $, €, £).`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostCurrencyEntityType = z.infer<typeof soundpostCurrencySchema>;

/**
 * zod schema definition for the entity Customers
 */
export const soundpostCustomerSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ExternalSystemRecordID: z.string().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System Record ID
        * * SQL Data Type: varchar(100)
        * * Description: External system customer identifier.`),
    CustomerNumber: z.string().nullable().describe(`
        * * Field Name: CustomerNumber
        * * Display Name: Customer Number
        * * SQL Data Type: varchar(50)`),
    CustomerName: z.string().describe(`
        * * Field Name: CustomerName
        * * Display Name: Customer Name
        * * SQL Data Type: nvarchar(200)
        * * Description: Display name for the customer.`),
    Email: z.string().nullable().describe(`
        * * Field Name: Email
        * * Display Name: Email
        * * SQL Data Type: varchar(200)`),
    Phone: z.string().nullable().describe(`
        * * Field Name: Phone
        * * Display Name: Phone
        * * SQL Data Type: varchar(50)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    CustomerType: z.string().nullable().describe(`
        * * Field Name: CustomerType
        * * Display Name: Customer Type
        * * SQL Data Type: varchar(50)
        * * Description: Type of customer (Individual, Organization, etc.).`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
});

export type soundpostCustomerEntityType = z.infer<typeof soundpostCustomerSchema>;

/**
 * zod schema definition for the entity Dimension Types
 */
export const soundpostDimensionTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: varchar(50)
        * * Description: Name of the dimension type (Department, Project, Location, etc.).`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Code: z.string().nullable().describe(`
        * * Field Name: Code
        * * Display Name: Code
        * * SQL Data Type: varchar(50)
        * * Description: Optional short code to identify the dimension type.`),
    Prefix: z.string().nullable().describe(`
        * * Field Name: Prefix
        * * Display Name: Prefix
        * * SQL Data Type: varchar(5)
        * * Description: Prefix used in generating display numbers or identifiers.`),
    SortOrder: z.number().describe(`
        * * Field Name: SortOrder
        * * Display Name: Sort Order
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Controls the display or processing order of dimension types. Lower values sort first.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
});

export type soundpostDimensionTypeEntityType = z.infer<typeof soundpostDimensionTypeSchema>;

/**
 * zod schema definition for the entity Dimensions
 */
export const soundpostDimensionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    DimensionTypeID: z.string().describe(`
        * * Field Name: DimensionTypeID
        * * Display Name: Dimension Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Dimension Types (vwDimensionTypes.ID)`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: varchar(100)
        * * Description: Dimension name or label.`),
    Code: z.string().nullable().describe(`
        * * Field Name: Code
        * * Display Name: Code
        * * SQL Data Type: varchar(50)
        * * Description: Short code for the dimension.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    DimensionType: z.string().describe(`
        * * Field Name: DimensionType
        * * Display Name: Dimension Type
        * * SQL Data Type: varchar(50)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
});

export type soundpostDimensionEntityType = z.infer<typeof soundpostDimensionSchema>;

/**
 * zod schema definition for the entity Exchange Rates
 */
export const soundpostExchangeRateSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    BaseCurrencyCode: z.string().describe(`
        * * Field Name: BaseCurrencyCode
        * * Display Name: Base Currency Code
        * * SQL Data Type: varchar(3)
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.CurrencyCode)
        * * Description: Base currency in the exchange rate pair (1 unit of this currency).`),
    QuoteCurrencyCode: z.string().describe(`
        * * Field Name: QuoteCurrencyCode
        * * Display Name: Quote Currency Code
        * * SQL Data Type: varchar(3)
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.CurrencyCode)
        * * Description: Quote currency in the exchange rate pair (equals this many units).`),
    Rate: z.number().describe(`
        * * Field Name: Rate
        * * Display Name: Rate
        * * SQL Data Type: decimal(12, 6)
        * * Description: Exchange rate value.`),
    EffectiveDate: z.date().describe(`
        * * Field Name: EffectiveDate
        * * Display Name: Effective Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date when this exchange rate is effective.`),
    RateType: z.string().describe(`
        * * Field Name: RateType
        * * Display Name: Rate Type
        * * SQL Data Type: varchar(20)
        * * Default Value: Spot`),
    RateSource: z.string().nullable().describe(`
        * * Field Name: RateSource
        * * Display Name: Rate Source
        * * SQL Data Type: varchar(50)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    BaseCurrencyCode_Virtual: z.string().describe(`
        * * Field Name: BaseCurrencyCode_Virtual
        * * Display Name: Base Currency Code Virtual
        * * SQL Data Type: varchar(100)`),
    QuoteCurrencyCode_Virtual: z.string().describe(`
        * * Field Name: QuoteCurrencyCode_Virtual
        * * Display Name: Quote Currency Code Virtual
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
});

export type soundpostExchangeRateEntityType = z.infer<typeof soundpostExchangeRateSchema>;

/**
 * zod schema definition for the entity GL Account Mappings
 */
export const soundpostGLAccountMappingSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    MappingType: z.string().describe(`
        * * Field Name: MappingType
        * * Display Name: Mapping Type
        * * SQL Data Type: varchar(50)
        * * Description: Type of mapping (Product, Category, PaymentMethod, etc.).`),
    AccountType: z.string().describe(`
        * * Field Name: AccountType
        * * Display Name: Account Type
        * * SQL Data Type: varchar(100)
        * * Description: Account type (Revenue, DeferredRevenue, Receivable, etc.).`),
    CategoryMatch: z.string().nullable().describe(`
        * * Field Name: CategoryMatch
        * * Display Name: Category Match
        * * SQL Data Type: varchar(200)`),
    ProductTypeMatch: z.string().nullable().describe(`
        * * Field Name: ProductTypeMatch
        * * Display Name: Product Type Match
        * * SQL Data Type: varchar(100)`),
    EventTypeMatch: z.string().nullable().describe(`
        * * Field Name: EventTypeMatch
        * * Display Name: Event Type Match
        * * SQL Data Type: varchar(100)`),
    PaymentMethodMatch: z.string().nullable().describe(`
        * * Field Name: PaymentMethodMatch
        * * Display Name: Payment Method Match
        * * SQL Data Type: varchar(100)`),
    GLAccountID: z.string().nullable().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)`),
    DimensionMappings: z.string().nullable().describe(`
        * * Field Name: DimensionMappings
        * * Display Name: Dimension Mappings
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON structure for dimension mappings.`),
    Priority: z.number().describe(`
        * * Field Name: Priority
        * * Display Name: Priority
        * * SQL Data Type: int
        * * Default Value: 100`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    SourceValue: z.string().nullable().describe(`
        * * Field Name: SourceValue
        * * Display Name: Source Value
        * * SQL Data Type: varchar(200)
        * * Description: The specific value to match for this mapping type (e.g., "CreditCard", "Member", "Webinar").`),
    SourceFieldName: z.string().nullable().describe(`
        * * Field Name: SourceFieldName
        * * Display Name: Source Field Name
        * * SQL Data Type: varchar(100)
        * * Description: The metadata field name to read from the source entity (e.g., "_sage_gl", "_deferred_revenue_required").`),
    EntityType: z.string().nullable().describe(`
        * * Field Name: EntityType
        * * Display Name: Entity Type
        * * SQL Data Type: varchar(50)
        * * Description: The entity type this mapping applies to (Product, Order, Customer, Payment).`),
    ConditionExpression: z.string().nullable().describe(`
        * * Field Name: ConditionExpression
        * * Display Name: Condition Expression
        * * SQL Data Type: varchar(500)
        * * Description: Optional conditional expression to evaluate (e.g., "value='yes'", "state='VA'").`),
    SegmentDelimiter: z.string().nullable().describe(`
        * * Field Name: SegmentDelimiter
        * * Display Name: Segment Delimiter
        * * SQL Data Type: varchar(10)
        * * Description: Delimiter used to parse GL account strings (e.g., "-" for "1234-N456-P789").`),
    AutoCreateGLAccount: z.boolean().describe(`
        * * Field Name: AutoCreateGLAccount
        * * Display Name: Auto Create GL Account
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether to automatically create GL accounts if they don't exist.`),
    AutoCreateDimensions: z.boolean().describe(`
        * * Field Name: AutoCreateDimensions
        * * Display Name: Auto Create Dimensions
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether to automatically create dimension types if they don't exist.`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Reference to the company integration this mapping belongs to (optional, null = global).`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(255)
        * * Description: Optional short description of this GL account mapping rule.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    GLAccount: z.string().nullable().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostGLAccountMappingEntityType = z.infer<typeof soundpostGLAccountMappingSchema>;

/**
 * zod schema definition for the entity GL Account Types
 */
export const soundpostGLAccountTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ParentID: z.string().nullable().describe(`
        * * Field Name: ParentID
        * * Display Name: Parent ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Account Types (vwGLAccountTypes.ID)
        * * Description: Parent GL Account Type for hierarchical classification.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: varchar(100)
        * * Description: Name of the GL account type (Asset, Liability, Revenue, etc.).`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    SortOrder: z.number().describe(`
        * * Field Name: SortOrder
        * * Display Name: Sort Order
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Controls the display or processing order of GL account types. Lower values sort first.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    Parent: z.string().nullable().describe(`
        * * Field Name: Parent
        * * Display Name: Parent
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    RootParentID: z.string().nullable().describe(`
        * * Field Name: RootParentID
        * * Display Name: Root Parent ID
        * * SQL Data Type: uniqueidentifier`),
    ParentIDDepth: z.number().nullable().describe(`
        * * Field Name: ParentIDDepth
        * * Display Name: Parent ID Depth
        * * SQL Data Type: int`),
    ParentIDPath: z.string().nullable().describe(`
        * * Field Name: ParentIDPath
        * * Display Name: Parent ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    ParentIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: ParentIDIsLeaf
        * * Display Name: Parent ID Is Leaf
        * * SQL Data Type: bit`),
    ParentIDChildCount: z.number().nullable().describe(`
        * * Field Name: ParentIDChildCount
        * * Display Name: Parent ID Child Count
        * * SQL Data Type: int`),
});

export type soundpostGLAccountTypeEntityType = z.infer<typeof soundpostGLAccountTypeSchema>;

/**
 * zod schema definition for the entity GL Accounts
 */
export const soundpostGLAccountSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AccountNumber: z.string().describe(`
        * * Field Name: AccountNumber
        * * Display Name: Account Number
        * * SQL Data Type: varchar(50)
        * * Description: Chart of account number used in accounting entries.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: varchar(100)
        * * Description: Name or label of the GL account.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    GLAccountTypeID: z.string().describe(`
        * * Field Name: GLAccountTypeID
        * * Display Name: GL Account Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Account Types (vwGLAccountTypes.ID)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    ControlAccountTypeID: z.string().nullable().describe(`
        * * Field Name: ControlAccountTypeID
        * * Display Name: Control Account Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Control Account Types (vwControlAccountTypes.ID)
        * * Description: Control account classification for reconciliation (e.g., AR, Deferred Revenue).`),
    ReconFlowCategoryID: z.string().nullable().describe(`
        * * Field Name: ReconFlowCategoryID
        * * Display Name: Recon Flow Category ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Recon Flow Categories (vwReconFlowCategories.ID)
        * * Description: Reconciliation flow category for GL rollups.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    ReconClassification: z.union([z.literal('Owned'), z.literal('Reclassified'), z.literal('Shared')]).nullable().describe(`
        * * Field Name: ReconClassification
        * * Display Name: Recon Classification
        * * SQL Data Type: varchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Owned
    *   * Reclassified
    *   * Shared
        * * Description: Reconciliation policy for this account: Owned (SoundPost is the only feed; difference is scored against ReconTolerance), Shared (other feeds post here; difference reported, never failed), Reclassified (accounting sweeps SoundPost postings elsewhere; informational). NULL = not reconciled.`),
    ReconTolerance: z.number().nullable().describe(`
        * * Field Name: ReconTolerance
        * * Display Name: Recon Tolerance
        * * SQL Data Type: decimal(18, 2)
        * * Description: Absolute tolerance (base currency) within which an Owned account is considered tied out. NULL = company default (1.00).`),
    GLAccountType: z.string().describe(`
        * * Field Name: GLAccountType
        * * Display Name: GL Account Type
        * * SQL Data Type: varchar(100)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    ControlAccountType: z.string().nullable().describe(`
        * * Field Name: ControlAccountType
        * * Display Name: Control Account Type
        * * SQL Data Type: varchar(100)`),
    ReconFlowCategory: z.string().nullable().describe(`
        * * Field Name: ReconFlowCategory
        * * Display Name: Recon Flow Category
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostGLAccountEntityType = z.infer<typeof soundpostGLAccountSchema>;

/**
 * zod schema definition for the entity Orchestration Run Steps
 */
export const soundpostOrchestrationRunStepSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrchestrationRunID: z.string().describe(`
        * * Field Name: OrchestrationRunID
        * * Display Name: Orchestration Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Orchestration Runs (vwOrchestrationRuns.ID)
        * * Description: Parent orchestration run.`),
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Company this step belongs to. Denormalized from OrchestrationRun for multi-tenancy filtering.`),
    StepNumber: z.number().describe(`
        * * Field Name: StepNumber
        * * Display Name: Step Number
        * * SQL Data Type: int
        * * Description: Execution order within the pipeline (1-based). Assigned dynamically based on active integrations and capabilities.`),
    StepName: z.string().describe(`
        * * Field Name: StepName
        * * Display Name: Step Name
        * * SQL Data Type: nvarchar(200)
        * * Description: Human-readable step name including integration context (e.g. Sync WooCommerce Orders).`),
    ActionName: z.string().nullable().describe(`
        * * Field Name: ActionName
        * * Display Name: Action Name
        * * SQL Data Type: nvarchar(425)
        * * Description: MJ Action name executed for this step (e.g. SoundPost: Sync Orders).`),
    ActionExecutionLogID: z.string().nullable().describe(`
        * * Field Name: ActionExecutionLogID
        * * Display Name: Action Execution Log ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Action Execution Logs (vwActionExecutionLogs.ID)
        * * Description: Links to the MJ ActionExecutionLog record for this step invocation.`),
    Status: z.union([z.literal('Completed'), z.literal('Failed'), z.literal('Pending'), z.literal('Running'), z.literal('Skipped')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(20)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Completed
    *   * Failed
    *   * Pending
    *   * Running
    *   * Skipped
        * * Description: Step execution status: Pending, Running, Completed, Failed, or Skipped.`),
    StartedAt: z.date().nullable().describe(`
        * * Field Name: StartedAt
        * * Display Name: Started At
        * * SQL Data Type: datetimeoffset`),
    CompletedAt: z.date().nullable().describe(`
        * * Field Name: CompletedAt
        * * Display Name: Completed At
        * * SQL Data Type: datetimeoffset`),
    DurationMs: z.number().nullable().describe(`
        * * Field Name: DurationMs
        * * Display Name: Duration Ms
        * * SQL Data Type: int`),
    RecordsProcessed: z.number().describe(`
        * * Field Name: RecordsProcessed
        * * Display Name: Records Processed
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsSucceeded: z.number().describe(`
        * * Field Name: RecordsSucceeded
        * * Display Name: Records Succeeded
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsFailed: z.number().describe(`
        * * Field Name: RecordsFailed
        * * Display Name: Records Failed
        * * SQL Data Type: int
        * * Default Value: 0`),
    ErrorMessage: z.string().nullable().describe(`
        * * Field Name: ErrorMessage
        * * Display Name: Error Message
        * * SQL Data Type: nvarchar(MAX)`),
    ResultDetailJson: z.string().nullable().describe(`
        * * Field Name: ResultDetailJson
        * * Display Name: Result Detail Json
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Step-specific output data serialized as JSON.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Company: z.string().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    ActionExecutionLog: z.string().nullable().describe(`
        * * Field Name: ActionExecutionLog
        * * Display Name: Action Execution Log
        * * SQL Data Type: nvarchar(425)`),
});

export type soundpostOrchestrationRunStepEntityType = z.infer<typeof soundpostOrchestrationRunStepSchema>;

/**
 * zod schema definition for the entity Orchestration Runs
 */
export const soundpostOrchestrationRunSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ScheduledJobRunID: z.string().nullable().describe(`
        * * Field Name: ScheduledJobRunID
        * * Display Name: Scheduled Job Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Scheduled Job Runs (vwScheduledJobRuns.ID)
        * * Description: Links to MJ ScheduledJobRun for scheduled executions. NULL for ad-hoc runs triggered via REST endpoint.`),
    PipelineName: z.string().describe(`
        * * Field Name: PipelineName
        * * Display Name: Pipeline Name
        * * SQL Data Type: varchar(50)
        * * Description: Pipeline identifier: NightlySync, ManualPosting, ResyncOrder, MonthlyClose, RefreshReports, GenerateSnapshot.`),
    Status: z.union([z.literal('Cancelled'), z.literal('Completed'), z.literal('Failed'), z.literal('Queued'), z.literal('Running'), z.literal('TimedOut')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(20)
        * * Default Value: Running
    * * Value List Type: List
    * * Possible Values 
    *   * Cancelled
    *   * Completed
    *   * Failed
    *   * Queued
    *   * Running
    *   * TimedOut
        * * Description: Current execution status: Queued, Running, Completed, Failed, TimedOut, or Cancelled.`),
    ParametersJson: z.string().nullable().describe(`
        * * Field Name: ParametersJson
        * * Display Name: Parameters Json
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Run-specific input parameters serialized as JSON.`),
    RequestedByID: z.string().describe(`
        * * Field Name: RequestedByID
        * * Display Name: Requested By ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Employees (vwEmployees.ID)
        * * Description: Employee who requested the run. For scheduled executions, this is the service account employee. Set server-side, not from client input.`),
    StartedAt: z.date().nullable().describe(`
        * * Field Name: StartedAt
        * * Display Name: Started At
        * * SQL Data Type: datetimeoffset`),
    CompletedAt: z.date().nullable().describe(`
        * * Field Name: CompletedAt
        * * Display Name: Completed At
        * * SQL Data Type: datetimeoffset`),
    DurationMs: z.number().nullable().describe(`
        * * Field Name: DurationMs
        * * Display Name: Duration Ms
        * * SQL Data Type: int
        * * Description: Wall-clock duration in milliseconds, computed on completion.`),
    TotalSteps: z.number().describe(`
        * * Field Name: TotalSteps
        * * Display Name: Total Steps
        * * SQL Data Type: int
        * * Default Value: 0`),
    CompletedSteps: z.number().describe(`
        * * Field Name: CompletedSteps
        * * Display Name: Completed Steps
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsProcessed: z.number().describe(`
        * * Field Name: RecordsProcessed
        * * Display Name: Records Processed
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Aggregate records processed across all steps.`),
    RecordsSucceeded: z.number().describe(`
        * * Field Name: RecordsSucceeded
        * * Display Name: Records Succeeded
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsFailed: z.number().describe(`
        * * Field Name: RecordsFailed
        * * Display Name: Records Failed
        * * SQL Data Type: int
        * * Default Value: 0`),
    ErrorMessage: z.string().nullable().describe(`
        * * Field Name: ErrorMessage
        * * Display Name: Error Message
        * * SQL Data Type: nvarchar(MAX)`),
    ResultSummaryJson: z.string().nullable().describe(`
        * * Field Name: ResultSummaryJson
        * * Display Name: Result Summary Json
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Pipeline-specific result data serialized as JSON.`),
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Company this orchestration run belongs to. Required for multi-tenancy.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    ScheduledJobRun: z.string().nullable().describe(`
        * * Field Name: ScheduledJobRun
        * * Display Name: Scheduled Job Run
        * * SQL Data Type: nvarchar(200)`),
    RequestedBy: z.string().nullable().describe(`
        * * Field Name: RequestedBy
        * * Display Name: Requested By
        * * SQL Data Type: nvarchar(81)`),
    Company: z.string().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostOrchestrationRunEntityType = z.infer<typeof soundpostOrchestrationRunSchema>;

/**
 * zod schema definition for the entity Order Lines
 */
export const soundpostOrderLineSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrderID: z.string().describe(`
        * * Field Name: OrderID
        * * Display Name: Order ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Orders (vwOrders.ID)`),
    LineNumber: z.number().describe(`
        * * Field Name: LineNumber
        * * Display Name: Line Number
        * * SQL Data Type: int
        * * Description: Sequential line number within the order.`),
    ProductID: z.string().describe(`
        * * Field Name: ProductID
        * * Display Name: Product ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Products (vwProducts.ID)`),
    ProductName: z.string().nullable().describe(`
        * * Field Name: ProductName
        * * Display Name: Product Name
        * * SQL Data Type: nvarchar(500)
        * * Description: Product name at time of order (for historical reference).`),
    Quantity: z.number().describe(`
        * * Field Name: Quantity
        * * Display Name: Quantity
        * * SQL Data Type: decimal(18, 4)
        * * Default Value: 1
        * * Description: Quantity ordered.`),
    UnitPrice: z.number().describe(`
        * * Field Name: UnitPrice
        * * Display Name: Unit Price
        * * SQL Data Type: decimal(18, 4)
        * * Description: Unit price at time of order.`),
    LineAmount: z.number().describe(`
        * * Field Name: LineAmount
        * * Display Name: Line Amount
        * * SQL Data Type: decimal(18, 2)
        * * Description: Total line amount (quantity × unit price - discounts + tax).`),
    TaxAmount: z.number().nullable().describe(`
        * * Field Name: TaxAmount
        * * Display Name: Tax Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    DiscountAmount: z.number().nullable().describe(`
        * * Field Name: DiscountAmount
        * * Display Name: Discount Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    ExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System Record ID
        * * SQL Data Type: nvarchar(100)
        * * Description: External system identifier for the order line item (e.g., WooCommerce line item ID)`),
    RecognitionStartDate: z.date().nullable().describe(`
        * * Field Name: RecognitionStartDate
        * * Display Name: Recognition Start Date
        * * SQL Data Type: datetimeoffset
        * * Description: Start date for revenue recognition (NULL for immediate recognition).`),
    RecognitionEndDate: z.date().nullable().describe(`
        * * Field Name: RecognitionEndDate
        * * Display Name: Recognition End Date
        * * SQL Data Type: datetimeoffset
        * * Description: End date for revenue recognition period (NULL for immediate recognition).`),
    RecipientName: z.string().nullable().describe(`
        * * Field Name: RecipientName
        * * Display Name: Recipient Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Full name of the recipient for this order line item.`),
    RecipientCompany: z.string().nullable().describe(`
        * * Field Name: RecipientCompany
        * * Display Name: Recipient Company
        * * SQL Data Type: nvarchar(255)
        * * Description: Name of the company associated with the recipient for this order line item.`),
    ReconFlowCategoryID: z.string().nullable().describe(`
        * * Field Name: ReconFlowCategoryID
        * * Display Name: Recon Flow Category ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Recon Flow Categories (vwReconFlowCategories.ID)
        * * Description: Reconciliation flow category snapshot frozen at time of order line creation.`),
    ReversalOfLineID: z.string().nullable().describe(`
        * * Field Name: ReversalOfLineID
        * * Display Name: Reversal Of Line ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Order Lines (vwOrderLines.ID)
        * * Description: Links to the original order line being reversed.`),
    ReversalReason: z.string().nullable().describe(`
        * * Field Name: ReversalReason
        * * Display Name: Reversal Reason
        * * SQL Data Type: nvarchar(500)
        * * Description: Reason for the reversal (e.g., Product cancelled, Quantity adjusted).`),
    IsReversal: z.boolean().describe(`
        * * Field Name: IsReversal
        * * Display Name: Is Reversal
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Indicates if this line is a reversal of another line.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    ReconFlowCategory: z.string().nullable().describe(`
        * * Field Name: ReconFlowCategory
        * * Display Name: Recon Flow Category
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    RootReversalOfLineID: z.string().nullable().describe(`
        * * Field Name: RootReversalOfLineID
        * * Display Name: Root Reversal Of Line ID
        * * SQL Data Type: uniqueidentifier`),
    ReversalOfLineIDDepth: z.number().nullable().describe(`
        * * Field Name: ReversalOfLineIDDepth
        * * Display Name: Reversal Of Line ID Depth
        * * SQL Data Type: int`),
    ReversalOfLineIDPath: z.string().nullable().describe(`
        * * Field Name: ReversalOfLineIDPath
        * * Display Name: Reversal Of Line ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    ReversalOfLineIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: ReversalOfLineIDIsLeaf
        * * Display Name: Reversal Of Line ID Is Leaf
        * * SQL Data Type: bit`),
    ReversalOfLineIDChildCount: z.number().nullable().describe(`
        * * Field Name: ReversalOfLineIDChildCount
        * * Display Name: Reversal Of Line ID Child Count
        * * SQL Data Type: int`),
});

export type soundpostOrderLineEntityType = z.infer<typeof soundpostOrderLineSchema>;

/**
 * zod schema definition for the entity Orders
 */
export const soundpostOrderSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System Record ID
        * * SQL Data Type: varchar(100)
        * * Description: External system order identifier.`),
    OrderNumber: z.string().describe(`
        * * Field Name: OrderNumber
        * * Display Name: Order Number
        * * SQL Data Type: varchar(50)`),
    OrderDate: z.date().describe(`
        * * Field Name: OrderDate
        * * Display Name: Order Date
        * * SQL Data Type: datetimeoffset
        * * Description: Order date, typically the transaction or purchase date.`),
    CustomerID: z.string().nullable().describe(`
        * * Field Name: CustomerID
        * * Display Name: Customer ID
        * * SQL Data Type: varchar(100)
        * * Description: Customer identifier from source system.`),
    CustomerType: z.string().nullable().describe(`
        * * Field Name: CustomerType
        * * Display Name: Customer Type
        * * SQL Data Type: varchar(50)
        * * Description: Type of customer (Individual, Organization, etc.).`),
    TotalAmount: z.number().describe(`
        * * Field Name: TotalAmount
        * * Display Name: Total Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Total order amount including tax and shipping.`),
    TaxAmount: z.number().nullable().describe(`
        * * Field Name: TaxAmount
        * * Display Name: Tax Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    ShippingAmount: z.number().nullable().describe(`
        * * Field Name: ShippingAmount
        * * Display Name: Shipping Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    DiscountAmount: z.number().nullable().describe(`
        * * Field Name: DiscountAmount
        * * Display Name: Discount Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    CurrencyID: z.string().nullable().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)`),
    Status: z.string().nullable().describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(50)`),
    PaymentMethod: z.string().nullable().describe(`
        * * Field Name: PaymentMethod
        * * Display Name: Payment Method
        * * SQL Data Type: varchar(50)`),
    OrderChannel: z.string().nullable().describe(`
        * * Field Name: OrderChannel
        * * Display Name: Order Channel
        * * SQL Data Type: varchar(50)`),
    State: z.string().nullable().describe(`
        * * Field Name: State
        * * Display Name: State
        * * SQL Data Type: varchar(50)`),
    ShippingState: z.string().nullable().describe(`
        * * Field Name: ShippingState
        * * Display Name: Shipping State
        * * SQL Data Type: varchar(50)`),
    BillingState: z.string().nullable().describe(`
        * * Field Name: BillingState
        * * Display Name: Billing State
        * * SQL Data Type: varchar(50)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Reference to the CompanyIntegration that created/updated this record`),
    CustomerName: z.string().nullable().describe(`
        * * Field Name: CustomerName
        * * Display Name: Customer Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Full name of the customer placing the order.`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)
        * * Description: Name of the company associated with the order.`),
    DueDate: z.date().nullable().describe(`
        * * Field Name: DueDate
        * * Display Name: Due Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date when payment for the order is due.`),
    PreviousVersionID: z.string().nullable().describe(`
        * * Field Name: PreviousVersionID
        * * Display Name: Previous Version ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Orders (vwOrders.ID)
        * * Description: Links to the previous version of this order.`),
    OriginalOrderID: z.string().nullable().describe(`
        * * Field Name: OriginalOrderID
        * * Display Name: Original Order ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Orders (vwOrders.ID)
        * * Description: ID of the original order (version 1). Set to own ID after initial save.`),
    VersionReason: z.string().nullable().describe(`
        * * Field Name: VersionReason
        * * Display Name: Version Reason
        * * SQL Data Type: nvarchar(500)
        * * Description: Reason for creating this version (e.g., Order refunded, Status changed).`),
    CanonicalHash: z.string().nullable().describe(`
        * * Field Name: CanonicalHash
        * * Display Name: Canonical Hash
        * * SQL Data Type: varchar(64)
        * * Description: SHA-256 hash of canonical order data for change detection.`),
    Version: z.number().describe(`
        * * Field Name: Version
        * * Display Name: Version
        * * SQL Data Type: int
        * * Default Value: 1
        * * Description: Version number of this order record, starting at 1.`),
    IsActiveVersion: z.boolean().describe(`
        * * Field Name: IsActiveVersion
        * * Display Name: Is Active Version
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Indicates if this is the current active version of the order.`),
    RefundState: z.string().describe(`
        * * Field Name: RefundState
        * * Display Name: Refund State
        * * SQL Data Type: varchar(50)
        * * Default Value: None
        * * Description: Refund state: None, Partial, or Full.`),
    RefundTotal: z.number().describe(`
        * * Field Name: RefundTotal
        * * Display Name: Refund Total
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Total amount refunded on this order.`),
    TaxRate: z.string().nullable().describe(`
        * * Field Name: TaxRate
        * * Display Name: Tax Rate
        * * SQL Data Type: nvarchar(50)
        * * Description: Tax rate description or code applicable to this order (e.g., 7.5%, GST, VAT).`),
    AgreementCode: z.string().nullable().describe(`
        * * Field Name: AgreementCode
        * * Display Name: Agreement Code
        * * SQL Data Type: nvarchar(20)
        * * Description: Agreement code used to associate orders with an Agreement (text).`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    AccountingTags: z.string().nullable().describe(`
        * * Field Name: AccountingTags
        * * Display Name: Accounting Tags
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON object containing accounting-relevant tags for this order, e.g. {"International": "true", "GrantNumber": "Grant-2025-001"}. Used by the Accounting Allocation Rules Engine to evaluate order-level conditions.`),
    Currency: z.string().nullable().describe(`
        * * Field Name: Currency
        * * Display Name: Currency
        * * SQL Data Type: varchar(100)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    Company_Virtual: z.string().nullable().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
    RootPreviousVersionID: z.string().nullable().describe(`
        * * Field Name: RootPreviousVersionID
        * * Display Name: Root Previous Version ID
        * * SQL Data Type: uniqueidentifier`),
    PreviousVersionIDDepth: z.number().nullable().describe(`
        * * Field Name: PreviousVersionIDDepth
        * * Display Name: Previous Version ID Depth
        * * SQL Data Type: int`),
    PreviousVersionIDPath: z.string().nullable().describe(`
        * * Field Name: PreviousVersionIDPath
        * * Display Name: Previous Version ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    PreviousVersionIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: PreviousVersionIDIsLeaf
        * * Display Name: Previous Version ID Is Leaf
        * * SQL Data Type: bit`),
    PreviousVersionIDChildCount: z.number().nullable().describe(`
        * * Field Name: PreviousVersionIDChildCount
        * * Display Name: Previous Version ID Child Count
        * * SQL Data Type: int`),
    RootOriginalOrderID: z.string().nullable().describe(`
        * * Field Name: RootOriginalOrderID
        * * Display Name: Root Original Order ID
        * * SQL Data Type: uniqueidentifier`),
    OriginalOrderIDDepth: z.number().nullable().describe(`
        * * Field Name: OriginalOrderIDDepth
        * * Display Name: Original Order ID Depth
        * * SQL Data Type: int`),
    OriginalOrderIDPath: z.string().nullable().describe(`
        * * Field Name: OriginalOrderIDPath
        * * Display Name: Original Order ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    OriginalOrderIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: OriginalOrderIDIsLeaf
        * * Display Name: Original Order ID Is Leaf
        * * SQL Data Type: bit`),
    OriginalOrderIDChildCount: z.number().nullable().describe(`
        * * Field Name: OriginalOrderIDChildCount
        * * Display Name: Original Order ID Child Count
        * * SQL Data Type: int`),
});

export type soundpostOrderEntityType = z.infer<typeof soundpostOrderSchema>;

/**
 * zod schema definition for the entity Payment Lines
 */
export const soundpostPaymentLineSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PaymentID: z.string().describe(`
        * * Field Name: PaymentID
        * * Display Name: Payment ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Payments (vwPayments.ID)`),
    OrderLineID: z.string().nullable().describe(`
        * * Field Name: OrderLineID
        * * Display Name: Order Line ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Order Lines (vwOrderLines.ID)`),
    AmountApplied: z.number().describe(`
        * * Field Name: AmountApplied
        * * Display Name: Amount Applied
        * * SQL Data Type: decimal(18, 2)
        * * Description: Amount of payment applied to this line.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostPaymentLineEntityType = z.infer<typeof soundpostPaymentLineSchema>;

/**
 * zod schema definition for the entity Payments
 */
export const soundpostPaymentSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PaymentNumber: z.string().nullable().describe(`
        * * Field Name: PaymentNumber
        * * Display Name: Payment Number
        * * SQL Data Type: varchar(50)
        * * Description: System-generated payment number.`),
    PaymentDate: z.date().describe(`
        * * Field Name: PaymentDate
        * * Display Name: Payment Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date when payment was received.`),
    CustomerID: z.string().nullable().describe(`
        * * Field Name: CustomerID
        * * Display Name: Customer ID
        * * SQL Data Type: varchar(100)
        * * Description: Customer identifier from source system.`),
    CustomerSource: z.string().nullable().describe(`
        * * Field Name: CustomerSource
        * * Display Name: Customer Source
        * * SQL Data Type: varchar(50)`),
    Amount: z.number().describe(`
        * * Field Name: Amount
        * * Display Name: Amount
        * * SQL Data Type: decimal(18, 2)`),
    PaymentMethod: z.string().nullable().describe(`
        * * Field Name: PaymentMethod
        * * Display Name: Payment Method
        * * SQL Data Type: varchar(50)
        * * Description: Payment method (Credit Card, ACH, Check, etc.).`),
    ReferenceNumber: z.string().nullable().describe(`
        * * Field Name: ReferenceNumber
        * * Display Name: Reference Number
        * * SQL Data Type: varchar(100)
        * * Description: External reference number (transaction ID, check number, etc.).`),
    Status: z.string().describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(50)
        * * Default Value: Pending
        * * Description: Payment status (Pending, Completed, Failed, Refunded).`),
    CurrencyID: z.string().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)`),
    ExchangeRateID: z.string().nullable().describe(`
        * * Field Name: ExchangeRateID
        * * Display Name: Exchange Rate ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Exchange Rates (vwExchangeRates.ID)`),
    Notes: z.string().nullable().describe(`
        * * Field Name: Notes
        * * Display Name: Notes
        * * SQL Data Type: nvarchar(MAX)`),
    IsPosted: z.boolean().describe(`
        * * Field Name: IsPosted
        * * Display Name: Is Posted
        * * SQL Data Type: bit
        * * Default Value: 0`),
    PostedDate: z.date().nullable().describe(`
        * * Field Name: PostedDate
        * * Display Name: Posted Date
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    ExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System Record ID
        * * SQL Data Type: varchar(100)`),
    ProcessingFee: z.number().describe(`
        * * Field Name: ProcessingFee
        * * Display Name: Processing Fee
        * * SQL Data Type: decimal(12, 2)
        * * Default Value: 0
        * * Description: Payment processor fee amount (e.g., credit card processing fees).`),
    CustomerName: z.string().nullable().describe(`
        * * Field Name: CustomerName
        * * Display Name: Customer Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Full name of the customer making the payment.`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)
        * * Description: Name of the company associated with the payment.`),
    RelatedPaymentID: z.string().nullable().describe(`
        * * Field Name: RelatedPaymentID
        * * Display Name: Related Payment ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Payments (vwPayments.ID)
        * * Description: Links refunds to the original payment.`),
    PaymentType: z.string().describe(`
        * * Field Name: PaymentType
        * * Display Name: Payment Type
        * * SQL Data Type: varchar(50)
        * * Default Value: Payment
        * * Description: Type of payment: Payment, Refund, or PartialRefund.`),
    IsReversal: z.boolean().describe(`
        * * Field Name: IsReversal
        * * Display Name: Is Reversal
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Indicates if this payment is a reversal/refund.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Currency: z.string().describe(`
        * * Field Name: Currency
        * * Display Name: Currency
        * * SQL Data Type: varchar(100)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    Company_Virtual: z.string().nullable().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
    RootRelatedPaymentID: z.string().nullable().describe(`
        * * Field Name: RootRelatedPaymentID
        * * Display Name: Root Related Payment ID
        * * SQL Data Type: uniqueidentifier`),
    RelatedPaymentIDDepth: z.number().nullable().describe(`
        * * Field Name: RelatedPaymentIDDepth
        * * Display Name: Related Payment ID Depth
        * * SQL Data Type: int`),
    RelatedPaymentIDPath: z.string().nullable().describe(`
        * * Field Name: RelatedPaymentIDPath
        * * Display Name: Related Payment ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    RelatedPaymentIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: RelatedPaymentIDIsLeaf
        * * Display Name: Related Payment ID Is Leaf
        * * SQL Data Type: bit`),
    RelatedPaymentIDChildCount: z.number().nullable().describe(`
        * * Field Name: RelatedPaymentIDChildCount
        * * Display Name: Related Payment ID Child Count
        * * SQL Data Type: int`),
});

export type soundpostPaymentEntityType = z.infer<typeof soundpostPaymentSchema>;

/**
 * zod schema definition for the entity Posting Group Line Dimensions
 */
export const soundpostPostingGroupLineDimensionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PostingGroupLineID: z.string().describe(`
        * * Field Name: PostingGroupLineID
        * * Display Name: Posting Group Line ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Posting Group Lines (vwPostingGroupLines.ID)`),
    DimensionTypeID: z.string().describe(`
        * * Field Name: DimensionTypeID
        * * Display Name: Dimension Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Dimension Types (vwDimensionTypes.ID)`),
    DimensionID: z.string().describe(`
        * * Field Name: DimensionID
        * * Display Name: Dimension ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Dimensions (vwDimensions.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    DimensionType: z.string().describe(`
        * * Field Name: DimensionType
        * * Display Name: Dimension Type
        * * SQL Data Type: varchar(50)`),
    Dimension: z.string().describe(`
        * * Field Name: Dimension
        * * Display Name: Dimension
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostPostingGroupLineDimensionEntityType = z.infer<typeof soundpostPostingGroupLineDimensionSchema>;

/**
 * zod schema definition for the entity Posting Group Lines
 */
export const soundpostPostingGroupLineSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PostingGroupID: z.string().describe(`
        * * Field Name: PostingGroupID
        * * Display Name: Posting Group ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Posting Groups (vwPostingGroups.ID)`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)`),
    DebitAmount: z.number().describe(`
        * * Field Name: DebitAmount
        * * Display Name: Debit Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    CreditAmount: z.number().describe(`
        * * Field Name: CreditAmount
        * * Display Name: Credit Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    GLAccount: z.string().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostPostingGroupLineEntityType = z.infer<typeof soundpostPostingGroupLineSchema>;

/**
 * zod schema definition for the entity Posting Groups
 */
export const soundpostPostingGroupSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key for the posting group.`),
    PostingDate: z.date().describe(`
        * * Field Name: PostingDate
        * * Display Name: Posting Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date the transactions in this group are considered posted.`),
    AccountingPeriodID: z.string().nullable().describe(`
        * * Field Name: AccountingPeriodID
        * * Display Name: Accounting Period ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)
        * * Description: Optional accounting period the posting group is associated with.`),
    CurrencyID: z.string().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
        * * Description: Currency in which this posting group is denominated.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: Date/time the posting group was created in the system.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: Date/time the posting group was last updated.`),
    PostedDate: z.date().nullable().describe(`
        * * Field Name: PostedDate
        * * Display Name: Posted Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date/time the posting group was marked as posted.`),
    PostingStatus: z.union([z.literal('Cutover'), z.literal('Error'), z.literal('Excluded'), z.literal('Pending'), z.literal('Posted')]).describe(`
        * * Field Name: PostingStatus
        * * Display Name: Posting Status
        * * SQL Data Type: varchar(20)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Cutover
    *   * Error
    *   * Excluded
    *   * Pending
    *   * Posted
        * * Description: Status of the posting group: Pending, Posted, Error, Cutover (included in reporting), or Excluded (hidden and excluded from reporting).`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Company integration context, if applicable (e.g., Intacct, BC).`),
    ExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System Record ID
        * * SQL Data Type: varchar(100)
        * * Description: Identifier of the record in the external accounting system.`),
    StartDate: z.date().describe(`
        * * Field Name: StartDate
        * * Display Name: Start Date
        * * SQL Data Type: datetimeoffset
        * * Description: Start of the posting group's effective date range.`),
    EndDate: z.date().describe(`
        * * Field Name: EndDate
        * * Display Name: End Date
        * * SQL Data Type: datetimeoffset
        * * Description: End of the posting group's effective date range.`),
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Owning company; references __mj.Company(ID).`),
    PostingNumber: z.number().describe(`
        * * Field Name: PostingNumber
        * * Display Name: Posting Number
        * * SQL Data Type: int`),
    ExternalSystemDisplayID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemDisplayID
        * * Display Name: External System Display ID
        * * SQL Data Type: varchar(100)
        * * Description: User-facing identifier for this record as displayed in the external accounting system UI (e.g., Intacct batch number, QuickBooks document number). Distinct from ExternalSystemRecordID, which holds the internal API/record key used for system-to-system operations.`),
    ExternalSystemURL: z.string().nullable().describe(`
        * * Field Name: ExternalSystemURL
        * * Display Name: External System URL
        * * SQL Data Type: nvarchar(500)
        * * Description: Full URL to this record in the external accounting system web UI. Stored as a complete URL because external systems (e.g., Intacct) use hashed or session-encoded paths that cannot be reconstructed from the record ID alone.`),
    Currency: z.string().describe(`
        * * Field Name: Currency
        * * Display Name: Currency
        * * SQL Data Type: varchar(100)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    Company: z.string().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostPostingGroupEntityType = z.infer<typeof soundpostPostingGroupSchema>;

/**
 * zod schema definition for the entity Processing Batches
 */
export const soundpostProcessingBatchSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    BatchNumber: z.string().nullable().describe(`
        * * Field Name: BatchNumber
        * * Display Name: Batch Number
        * * SQL Data Type: varchar(50)
        * * Description: System-generated batch number for tracking.`),
    SourceSystem: z.string().describe(`
        * * Field Name: SourceSystem
        * * Display Name: Source System
        * * SQL Data Type: varchar(50)`),
    EntityType: z.string().describe(`
        * * Field Name: EntityType
        * * Display Name: Entity Type
        * * SQL Data Type: varchar(50)
        * * Description: Type of entity being processed (Order, Product, etc.).`),
    StartTime: z.date().describe(`
        * * Field Name: StartTime
        * * Display Name: Start Time
        * * SQL Data Type: datetime`),
    EndTime: z.date().nullable().describe(`
        * * Field Name: EndTime
        * * Display Name: End Time
        * * SQL Data Type: datetime`),
    Status: z.string().describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(20)
        * * Default Value: Pending
        * * Description: Processing status (Pending, Running, Completed, Failed).`),
    RecordsProcessed: z.number().describe(`
        * * Field Name: RecordsProcessed
        * * Display Name: Records Processed
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsSucceeded: z.number().describe(`
        * * Field Name: RecordsSucceeded
        * * Display Name: Records Succeeded
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsFailed: z.number().describe(`
        * * Field Name: RecordsFailed
        * * Display Name: Records Failed
        * * SQL Data Type: int
        * * Default Value: 0`),
    ProcessingDuration: z.number().nullable().describe(`
        * * Field Name: ProcessingDuration
        * * Display Name: Processing Duration
        * * SQL Data Type: int
        * * Description: Duration of processing in milliseconds.`),
    ErrorMessage: z.string().nullable().describe(`
        * * Field Name: ErrorMessage
        * * Display Name: Error Message
        * * SQL Data Type: nvarchar(MAX)`),
    CreatedBy: z.string().nullable().describe(`
        * * Field Name: CreatedBy
        * * Display Name: Created By
        * * SQL Data Type: varchar(100)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostProcessingBatchEntityType = z.infer<typeof soundpostProcessingBatchSchema>;

/**
 * zod schema definition for the entity Processing Errors
 */
export const soundpostProcessingErrorSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProcessingBatchID: z.string().nullable().describe(`
        * * Field Name: ProcessingBatchID
        * * Display Name: Processing Batch ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Processing Batches (vwProcessingBatches.ID)`),
    RecordID: z.string().nullable().describe(`
        * * Field Name: RecordID
        * * Display Name: Record ID
        * * SQL Data Type: varchar(100)
        * * Description: Identifier of the record that failed processing.`),
    ErrorType: z.string().describe(`
        * * Field Name: ErrorType
        * * Display Name: Error Type
        * * SQL Data Type: varchar(50)
        * * Description: Type of error (Validation, Mapping, System, etc.).`),
    ErrorCode: z.string().nullable().describe(`
        * * Field Name: ErrorCode
        * * Display Name: Error Code
        * * SQL Data Type: varchar(50)`),
    ErrorMessage: z.string().describe(`
        * * Field Name: ErrorMessage
        * * Display Name: Error Message
        * * SQL Data Type: nvarchar(MAX)`),
    FieldName: z.string().nullable().describe(`
        * * Field Name: FieldName
        * * Display Name: Field Name
        * * SQL Data Type: varchar(100)
        * * Description: Field that caused the error if applicable.`),
    FieldValue: z.string().nullable().describe(`
        * * Field Name: FieldValue
        * * Display Name: Field Value
        * * SQL Data Type: nvarchar(MAX)`),
    SourceData: z.string().nullable().describe(`
        * * Field Name: SourceData
        * * Display Name: Source Data
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON representation of source data that failed.`),
    ErrorDate: z.date().describe(`
        * * Field Name: ErrorDate
        * * Display Name: Error Date
        * * SQL Data Type: datetime
        * * Default Value: getdate()`),
    IsResolved: z.boolean().describe(`
        * * Field Name: IsResolved
        * * Display Name: Is Resolved
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether the error has been resolved.`),
    ResolvedDate: z.date().nullable().describe(`
        * * Field Name: ResolvedDate
        * * Display Name: Resolved Date
        * * SQL Data Type: datetime`),
    ResolvedBy: z.string().nullable().describe(`
        * * Field Name: ResolvedBy
        * * Display Name: Resolved By
        * * SQL Data Type: varchar(100)`),
    Resolution: z.string().nullable().describe(`
        * * Field Name: Resolution
        * * Display Name: Resolution
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostProcessingErrorEntityType = z.infer<typeof soundpostProcessingErrorSchema>;

/**
 * zod schema definition for the entity Product GL Account Dimensions
 */
export const soundpostProductGLAccountDimensionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProductGLAccountID: z.string().describe(`
        * * Field Name: ProductGLAccountID
        * * Display Name: Product GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Product GL Accounts (vwProductGLAccounts.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    DimensionTypeID: z.string().describe(`
        * * Field Name: DimensionTypeID
        * * Display Name: Dimension Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Dimension Types (vwDimensionTypes.ID)
        * * Description: Reference to the dimension type.`),
    DimensionID: z.string().describe(`
        * * Field Name: DimensionID
        * * Display Name: Dimension ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Dimensions (vwDimensions.ID)
        * * Description: Reference to the specific dimension value.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    DimensionType: z.string().describe(`
        * * Field Name: DimensionType
        * * Display Name: Dimension Type
        * * SQL Data Type: varchar(50)`),
    Dimension: z.string().describe(`
        * * Field Name: Dimension
        * * Display Name: Dimension
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostProductGLAccountDimensionEntityType = z.infer<typeof soundpostProductGLAccountDimensionSchema>;

/**
 * zod schema definition for the entity Product GL Accounts
 */
export const soundpostProductGLAccountSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Unique identifier for the product GL account mapping`),
    ProductID: z.string().describe(`
        * * Field Name: ProductID
        * * Display Name: Product ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Products (vwProducts.ID)
        * * Description: Foreign key to the Product table`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Indicates if this GL account mapping is active`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    GLAccountTypeID: z.string().nullable().describe(`
        * * Field Name: GLAccountTypeID
        * * Display Name: GL Account Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Account Types (vwGLAccountTypes.ID)
        * * Description: Foreign key to the GLAccountType table (Revenue, Receivable, etc.)`),
    GLAccountID: z.string().nullable().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
        * * Description: Foreign key to the GLAccount table`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    GLAccountType: z.string().nullable().describe(`
        * * Field Name: GLAccountType
        * * Display Name: GL Account Type
        * * SQL Data Type: varchar(100)`),
    GLAccount: z.string().nullable().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostProductGLAccountEntityType = z.infer<typeof soundpostProductGLAccountSchema>;

/**
 * zod schema definition for the entity Products
 */
export const soundpostProductSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System Record ID
        * * SQL Data Type: varchar(100)
        * * Description: External system product identifier.`),
    ProductName: z.string().describe(`
        * * Field Name: ProductName
        * * Display Name: Product Name
        * * SQL Data Type: nvarchar(500)
        * * Description: Name of the product.`),
    SKU: z.string().nullable().describe(`
        * * Field Name: SKU
        * * Display Name: SKU
        * * SQL Data Type: varchar(100)
        * * Description: Stock Keeping Unit for inventory tracking.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    Category: z.string().nullable().describe(`
        * * Field Name: Category
        * * Display Name: Category
        * * SQL Data Type: varchar(200)
        * * Description: Product category for grouping and mapping.`),
    Price: z.number().nullable().describe(`
        * * Field Name: Price
        * * Display Name: Price
        * * SQL Data Type: decimal(18, 2)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    ProductType: z.string().nullable().describe(`
        * * Field Name: ProductType
        * * Display Name: Product Type
        * * SQL Data Type: varchar(50)
        * * Description: Type of product (Physical, Digital, Service, Membership, etc.).`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)`),
    ReconFlowCategoryID: z.string().nullable().describe(`
        * * Field Name: ReconFlowCategoryID
        * * Display Name: Recon Flow Category ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Recon Flow Categories (vwReconFlowCategories.ID)
        * * Description: Default reconciliation flow category applied to new order lines for this product.`),
    SourceMetadata: z.string().nullable().describe(`
        * * Field Name: SourceMetadata
        * * Display Name: Source Metadata
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON metadata from source system for GL mapping and integration-specific fields.`),
    ParentID: z.string().nullable().describe(`
        * * Field Name: ParentID
        * * Display Name: Parent ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Products (vwProducts.ID)
        * * Description: Parent product identifier. Links to another record in the Product table. Nullable.`),
    RevenueRecognitionDate: z.date().nullable().describe(`
        * * Field Name: RevenueRecognitionDate
        * * Display Name: Revenue Recognition Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date used for revenue recognition, if applicable. Nullable.`),
    HasGLSetupIssue: z.boolean().describe(`
        * * Field Name: HasGLSetupIssue
        * * Display Name: Has GL Setup Issue
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Indicates if this product has a GL account setup issue (1 = issue, 0 = none).`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    ReconFlowCategory: z.string().nullable().describe(`
        * * Field Name: ReconFlowCategory
        * * Display Name: Recon Flow Category
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    RootParentID: z.string().nullable().describe(`
        * * Field Name: RootParentID
        * * Display Name: Root Parent ID
        * * SQL Data Type: uniqueidentifier`),
    ParentIDDepth: z.number().nullable().describe(`
        * * Field Name: ParentIDDepth
        * * Display Name: Parent ID Depth
        * * SQL Data Type: int`),
    ParentIDPath: z.string().nullable().describe(`
        * * Field Name: ParentIDPath
        * * Display Name: Parent ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    ParentIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: ParentIDIsLeaf
        * * Display Name: Parent ID Is Leaf
        * * SQL Data Type: bit`),
    ParentIDChildCount: z.number().nullable().describe(`
        * * Field Name: ParentIDChildCount
        * * Display Name: Parent ID Child Count
        * * SQL Data Type: int`),
});

export type soundpostProductEntityType = z.infer<typeof soundpostProductSchema>;

/**
 * zod schema definition for the entity Recon Balance Snapshots
 */
export const soundpostReconBalanceSnapshotSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    RunID: z.string().describe(`
        * * Field Name: RunID
        * * Display Name: Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Recon Runs (vwReconRuns.ID)
        * * Description: Reconciliation run.`),
    CompanyIntegrationID: z.string().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Integration instance.`),
    ControlAccountTypeID: z.string().describe(`
        * * Field Name: ControlAccountTypeID
        * * Display Name: Control Account Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Control Account Types (vwControlAccountTypes.ID)
        * * Description: Control account type (e.g., AR).`),
    GLAccountID: z.string().nullable().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
        * * Description: Optional GL account when aggregating by account.`),
    AmountBase: z.number().describe(`
        * * Field Name: AmountBase
        * * Display Name: Amount Base
        * * SQL Data Type: decimal(18, 2)
        * * Description: Amount in base currency.`),
    CurrencyID: z.string().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Description: Currency of the amount.`),
    ExchangeRate: z.number().describe(`
        * * Field Name: ExchangeRate
        * * Display Name: Exchange Rate
        * * SQL Data Type: decimal(12, 6)
        * * Default Value: 1.0
        * * Description: Rate to convert to base.`),
    RowCount: z.number().nullable().describe(`
        * * Field Name: RowCount
        * * Display Name: Row Count
        * * SQL Data Type: bigint
        * * Description: Count of detail rows aggregated.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC created timestamp.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC updated timestamp.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegration: z.string().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    ControlAccountType: z.string().describe(`
        * * Field Name: ControlAccountType
        * * Display Name: Control Account Type
        * * SQL Data Type: varchar(100)`),
    GLAccount: z.string().nullable().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReconBalanceSnapshotEntityType = z.infer<typeof soundpostReconBalanceSnapshotSchema>;

/**
 * zod schema definition for the entity Recon Flow Categories
 */
export const soundpostReconFlowCategorySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    Code: z.string().describe(`
        * * Field Name: Code
        * * Display Name: Code
        * * SQL Data Type: varchar(50)
        * * Description: Unique short code.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: varchar(100)
        * * Description: Display name.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)
        * * Description: Optional description.`),
    CategoryDomain: z.union([z.literal('COGS'), z.literal('Other'), z.literal('OtherIncome'), z.literal('Revenue')]).describe(`
        * * Field Name: CategoryDomain
        * * Display Name: Category Domain
        * * SQL Data Type: varchar(30)
        * * Default Value: Revenue
    * * Value List Type: List
    * * Possible Values 
    *   * COGS
    *   * Other
    *   * OtherIncome
    *   * Revenue
        * * Description: Logical domain for rollups.`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Active flag.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC created timestamp.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC updated timestamp.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReconFlowCategoryEntityType = z.infer<typeof soundpostReconFlowCategorySchema>;

/**
 * zod schema definition for the entity Recon Flow Snapshots
 */
export const soundpostReconFlowSnapshotSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    RunID: z.string().describe(`
        * * Field Name: RunID
        * * Display Name: Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Recon Runs (vwReconRuns.ID)
        * * Description: Reconciliation run.`),
    PeriodStart: z.date().describe(`
        * * Field Name: PeriodStart
        * * Display Name: Period Start
        * * SQL Data Type: datetimeoffset
        * * Description: Period start date.`),
    PeriodEnd: z.date().describe(`
        * * Field Name: PeriodEnd
        * * Display Name: Period End
        * * SQL Data Type: datetimeoffset
        * * Description: Period end date.`),
    YTDThrough: z.date().describe(`
        * * Field Name: YTDThrough
        * * Display Name: YTD Through
        * * SQL Data Type: datetimeoffset
        * * Description: YTD through date (usually same as AsOf).`),
    CompanyIntegrationID: z.string().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Integration instance.`),
    ReconFlowCategoryID: z.string().describe(`
        * * Field Name: ReconFlowCategoryID
        * * Display Name: Recon Flow Category ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Recon Flow Categories (vwReconFlowCategories.ID)
        * * Description: Flow category (e.g., Registration).`),
    GLAccountID: z.string().nullable().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
        * * Description: Optional GL account for GL-side rollups.`),
    AmountBase: z.number().describe(`
        * * Field Name: AmountBase
        * * Display Name: Amount Base
        * * SQL Data Type: decimal(18, 2)
        * * Description: Amount in base currency.`),
    CurrencyID: z.string().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Description: Currency of the amount.`),
    ExchangeRate: z.number().describe(`
        * * Field Name: ExchangeRate
        * * Display Name: Exchange Rate
        * * SQL Data Type: decimal(12, 6)
        * * Default Value: 1.0
        * * Description: Rate to convert to base.`),
    RowCount: z.number().nullable().describe(`
        * * Field Name: RowCount
        * * Display Name: Row Count
        * * SQL Data Type: bigint
        * * Description: Number of detailed rows aggregated.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC created timestamp.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC updated timestamp.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegration: z.string().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    ReconFlowCategory: z.string().describe(`
        * * Field Name: ReconFlowCategory
        * * Display Name: Recon Flow Category
        * * SQL Data Type: varchar(100)`),
    GLAccount: z.string().nullable().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReconFlowSnapshotEntityType = z.infer<typeof soundpostReconFlowSnapshotSchema>;

/**
 * zod schema definition for the entity Recon Run Integrations
 */
export const soundpostReconRunIntegrationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    RunID: z.string().describe(`
        * * Field Name: RunID
        * * Display Name: Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Recon Runs (vwReconRuns.ID)
        * * Description: Parent reconciliation run.`),
    CompanyIntegrationID: z.string().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Integration instance (source).`),
    IsAccountingSystem: z.boolean().describe(`
        * * Field Name: IsAccountingSystem
        * * Display Name: Is Accounting System
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Flags accounting system (e.g., Intacct).`),
    Status: z.union([z.literal('Complete'), z.literal('Error'), z.literal('Pending')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(20)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Complete
    *   * Error
    *   * Pending
        * * Description: Extract/processing status.`),
    ExtractRecordCount: z.number().nullable().describe(`
        * * Field Name: ExtractRecordCount
        * * Display Name: Extract Record Count
        * * SQL Data Type: int
        * * Description: Row count fetched from the integration.`),
    ExtractStartedAt: z.date().nullable().describe(`
        * * Field Name: ExtractStartedAt
        * * Display Name: Extract Started At
        * * SQL Data Type: datetimeoffset
        * * Description: UTC start of extract.`),
    ExtractEndedAt: z.date().nullable().describe(`
        * * Field Name: ExtractEndedAt
        * * Display Name: Extract Ended At
        * * SQL Data Type: datetimeoffset
        * * Description: UTC end of extract.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC created timestamp.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC updated timestamp.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Message: z.string().nullable().describe(`
        * * Field Name: Message
        * * Display Name: Message
        * * SQL Data Type: nvarchar(1000)
        * * Description: Outcome message for this integration extract (error text or a short summary).`),
    CompanyIntegration: z.string().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReconRunIntegrationEntityType = z.infer<typeof soundpostReconRunIntegrationSchema>;

/**
 * zod schema definition for the entity Recon Runs
 */
export const soundpostReconRunSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    AsOfDate: z.date().describe(`
        * * Field Name: AsOfDate
        * * Display Name: As Of Date
        * * SQL Data Type: datetimeoffset
        * * Description: Cutoff date for the reconciliation (as-of).`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC created timestamp.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC updated timestamp.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Optional link to the related company record.`),
    PeriodStartDate: z.date().nullable().describe(`
        * * Field Name: PeriodStartDate
        * * Display Name: Period Start Date
        * * SQL Data Type: date
        * * Description: Start of the comparison window (activity is compared for PeriodStartDate..AsOfDate; balances as of AsOfDate). NULL = fiscal-year-to-date (legacy runs).`),
    Status: z.union([z.literal('Complete'), z.literal('Error'), z.literal('Pending'), z.literal('Warning')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(20)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Complete
    *   * Error
    *   * Pending
    *   * Warning
        * * Description: Pending (created, not yet compared), Complete (all Owned accounts within tolerance and readiness gates passed), Warning (an Owned account is outside tolerance or a readiness gate failed), Error (the GL pull failed).`),
    Summary: z.string().nullable().describe(`
        * * Field Name: Summary
        * * Display Name: Summary
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON: per-account comparison rows (GL vs SoundPost buckets, classification, status) and readiness gate results for this run.`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReconRunEntityType = z.infer<typeof soundpostReconRunSchema>;

/**
 * zod schema definition for the entity Report Output AR Agings
 */
export const soundpostReportOutputARAgingSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReportRunID: z.string().describe(`
        * * Field Name: ReportRunID
        * * Display Name: Report Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Report Runs (vwReportRuns.ID)`),
    OrderID: z.string().nullable().describe(`
        * * Field Name: OrderID
        * * Display Name: Order ID
        * * SQL Data Type: uniqueidentifier`),
    OrderNumber: z.string().describe(`
        * * Field Name: OrderNumber
        * * Display Name: Order Number
        * * SQL Data Type: varchar(50)`),
    OrderDate: z.date().describe(`
        * * Field Name: OrderDate
        * * Display Name: Order Date
        * * SQL Data Type: datetimeoffset`),
    OrderTotal: z.number().describe(`
        * * Field Name: OrderTotal
        * * Display Name: Order Total
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0`),
    OrderStatus: z.string().nullable().describe(`
        * * Field Name: OrderStatus
        * * Display Name: Order Status
        * * SQL Data Type: varchar(50)`),
    Integration: z.string().nullable().describe(`
        * * Field Name: Integration
        * * Display Name: Integration
        * * SQL Data Type: varchar(100)`),
    DueDate: z.date().nullable().describe(`
        * * Field Name: DueDate
        * * Display Name: Due Date
        * * SQL Data Type: datetimeoffset`),
    DaysAged: z.number().describe(`
        * * Field Name: DaysAged
        * * Display Name: Days Aged
        * * SQL Data Type: int`),
    Account: z.string().nullable().describe(`
        * * Field Name: Account
        * * Display Name: Account
        * * SQL Data Type: nvarchar(255)`),
    CustomerName: z.string().nullable().describe(`
        * * Field Name: CustomerName
        * * Display Name: Customer Name
        * * SQL Data Type: nvarchar(255)`),
    GLAccountNumber: z.string().describe(`
        * * Field Name: GLAccountNumber
        * * Display Name: GL Account Number
        * * SQL Data Type: varchar(50)`),
    GLAccountName: z.string().nullable().describe(`
        * * Field Name: GLAccountName
        * * Display Name: GL Account Name
        * * SQL Data Type: nvarchar(255)`),
    Location: z.string().nullable().describe(`
        * * Field Name: Location
        * * Display Name: Location
        * * SQL Data Type: nvarchar(255)`),
    Balance: z.number().describe(`
        * * Field Name: Balance
        * * Display Name: Balance
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Total outstanding A/R balance for this row (sum of all aging buckets).`),
    Bucket0Amount: z.number().describe(`
        * * Field Name: Bucket0Amount
        * * Display Name: Bucket 0 Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Aging bucket 0 amount (e.g., Not Due).`),
    Bucket1Amount: z.number().describe(`
        * * Field Name: Bucket1Amount
        * * Display Name: Bucket 1 Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Aging bucket 1 amount (e.g., 0–30 days).`),
    Bucket2Amount: z.number().describe(`
        * * Field Name: Bucket2Amount
        * * Display Name: Bucket 2 Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Aging bucket 2 amount (e.g., 31–60 days).`),
    Bucket3Amount: z.number().describe(`
        * * Field Name: Bucket3Amount
        * * Display Name: Bucket 3 Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Aging bucket 3 amount (e.g., 61–90 days).`),
    Bucket4Amount: z.number().describe(`
        * * Field Name: Bucket4Amount
        * * Display Name: Bucket 4 Amount
        * * SQL Data Type: decimal(18, 2)
        * * Default Value: 0
        * * Description: Aging bucket 4 amount (e.g., 90+ days).`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Link to CompanyIntegration for external URL resolution (e.g., WooCommerce, Pheedloop).`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportOutputARAgingEntityType = z.infer<typeof soundpostReportOutputARAgingSchema>;

/**
 * zod schema definition for the entity Report Output Account Activity Summaries
 */
export const soundpostReportOutputAccountActivitySummarySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    AccountingPeriodID: z.string().describe(`
        * * Field Name: AccountingPeriodID
        * * Display Name: Accounting Period ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier`),
    LocationDimensionCode: z.string().nullable().describe(`
        * * Field Name: LocationDimensionCode
        * * Display Name: Location Dimension Code
        * * SQL Data Type: nvarchar(50)`),
    Company: z.string().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)`),
    AccountingPeriodName: z.string().describe(`
        * * Field Name: AccountingPeriodName
        * * Display Name: Accounting Period Name
        * * SQL Data Type: nvarchar(50)`),
    AccountNumber: z.string().describe(`
        * * Field Name: AccountNumber
        * * Display Name: Account Number
        * * SQL Data Type: nvarchar(50)`),
    GLAccountName: z.string().describe(`
        * * Field Name: GLAccountName
        * * Display Name: GL Account Name
        * * SQL Data Type: nvarchar(255)`),
    GLAccountType: z.string().describe(`
        * * Field Name: GLAccountType
        * * Display Name: GL Account Type
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    LocationDimensionName: z.string().nullable().describe(`
        * * Field Name: LocationDimensionName
        * * Display Name: Location Dimension Name
        * * SQL Data Type: nvarchar(255)`),
    TotalDebits: z.number().describe(`
        * * Field Name: TotalDebits
        * * Display Name: Total Debits
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    TotalCredits: z.number().describe(`
        * * Field Name: TotalCredits
        * * Display Name: Total Credits
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    NetActivity: z.number().describe(`
        * * Field Name: NetActivity
        * * Display Name: Net Activity
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    TransactionCount: z.number().describe(`
        * * Field Name: TransactionCount
        * * Display Name: Transaction Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Company_Virtual: z.string().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
    GLAccount: z.string().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
});

export type soundpostReportOutputAccountActivitySummaryEntityType = z.infer<typeof soundpostReportOutputAccountActivitySummarySchema>;

/**
 * zod schema definition for the entity Report Output Accounting Entry Lines
 */
export const soundpostReportOutputAccountingEntryLineSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AccountingEntryID: z.string().describe(`
        * * Field Name: AccountingEntryID
        * * Display Name: Accounting Entry ID
        * * SQL Data Type: uniqueidentifier`),
    AccountingEntryLineID: z.string().describe(`
        * * Field Name: AccountingEntryLineID
        * * Display Name: Accounting Entry Line ID
        * * SQL Data Type: uniqueidentifier`),
    EntryNumber: z.string().nullable().describe(`
        * * Field Name: EntryNumber
        * * Display Name: Entry Number
        * * SQL Data Type: nvarchar(50)`),
    EntryDate: z.date().nullable().describe(`
        * * Field Name: EntryDate
        * * Display Name: Entry Date
        * * SQL Data Type: datetimeoffset`),
    EntryDescription: z.string().nullable().describe(`
        * * Field Name: EntryDescription
        * * Display Name: Entry Description
        * * SQL Data Type: nvarchar(MAX)`),
    SourceType: z.string().nullable().describe(`
        * * Field Name: SourceType
        * * Display Name: Source Type
        * * SQL Data Type: nvarchar(50)`),
    SourceID: z.string().nullable().describe(`
        * * Field Name: SourceID
        * * Display Name: Source ID
        * * SQL Data Type: uniqueidentifier`),
    GLAccountID: z.string().nullable().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier`),
    AccountNumber: z.string().nullable().describe(`
        * * Field Name: AccountNumber
        * * Display Name: Account Number
        * * SQL Data Type: nvarchar(50)`),
    GLAccountName: z.string().nullable().describe(`
        * * Field Name: GLAccountName
        * * Display Name: GL Account Name
        * * SQL Data Type: nvarchar(255)`),
    GLAccountType: z.string().nullable().describe(`
        * * Field Name: GLAccountType
        * * Display Name: GL Account Type
        * * SQL Data Type: nvarchar(50)`),
    DebitAmount: z.number().nullable().describe(`
        * * Field Name: DebitAmount
        * * Display Name: Debit Amount
        * * SQL Data Type: decimal(19, 4)`),
    CreditAmount: z.number().nullable().describe(`
        * * Field Name: CreditAmount
        * * Display Name: Credit Amount
        * * SQL Data Type: decimal(19, 4)`),
    NetAmount: z.number().nullable().describe(`
        * * Field Name: NetAmount
        * * Display Name: Net Amount
        * * SQL Data Type: decimal(19, 4)`),
    LineDescription: z.string().nullable().describe(`
        * * Field Name: LineDescription
        * * Display Name: Line Description
        * * SQL Data Type: nvarchar(MAX)`),
    LocationDimensionCode: z.string().nullable().describe(`
        * * Field Name: LocationDimensionCode
        * * Display Name: Location Dimension Code
        * * SQL Data Type: nvarchar(50)`),
    LocationDimensionName: z.string().nullable().describe(`
        * * Field Name: LocationDimensionName
        * * Display Name: Location Dimension Name
        * * SQL Data Type: nvarchar(255)`),
    DepartmentDimensionCode: z.string().nullable().describe(`
        * * Field Name: DepartmentDimensionCode
        * * Display Name: Department Dimension Code
        * * SQL Data Type: nvarchar(50)`),
    DepartmentDimensionName: z.string().nullable().describe(`
        * * Field Name: DepartmentDimensionName
        * * Display Name: Department Dimension Name
        * * SQL Data Type: nvarchar(255)`),
    ProjectDimensionCode: z.string().nullable().describe(`
        * * Field Name: ProjectDimensionCode
        * * Display Name: Project Dimension Code
        * * SQL Data Type: nvarchar(50)`),
    ProjectDimensionName: z.string().nullable().describe(`
        * * Field Name: ProjectDimensionName
        * * Display Name: Project Dimension Name
        * * SQL Data Type: nvarchar(255)`),
    ClassDimensionCode: z.string().nullable().describe(`
        * * Field Name: ClassDimensionCode
        * * Display Name: Class Dimension Code
        * * SQL Data Type: nvarchar(50)`),
    ClassDimensionName: z.string().nullable().describe(`
        * * Field Name: ClassDimensionName
        * * Display Name: Class Dimension Name
        * * SQL Data Type: nvarchar(255)`),
    PostingGroupID: z.string().nullable().describe(`
        * * Field Name: PostingGroupID
        * * Display Name: Posting Group ID
        * * SQL Data Type: uniqueidentifier`),
    PostingNumber: z.string().nullable().describe(`
        * * Field Name: PostingNumber
        * * Display Name: Posting Number
        * * SQL Data Type: nvarchar(50)`),
    PostingDate: z.date().nullable().describe(`
        * * Field Name: PostingDate
        * * Display Name: Posting Date
        * * SQL Data Type: datetimeoffset`),
    JENumber: z.string().nullable().describe(`
        * * Field Name: JENumber
        * * Display Name: JE Number
        * * SQL Data Type: nvarchar(100)`),
    AccountingPeriodID: z.string().nullable().describe(`
        * * Field Name: AccountingPeriodID
        * * Display Name: Accounting Period ID
        * * SQL Data Type: uniqueidentifier
        * * Description: The accounting period this entry belongs to (from PostingGroup).`),
    AccountingPeriodName: z.string().nullable().describe(`
        * * Field Name: AccountingPeriodName
        * * Display Name: Accounting Period Name
        * * SQL Data Type: nvarchar(50)
        * * Description: Display name of the accounting period (e.g., January 2025).`),
    AccountingPeriodStartDate: z.date().nullable().describe(`
        * * Field Name: AccountingPeriodStartDate
        * * Display Name: Accounting Period Start Date
        * * SQL Data Type: datetimeoffset`),
    AccountingPeriodEndDate: z.date().nullable().describe(`
        * * Field Name: AccountingPeriodEndDate
        * * Display Name: Accounting Period End Date
        * * SQL Data Type: datetimeoffset`),
    AccountingPeriodIsClosed: z.boolean().nullable().describe(`
        * * Field Name: AccountingPeriodIsClosed
        * * Display Name: Accounting Period Is Closed
        * * SQL Data Type: bit
        * * Description: Whether the accounting period has been closed.`),
    OrderID: z.string().nullable().describe(`
        * * Field Name: OrderID
        * * Display Name: Order ID
        * * SQL Data Type: uniqueidentifier`),
    OrderNumber: z.string().nullable().describe(`
        * * Field Name: OrderNumber
        * * Display Name: Order Number
        * * SQL Data Type: nvarchar(50)`),
    OrderDate: z.date().nullable().describe(`
        * * Field Name: OrderDate
        * * Display Name: Order Date
        * * SQL Data Type: datetimeoffset`),
    CustomerName: z.string().nullable().describe(`
        * * Field Name: CustomerName
        * * Display Name: Customer Name
        * * SQL Data Type: nvarchar(255)`),
    AgreementCode: z.string().nullable().describe(`
        * * Field Name: AgreementCode
        * * Display Name: Agreement Code
        * * SQL Data Type: nvarchar(50)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    ProductID: z.string().nullable().describe(`
        * * Field Name: ProductID
        * * Display Name: Product ID
        * * SQL Data Type: uniqueidentifier`),
    ProductExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ProductExternalSystemRecordID
        * * Display Name: Product External System Record ID
        * * SQL Data Type: nvarchar(255)`),
    SKU: z.string().nullable().describe(`
        * * Field Name: SKU
        * * Display Name: SKU
        * * SQL Data Type: nvarchar(100)`),
    ProductName: z.string().nullable().describe(`
        * * Field Name: ProductName
        * * Display Name: Product Name
        * * SQL Data Type: nvarchar(255)`),
    ProductCategory: z.string().nullable().describe(`
        * * Field Name: ProductCategory
        * * Display Name: Product Category
        * * SQL Data Type: nvarchar(100)`),
    ProductType: z.string().nullable().describe(`
        * * Field Name: ProductType
        * * Display Name: Product Type
        * * SQL Data Type: nvarchar(100)`),
    ProductRevenueRecognitionDate: z.date().nullable().describe(`
        * * Field Name: ProductRevenueRecognitionDate
        * * Display Name: Product Revenue Recognition Date
        * * SQL Data Type: datetimeoffset`),
    ParentProductID: z.string().nullable().describe(`
        * * Field Name: ParentProductID
        * * Display Name: Parent Product ID
        * * SQL Data Type: uniqueidentifier`),
    ParentProductExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ParentProductExternalSystemRecordID
        * * Display Name: Parent Product External System Record ID
        * * SQL Data Type: nvarchar(255)`),
    ParentProductSKU: z.string().nullable().describe(`
        * * Field Name: ParentProductSKU
        * * Display Name: Parent Product SKU
        * * SQL Data Type: nvarchar(100)`),
    ParentProductName: z.string().nullable().describe(`
        * * Field Name: ParentProductName
        * * Display Name: Parent Product Name
        * * SQL Data Type: nvarchar(255)`),
    ParentProductCategory: z.string().nullable().describe(`
        * * Field Name: ParentProductCategory
        * * Display Name: Parent Product Category
        * * SQL Data Type: nvarchar(100)`),
    ParentProductType: z.string().nullable().describe(`
        * * Field Name: ParentProductType
        * * Display Name: Parent Product Type
        * * SQL Data Type: nvarchar(100)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    AgreementAccountName: z.string().nullable().describe(`
        * * Field Name: AgreementAccountName
        * * Display Name: Agreement Account Name
        * * SQL Data Type: nvarchar(255)
        * * Description: The account name from the Agreement associated with this entry line, used for grouping and filtering in the Agreement GL Activity Dashboard`),
    Company_Virtual: z.string().nullable().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportOutputAccountingEntryLineEntityType = z.infer<typeof soundpostReportOutputAccountingEntryLineSchema>;

/**
 * zod schema definition for the entity Report Output Agreement Balances
 */
export const soundpostReportOutputAgreementBalanceSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AgreementID: z.string().describe(`
        * * Field Name: AgreementID
        * * Display Name: Agreement ID
        * * SQL Data Type: uniqueidentifier`),
    AgreementCode: z.string().nullable().describe(`
        * * Field Name: AgreementCode
        * * Display Name: Agreement Code
        * * SQL Data Type: nvarchar(50)`),
    AccountName: z.string().nullable().describe(`
        * * Field Name: AccountName
        * * Display Name: Account Name
        * * SQL Data Type: nvarchar(255)`),
    ContactName: z.string().nullable().describe(`
        * * Field Name: ContactName
        * * Display Name: Contact Name
        * * SQL Data Type: nvarchar(255)`),
    AgreementType: z.string().nullable().describe(`
        * * Field Name: AgreementType
        * * Display Name: Agreement Type
        * * SQL Data Type: nvarchar(200)`),
    AgreementStatus: z.string().nullable().describe(`
        * * Field Name: AgreementStatus
        * * Display Name: Agreement Status
        * * SQL Data Type: nvarchar(200)`),
    StartDate: z.date().nullable().describe(`
        * * Field Name: StartDate
        * * Display Name: Start Date
        * * SQL Data Type: datetimeoffset`),
    EndDate: z.date().nullable().describe(`
        * * Field Name: EndDate
        * * Display Name: End Date
        * * SQL Data Type: datetimeoffset`),
    TotalAllocated: z.number().describe(`
        * * Field Name: TotalAllocated
        * * Display Name: Total Allocated
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0
        * * Description: Sum of AllocatedAmount from all AgreementLines for this agreement.`),
    TotalAdjustment: z.number().describe(`
        * * Field Name: TotalAdjustment
        * * Display Name: Total Adjustment
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0
        * * Description: Sum of AdjustmentAmount from all AgreementLines for this agreement.`),
    TotalUsage: z.number().describe(`
        * * Field Name: TotalUsage
        * * Display Name: Total Usage
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0
        * * Description: Sum of LineAmount from all Orders with matching AgreementCode.`),
    CurrentBalance: z.number().describe(`
        * * Field Name: CurrentBalance
        * * Display Name: Current Balance
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0
        * * Description: Calculated balance: TotalAllocated + TotalAdjustment - TotalUsage.`),
    UsageThisMonth: z.number().nullable().describe(`
        * * Field Name: UsageThisMonth
        * * Display Name: Usage This Month
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    UsageLastMonth: z.number().nullable().describe(`
        * * Field Name: UsageLastMonth
        * * Display Name: Usage Last Month
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    AgreementLineCount: z.number().nullable().describe(`
        * * Field Name: AgreementLineCount
        * * Display Name: Agreement Line Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    OrderCount: z.number().nullable().describe(`
        * * Field Name: OrderCount
        * * Display Name: Order Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    LastCalculatedAt: z.date().describe(`
        * * Field Name: LastCalculatedAt
        * * Display Name: Last Calculated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportOutputAgreementBalanceEntityType = z.infer<typeof soundpostReportOutputAgreementBalanceSchema>;

/**
 * zod schema definition for the entity Report Output Agreement GL Activity Summaries
 */
export const soundpostReportOutputAgreementGLActivitySummarySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    AccountingPeriodID: z.string().describe(`
        * * Field Name: AccountingPeriodID
        * * Display Name: Accounting Period ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)`),
    AgreementCode: z.string().describe(`
        * * Field Name: AgreementCode
        * * Display Name: Agreement Code
        * * SQL Data Type: nvarchar(50)`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier`),
    LocationDimensionCode: z.string().nullable().describe(`
        * * Field Name: LocationDimensionCode
        * * Display Name: Location Dimension Code
        * * SQL Data Type: nvarchar(50)`),
    Company: z.string().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)`),
    AccountingPeriodName: z.string().describe(`
        * * Field Name: AccountingPeriodName
        * * Display Name: Accounting Period Name
        * * SQL Data Type: nvarchar(50)`),
    AgreementAccountName: z.string().nullable().describe(`
        * * Field Name: AgreementAccountName
        * * Display Name: Agreement Account Name
        * * SQL Data Type: nvarchar(255)`),
    AccountNumber: z.string().describe(`
        * * Field Name: AccountNumber
        * * Display Name: Account Number
        * * SQL Data Type: nvarchar(50)`),
    GLAccountName: z.string().describe(`
        * * Field Name: GLAccountName
        * * Display Name: GL Account Name
        * * SQL Data Type: nvarchar(255)`),
    GLAccountType: z.string().describe(`
        * * Field Name: GLAccountType
        * * Display Name: GL Account Type
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    LocationDimensionName: z.string().nullable().describe(`
        * * Field Name: LocationDimensionName
        * * Display Name: Location Dimension Name
        * * SQL Data Type: nvarchar(255)`),
    TotalDebits: z.number().describe(`
        * * Field Name: TotalDebits
        * * Display Name: Total Debits
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    TotalCredits: z.number().describe(`
        * * Field Name: TotalCredits
        * * Display Name: Total Credits
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    NetActivity: z.number().describe(`
        * * Field Name: NetActivity
        * * Display Name: Net Activity
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    TransactionCount: z.number().describe(`
        * * Field Name: TransactionCount
        * * Display Name: Transaction Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Company_Virtual: z.string().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
    GLAccount: z.string().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
});

export type soundpostReportOutputAgreementGLActivitySummaryEntityType = z.infer<typeof soundpostReportOutputAgreementGLActivitySummarySchema>;

/**
 * zod schema definition for the entity Report Output Deferred Revenue By Schedules
 */
export const soundpostReportOutputDeferredRevenueByScheduleSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReportRunID: z.string().describe(`
        * * Field Name: ReportRunID
        * * Display Name: Report Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Report Runs (vwReportRuns.ID)`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier`),
    AccountNumber: z.string().describe(`
        * * Field Name: AccountNumber
        * * Display Name: Account Number
        * * SQL Data Type: nvarchar(50)`),
    GLAccountName: z.string().describe(`
        * * Field Name: GLAccountName
        * * Display Name: GL Account Name
        * * SQL Data Type: nvarchar(255)`),
    AccountingScheduleID: z.string().nullable().describe(`
        * * Field Name: AccountingScheduleID
        * * Display Name: Accounting Schedule ID
        * * SQL Data Type: uniqueidentifier`),
    RecognitionMethod: z.string().nullable().describe(`
        * * Field Name: RecognitionMethod
        * * Display Name: Recognition Method
        * * SQL Data Type: nvarchar(50)`),
    ScheduleStartDate: z.date().nullable().describe(`
        * * Field Name: ScheduleStartDate
        * * Display Name: Schedule Start Date
        * * SQL Data Type: datetimeoffset`),
    ScheduleEndDate: z.date().nullable().describe(`
        * * Field Name: ScheduleEndDate
        * * Display Name: Schedule End Date
        * * SQL Data Type: datetimeoffset`),
    ScheduleTotalAmount: z.number().nullable().describe(`
        * * Field Name: ScheduleTotalAmount
        * * Display Name: Schedule Total Amount
        * * SQL Data Type: decimal(19, 4)`),
    DeferredBalance: z.number().describe(`
        * * Field Name: DeferredBalance
        * * Display Name: Deferred Balance
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    RecognizedAmount: z.number().describe(`
        * * Field Name: RecognizedAmount
        * * Display Name: Recognized Amount
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    UnpostedEntryCount: z.number().describe(`
        * * Field Name: UnpostedEntryCount
        * * Display Name: Unposted Entry Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    OrderID: z.string().nullable().describe(`
        * * Field Name: OrderID
        * * Display Name: Order ID
        * * SQL Data Type: uniqueidentifier`),
    OrderNumber: z.string().nullable().describe(`
        * * Field Name: OrderNumber
        * * Display Name: Order Number
        * * SQL Data Type: nvarchar(100)`),
    OrderDate: z.date().nullable().describe(`
        * * Field Name: OrderDate
        * * Display Name: Order Date
        * * SQL Data Type: datetimeoffset`),
    CustomerName: z.string().nullable().describe(`
        * * Field Name: CustomerName
        * * Display Name: Customer Name
        * * SQL Data Type: nvarchar(500)`),
    ProductID: z.string().nullable().describe(`
        * * Field Name: ProductID
        * * Display Name: Product ID
        * * SQL Data Type: uniqueidentifier`),
    ProductName: z.string().nullable().describe(`
        * * Field Name: ProductName
        * * Display Name: Product Name
        * * SQL Data Type: nvarchar(500)`),
    ProductCategory: z.string().nullable().describe(`
        * * Field Name: ProductCategory
        * * Display Name: Product Category
        * * SQL Data Type: nvarchar(100)`),
    LocationCode: z.string().nullable().describe(`
        * * Field Name: LocationCode
        * * Display Name: Location Code
        * * SQL Data Type: nvarchar(50)`),
    DepartmentCode: z.string().nullable().describe(`
        * * Field Name: DepartmentCode
        * * Display Name: Department Code
        * * SQL Data Type: nvarchar(50)`),
    ClassCode: z.string().nullable().describe(`
        * * Field Name: ClassCode
        * * Display Name: Class Code
        * * SQL Data Type: nvarchar(50)`),
    ProjectCode: z.string().nullable().describe(`
        * * Field Name: ProjectCode
        * * Display Name: Project Code
        * * SQL Data Type: nvarchar(50)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)`),
    RecognitionThisMonth: z.number().describe(`
        * * Field Name: RecognitionThisMonth
        * * Display Name: Recognition This Month
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    RecognitionRestOfYear: z.number().describe(`
        * * Field Name: RecognitionRestOfYear
        * * Display Name: Recognition Rest Of Year
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    RecognitionBeyond: z.number().describe(`
        * * Field Name: RecognitionBeyond
        * * Display Name: Recognition Beyond
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    RecognitionNextYear: z.number().describe(`
        * * Field Name: RecognitionNextYear
        * * Display Name: Recognition Next Year
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    AgreementCode: z.string().nullable().describe(`
        * * Field Name: AgreementCode
        * * Display Name: Agreement Code
        * * SQL Data Type: nvarchar(100)`),
    RecognitionUnscheduled: z.number().describe(`
        * * Field Name: RecognitionUnscheduled
        * * Display Name: Recognition Unscheduled
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company_Virtual: z.string().nullable().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportOutputDeferredRevenueByScheduleEntityType = z.infer<typeof soundpostReportOutputDeferredRevenueByScheduleSchema>;

/**
 * zod schema definition for the entity Report Output Deferred Revenue Details
 */
export const soundpostReportOutputDeferredRevenueDetailSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReportRunID: z.string().describe(`
        * * Field Name: ReportRunID
        * * Display Name: Report Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Report Runs (vwReportRuns.ID)`),
    AccountingEntryLineID: z.string().describe(`
        * * Field Name: AccountingEntryLineID
        * * Display Name: Accounting Entry Line ID
        * * SQL Data Type: uniqueidentifier`),
    AccountingEntryID: z.string().describe(`
        * * Field Name: AccountingEntryID
        * * Display Name: Accounting Entry ID
        * * SQL Data Type: uniqueidentifier`),
    EntryNumber: z.string().nullable().describe(`
        * * Field Name: EntryNumber
        * * Display Name: Entry Number
        * * SQL Data Type: nvarchar(50)`),
    EntryDate: z.date().nullable().describe(`
        * * Field Name: EntryDate
        * * Display Name: Entry Date
        * * SQL Data Type: datetimeoffset`),
    EntryDescription: z.string().nullable().describe(`
        * * Field Name: EntryDescription
        * * Display Name: Entry Description
        * * SQL Data Type: nvarchar(MAX)`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier`),
    AccountNumber: z.string().describe(`
        * * Field Name: AccountNumber
        * * Display Name: Account Number
        * * SQL Data Type: nvarchar(50)`),
    GLAccountName: z.string().describe(`
        * * Field Name: GLAccountName
        * * Display Name: GL Account Name
        * * SQL Data Type: nvarchar(255)`),
    DebitAmount: z.number().describe(`
        * * Field Name: DebitAmount
        * * Display Name: Debit Amount
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    CreditAmount: z.number().describe(`
        * * Field Name: CreditAmount
        * * Display Name: Credit Amount
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    NetAmount: z.number().describe(`
        * * Field Name: NetAmount
        * * Display Name: Net Amount
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    AccountingScheduleID: z.string().nullable().describe(`
        * * Field Name: AccountingScheduleID
        * * Display Name: Accounting Schedule ID
        * * SQL Data Type: uniqueidentifier`),
    ScheduleStartDate: z.date().nullable().describe(`
        * * Field Name: ScheduleStartDate
        * * Display Name: Schedule Start Date
        * * SQL Data Type: datetimeoffset`),
    ScheduleEndDate: z.date().nullable().describe(`
        * * Field Name: ScheduleEndDate
        * * Display Name: Schedule End Date
        * * SQL Data Type: datetimeoffset`),
    ScheduleTotalAmount: z.number().nullable().describe(`
        * * Field Name: ScheduleTotalAmount
        * * Display Name: Schedule Total Amount
        * * SQL Data Type: decimal(19, 4)`),
    OrderID: z.string().nullable().describe(`
        * * Field Name: OrderID
        * * Display Name: Order ID
        * * SQL Data Type: uniqueidentifier`),
    OrderNumber: z.string().nullable().describe(`
        * * Field Name: OrderNumber
        * * Display Name: Order Number
        * * SQL Data Type: nvarchar(100)`),
    OrderDate: z.date().nullable().describe(`
        * * Field Name: OrderDate
        * * Display Name: Order Date
        * * SQL Data Type: datetimeoffset`),
    CustomerName: z.string().nullable().describe(`
        * * Field Name: CustomerName
        * * Display Name: Customer Name
        * * SQL Data Type: nvarchar(500)`),
    ProductID: z.string().nullable().describe(`
        * * Field Name: ProductID
        * * Display Name: Product ID
        * * SQL Data Type: uniqueidentifier`),
    ProductName: z.string().nullable().describe(`
        * * Field Name: ProductName
        * * Display Name: Product Name
        * * SQL Data Type: nvarchar(500)`),
    LocationCode: z.string().nullable().describe(`
        * * Field Name: LocationCode
        * * Display Name: Location Code
        * * SQL Data Type: nvarchar(50)`),
    DepartmentCode: z.string().nullable().describe(`
        * * Field Name: DepartmentCode
        * * Display Name: Department Code
        * * SQL Data Type: nvarchar(50)`),
    ClassCode: z.string().nullable().describe(`
        * * Field Name: ClassCode
        * * Display Name: Class Code
        * * SQL Data Type: nvarchar(50)`),
    ProjectCode: z.string().nullable().describe(`
        * * Field Name: ProjectCode
        * * Display Name: Project Code
        * * SQL Data Type: nvarchar(50)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)`),
    ProductCategory: z.string().nullable().describe(`
        * * Field Name: ProductCategory
        * * Display Name: Product Category
        * * SQL Data Type: nvarchar(100)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    OrderLinePostingGroupID: z.string().nullable().describe(`
        * * Field Name: OrderLinePostingGroupID
        * * Display Name: Order Line Posting Group ID
        * * SQL Data Type: uniqueidentifier`),
    OrderLinePostingNumber: z.string().nullable().describe(`
        * * Field Name: OrderLinePostingNumber
        * * Display Name: Order Line Posting Number
        * * SQL Data Type: nvarchar(50)`),
    OrderLinePostingDate: z.date().nullable().describe(`
        * * Field Name: OrderLinePostingDate
        * * Display Name: Order Line Posting Date
        * * SQL Data Type: datetimeoffset`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company_Virtual: z.string().nullable().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportOutputDeferredRevenueDetailEntityType = z.infer<typeof soundpostReportOutputDeferredRevenueDetailSchema>;

/**
 * zod schema definition for the entity Report Output Deferred Revenue Summaries
 */
export const soundpostReportOutputDeferredRevenueSummarySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReportRunID: z.string().describe(`
        * * Field Name: ReportRunID
        * * Display Name: Report Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Report Runs (vwReportRuns.ID)`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier`),
    AccountNumber: z.string().describe(`
        * * Field Name: AccountNumber
        * * Display Name: Account Number
        * * SQL Data Type: nvarchar(50)`),
    GLAccountName: z.string().describe(`
        * * Field Name: GLAccountName
        * * Display Name: GL Account Name
        * * SQL Data Type: nvarchar(255)`),
    DeferredBalance: z.number().describe(`
        * * Field Name: DeferredBalance
        * * Display Name: Deferred Balance
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    EntryCount: z.number().describe(`
        * * Field Name: EntryCount
        * * Display Name: Entry Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    ActiveScheduleCount: z.number().describe(`
        * * Field Name: ActiveScheduleCount
        * * Display Name: Active Schedule Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecognitionThisMonth: z.number().describe(`
        * * Field Name: RecognitionThisMonth
        * * Display Name: Recognition This Month
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    RecognitionRestOfYear: z.number().describe(`
        * * Field Name: RecognitionRestOfYear
        * * Display Name: Recognition Rest Of Year
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    RecognitionBeyond: z.number().describe(`
        * * Field Name: RecognitionBeyond
        * * Display Name: Recognition Beyond
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    RecognitionNextYear: z.number().describe(`
        * * Field Name: RecognitionNextYear
        * * Display Name: Recognition Next Year
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    RecognitionUnscheduled: z.number().describe(`
        * * Field Name: RecognitionUnscheduled
        * * Display Name: Recognition Unscheduled
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportOutputDeferredRevenueSummaryEntityType = z.infer<typeof soundpostReportOutputDeferredRevenueSummarySchema>;

/**
 * zod schema definition for the entity Report Output Order Payment Lines
 */
export const soundpostReportOutputOrderPaymentLineSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrderID: z.string().describe(`
        * * Field Name: OrderID
        * * Display Name: Order ID
        * * SQL Data Type: uniqueidentifier`),
    OrderLineID: z.string().describe(`
        * * Field Name: OrderLineID
        * * Display Name: Order Line ID
        * * SQL Data Type: uniqueidentifier`),
    OrderNumber: z.string().nullable().describe(`
        * * Field Name: OrderNumber
        * * Display Name: Order Number
        * * SQL Data Type: nvarchar(50)`),
    OrderExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: OrderExternalSystemRecordID
        * * Display Name: Order External System Record ID
        * * SQL Data Type: nvarchar(255)`),
    OrderDate: z.date().nullable().describe(`
        * * Field Name: OrderDate
        * * Display Name: Order Date
        * * SQL Data Type: datetimeoffset`),
    DueDate: z.date().nullable().describe(`
        * * Field Name: DueDate
        * * Display Name: Due Date
        * * SQL Data Type: datetimeoffset`),
    OrderStatus: z.string().nullable().describe(`
        * * Field Name: OrderStatus
        * * Display Name: Order Status
        * * SQL Data Type: nvarchar(50)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)`),
    CustomerName: z.string().nullable().describe(`
        * * Field Name: CustomerName
        * * Display Name: Customer Name
        * * SQL Data Type: nvarchar(255)`),
    OrderPaymentMethod: z.string().nullable().describe(`
        * * Field Name: OrderPaymentMethod
        * * Display Name: Order Payment Method
        * * SQL Data Type: nvarchar(50)`),
    AgreementCode: z.string().nullable().describe(`
        * * Field Name: AgreementCode
        * * Display Name: Agreement Code
        * * SQL Data Type: nvarchar(50)`),
    TotalAmount: z.number().nullable().describe(`
        * * Field Name: TotalAmount
        * * Display Name: Total Amount
        * * SQL Data Type: decimal(19, 4)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    LineNumber: z.number().nullable().describe(`
        * * Field Name: LineNumber
        * * Display Name: Line Number
        * * SQL Data Type: int`),
    OrderLineExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: OrderLineExternalSystemRecordID
        * * Display Name: Order Line External System Record ID
        * * SQL Data Type: nvarchar(255)`),
    UnitPrice: z.number().nullable().describe(`
        * * Field Name: UnitPrice
        * * Display Name: Unit Price
        * * SQL Data Type: decimal(19, 4)`),
    Quantity: z.number().nullable().describe(`
        * * Field Name: Quantity
        * * Display Name: Quantity
        * * SQL Data Type: decimal(19, 4)`),
    OrderLineAmount: z.number().nullable().describe(`
        * * Field Name: OrderLineAmount
        * * Display Name: Order Line Amount
        * * SQL Data Type: decimal(19, 4)`),
    OrderLineDiscountAmount: z.number().nullable().describe(`
        * * Field Name: OrderLineDiscountAmount
        * * Display Name: Order Line Discount Amount
        * * SQL Data Type: decimal(19, 4)`),
    OrderLineTaxAmount: z.number().nullable().describe(`
        * * Field Name: OrderLineTaxAmount
        * * Display Name: Order Line Tax Amount
        * * SQL Data Type: decimal(19, 4)`),
    RecognitionStartDate: z.date().nullable().describe(`
        * * Field Name: RecognitionStartDate
        * * Display Name: Recognition Start Date
        * * SQL Data Type: datetimeoffset`),
    RecognitionEndDate: z.date().nullable().describe(`
        * * Field Name: RecognitionEndDate
        * * Display Name: Recognition End Date
        * * SQL Data Type: datetimeoffset`),
    ProductID: z.string().nullable().describe(`
        * * Field Name: ProductID
        * * Display Name: Product ID
        * * SQL Data Type: uniqueidentifier`),
    ProductExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ProductExternalSystemRecordID
        * * Display Name: Product External System Record ID
        * * SQL Data Type: nvarchar(255)`),
    SKU: z.string().nullable().describe(`
        * * Field Name: SKU
        * * Display Name: SKU
        * * SQL Data Type: nvarchar(100)`),
    ProductName: z.string().nullable().describe(`
        * * Field Name: ProductName
        * * Display Name: Product Name
        * * SQL Data Type: nvarchar(255)`),
    ProductCategory: z.string().nullable().describe(`
        * * Field Name: ProductCategory
        * * Display Name: Product Category
        * * SQL Data Type: nvarchar(100)`),
    ProductType: z.string().nullable().describe(`
        * * Field Name: ProductType
        * * Display Name: Product Type
        * * SQL Data Type: nvarchar(100)`),
    ProductRevenueRecognitionDate: z.date().nullable().describe(`
        * * Field Name: ProductRevenueRecognitionDate
        * * Display Name: Product Revenue Recognition Date
        * * SQL Data Type: datetimeoffset`),
    ParentProductID: z.string().nullable().describe(`
        * * Field Name: ParentProductID
        * * Display Name: Parent Product ID
        * * SQL Data Type: uniqueidentifier`),
    ParentProductExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ParentProductExternalSystemRecordID
        * * Display Name: Parent Product External System Record ID
        * * SQL Data Type: nvarchar(255)`),
    ParentProductSKU: z.string().nullable().describe(`
        * * Field Name: ParentProductSKU
        * * Display Name: Parent Product SKU
        * * SQL Data Type: nvarchar(100)`),
    ParentProductName: z.string().nullable().describe(`
        * * Field Name: ParentProductName
        * * Display Name: Parent Product Name
        * * SQL Data Type: nvarchar(255)`),
    ParentProductCategory: z.string().nullable().describe(`
        * * Field Name: ParentProductCategory
        * * Display Name: Parent Product Category
        * * SQL Data Type: nvarchar(100)`),
    ParentProductType: z.string().nullable().describe(`
        * * Field Name: ParentProductType
        * * Display Name: Parent Product Type
        * * SQL Data Type: nvarchar(100)`),
    PaymentID: z.string().nullable().describe(`
        * * Field Name: PaymentID
        * * Display Name: Payment ID
        * * SQL Data Type: uniqueidentifier`),
    PaymentLineID: z.string().nullable().describe(`
        * * Field Name: PaymentLineID
        * * Display Name: Payment Line ID
        * * SQL Data Type: uniqueidentifier`),
    PaymentNumber: z.string().nullable().describe(`
        * * Field Name: PaymentNumber
        * * Display Name: Payment Number
        * * SQL Data Type: nvarchar(50)`),
    PaymentDate: z.date().nullable().describe(`
        * * Field Name: PaymentDate
        * * Display Name: Payment Date
        * * SQL Data Type: datetimeoffset`),
    PaymentStatus: z.string().nullable().describe(`
        * * Field Name: PaymentStatus
        * * Display Name: Payment Status
        * * SQL Data Type: nvarchar(50)`),
    PaymentMethod: z.string().nullable().describe(`
        * * Field Name: PaymentMethod
        * * Display Name: Payment Method
        * * SQL Data Type: nvarchar(50)`),
    AmountApplied: z.number().nullable().describe(`
        * * Field Name: AmountApplied
        * * Display Name: Amount Applied
        * * SQL Data Type: decimal(19, 4)`),
    ProcessingFee: z.number().nullable().describe(`
        * * Field Name: ProcessingFee
        * * Display Name: Processing Fee
        * * SQL Data Type: decimal(19, 4)`),
    OrderLineAccountingEntryID: z.string().nullable().describe(`
        * * Field Name: OrderLineAccountingEntryID
        * * Display Name: Order Line Accounting Entry ID
        * * SQL Data Type: uniqueidentifier`),
    OrderLinePostingGroupID: z.string().nullable().describe(`
        * * Field Name: OrderLinePostingGroupID
        * * Display Name: Order Line Posting Group ID
        * * SQL Data Type: uniqueidentifier`),
    OrderLinePostingNumber: z.string().nullable().describe(`
        * * Field Name: OrderLinePostingNumber
        * * Display Name: Order Line Posting Number
        * * SQL Data Type: nvarchar(50)`),
    OrderLinePostingDate: z.date().nullable().describe(`
        * * Field Name: OrderLinePostingDate
        * * Display Name: Order Line Posting Date
        * * SQL Data Type: datetimeoffset`),
    AccountingPeriodID: z.string().nullable().describe(`
        * * Field Name: AccountingPeriodID
        * * Display Name: Accounting Period ID
        * * SQL Data Type: uniqueidentifier
        * * Description: The accounting period this order line posting belongs to.`),
    AccountingPeriodName: z.string().nullable().describe(`
        * * Field Name: AccountingPeriodName
        * * Display Name: Accounting Period Name
        * * SQL Data Type: nvarchar(50)`),
    AccountingPeriodStartDate: z.date().nullable().describe(`
        * * Field Name: AccountingPeriodStartDate
        * * Display Name: Accounting Period Start Date
        * * SQL Data Type: datetimeoffset`),
    AccountingPeriodEndDate: z.date().nullable().describe(`
        * * Field Name: AccountingPeriodEndDate
        * * Display Name: Accounting Period End Date
        * * SQL Data Type: datetimeoffset`),
    AccountingPeriodIsClosed: z.boolean().nullable().describe(`
        * * Field Name: AccountingPeriodIsClosed
        * * Display Name: Accounting Period Is Closed
        * * SQL Data Type: bit`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    OrderLineIsReversal: z.boolean().nullable().describe(`
        * * Field Name: OrderLineIsReversal
        * * Display Name: Order Line Is Reversal
        * * SQL Data Type: bit`),
    Company_Virtual: z.string().nullable().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportOutputOrderPaymentLineEntityType = z.infer<typeof soundpostReportOutputOrderPaymentLineSchema>;

/**
 * zod schema definition for the entity Report Output Product GL Activity Summaries
 */
export const soundpostReportOutputProductGLActivitySummarySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    AccountingPeriodID: z.string().describe(`
        * * Field Name: AccountingPeriodID
        * * Display Name: Accounting Period ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)`),
    ProductExternalSystemRecordID: z.string().describe(`
        * * Field Name: ProductExternalSystemRecordID
        * * Display Name: Product External System Record ID
        * * SQL Data Type: nvarchar(255)`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)`),
    CompanyIntegrationID: z.string().nullable().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier`),
    LocationDimensionCode: z.string().nullable().describe(`
        * * Field Name: LocationDimensionCode
        * * Display Name: Location Dimension Code
        * * SQL Data Type: nvarchar(50)`),
    Company: z.string().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(255)`),
    AccountingPeriodName: z.string().describe(`
        * * Field Name: AccountingPeriodName
        * * Display Name: Accounting Period Name
        * * SQL Data Type: nvarchar(50)`),
    ProductName: z.string().describe(`
        * * Field Name: ProductName
        * * Display Name: Product Name
        * * SQL Data Type: nvarchar(255)`),
    ProductCategory: z.string().nullable().describe(`
        * * Field Name: ProductCategory
        * * Display Name: Product Category
        * * SQL Data Type: nvarchar(100)`),
    AccountNumber: z.string().describe(`
        * * Field Name: AccountNumber
        * * Display Name: Account Number
        * * SQL Data Type: nvarchar(50)`),
    GLAccountName: z.string().describe(`
        * * Field Name: GLAccountName
        * * Display Name: GL Account Name
        * * SQL Data Type: nvarchar(255)`),
    GLAccountType: z.string().describe(`
        * * Field Name: GLAccountType
        * * Display Name: GL Account Type
        * * SQL Data Type: nvarchar(50)`),
    CompanyIntegration: z.string().nullable().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    LocationDimensionName: z.string().nullable().describe(`
        * * Field Name: LocationDimensionName
        * * Display Name: Location Dimension Name
        * * SQL Data Type: nvarchar(255)`),
    TotalDebits: z.number().describe(`
        * * Field Name: TotalDebits
        * * Display Name: Total Debits
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    TotalCredits: z.number().describe(`
        * * Field Name: TotalCredits
        * * Display Name: Total Credits
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    NetActivity: z.number().describe(`
        * * Field Name: NetActivity
        * * Display Name: Net Activity
        * * SQL Data Type: decimal(19, 4)
        * * Default Value: 0`),
    TransactionCount: z.number().describe(`
        * * Field Name: TransactionCount
        * * Display Name: Transaction Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Company_Virtual: z.string().describe(`
        * * Field Name: Company_Virtual
        * * Display Name: Company Virtual
        * * SQL Data Type: nvarchar(50)`),
    GLAccount: z.string().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
});

export type soundpostReportOutputProductGLActivitySummaryEntityType = z.infer<typeof soundpostReportOutputProductGLActivitySummarySchema>;

/**
 * zod schema definition for the entity Report Runs
 */
export const soundpostReportRunSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReportTypeID: z.string().describe(`
        * * Field Name: ReportTypeID
        * * Display Name: Report Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Report Types (vwReportTypes.ID)`),
    RunDate: z.date().describe(`
        * * Field Name: RunDate
        * * Display Name: Run Date
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    NumRecords: z.number().describe(`
        * * Field Name: NumRecords
        * * Display Name: Num Records
        * * SQL Data Type: int
        * * Default Value: 0`),
    AsOfDate: z.date().nullable().describe(`
        * * Field Name: AsOfDate
        * * Display Name: As Of Date
        * * SQL Data Type: datetimeoffset
        * * Description: As-of date used when generating the report.`),
    StartDate: z.date().nullable().describe(`
        * * Field Name: StartDate
        * * Display Name: Start Date
        * * SQL Data Type: datetimeoffset`),
    EndDate: z.date().nullable().describe(`
        * * Field Name: EndDate
        * * Display Name: End Date
        * * SQL Data Type: datetimeoffset`),
    AccountingPeriodID: z.string().nullable().describe(`
        * * Field Name: AccountingPeriodID
        * * Display Name: Accounting Period ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)`),
    ReportParametersJson: z.string().nullable().describe(`
        * * Field Name: ReportParametersJson
        * * Display Name: Report Parameters Json
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Additional report parameters serialized as JSON (e.g., filters, client-specific options).`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    ReportType: z.string().describe(`
        * * Field Name: ReportType
        * * Display Name: Report Type
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportRunEntityType = z.infer<typeof soundpostReportRunSchema>;

/**
 * zod schema definition for the entity Report Types
 */
export const soundpostReportTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: varchar(100)
        * * Description: Name of the report (A/R Aging, Deferred Revenue, etc.).`),
    TargetTable: z.string().describe(`
        * * Field Name: TargetTable
        * * Display Name: Target Table
        * * SQL Data Type: varchar(128)
        * * Description: Name of the table where rows for this report are stored.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostReportTypeEntityType = z.infer<typeof soundpostReportTypeSchema>;

/**
 * zod schema definition for the entity Revenue Recognition Rules
 */
export const soundpostRevenueRecognitionRuleSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProductID: z.string().describe(`
        * * Field Name: ProductID
        * * Display Name: Product ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Products (vwProducts.ID)`),
    RecognitionMethod: z.union([z.literal('fixed-date'), z.literal('immediate'), z.literal('milestone'), z.literal('percentage-completion'), z.literal('straight-line')]).describe(`
        * * Field Name: RecognitionMethod
        * * Display Name: Recognition Method
        * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * fixed-date
    *   * immediate
    *   * milestone
    *   * percentage-completion
    *   * straight-line
        * * Description: Method of revenue recognition (immediate, straight-line, fixed-date, etc.).`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)`),
    PeriodInMonths: z.number().nullable().describe(`
        * * Field Name: PeriodInMonths
        * * Display Name: Period In Months
        * * SQL Data Type: int
        * * Description: Number of months over which to recognize revenue (for straight-line).`),
    StartDateOffset: z.number().nullable().describe(`
        * * Field Name: StartDateOffset
        * * Display Name: Start Date Offset
        * * SQL Data Type: int
        * * Description: Days to offset from order date for recognition start.`),
    RecognitionDate: z.date().describe(`
        * * Field Name: RecognitionDate
        * * Display Name: Recognition Date
        * * SQL Data Type: datetimeoffset
        * * Description: Specific date when revenue should be recognized (for fixed-date).`),
    MilestoneConditions: z.string().nullable().describe(`
        * * Field Name: MilestoneConditions
        * * Display Name: Milestone Conditions
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON defining milestone conditions for milestone-based recognition.`),
    CompletionCriteria: z.string().nullable().describe(`
        * * Field Name: CompletionCriteria
        * * Display Name: Completion Criteria
        * * SQL Data Type: nvarchar(MAX)`),
    Priority: z.number().describe(`
        * * Field Name: Priority
        * * Display Name: Priority
        * * SQL Data Type: int
        * * Default Value: 100
        * * Description: Priority for rule evaluation (lower number = higher priority).`),
    Condition: z.string().nullable().describe(`
        * * Field Name: Condition
        * * Display Name: Condition
        * * SQL Data Type: nvarchar(500)`),
    EffectiveStartDate: z.date().nullable().describe(`
        * * Field Name: EffectiveStartDate
        * * Display Name: Effective Start Date
        * * SQL Data Type: datetimeoffset`),
    EffectiveEndDate: z.date().nullable().describe(`
        * * Field Name: EffectiveEndDate
        * * Display Name: Effective End Date
        * * SQL Data Type: datetimeoffset`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostRevenueRecognitionRuleEntityType = z.infer<typeof soundpostRevenueRecognitionRuleSchema>;

/**
 * zod schema definition for the entity Sync Logs
 */
export const soundpostSyncLogSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SourceSystem: z.string().describe(`
        * * Field Name: SourceSystem
        * * Display Name: Source System
        * * SQL Data Type: varchar(50)
        * * Description: Source system being synchronized.`),
    EntityType: z.string().describe(`
        * * Field Name: EntityType
        * * Display Name: Entity Type
        * * SQL Data Type: varchar(50)
        * * Description: Type of entity being synchronized.`),
    SyncType: z.string().describe(`
        * * Field Name: SyncType
        * * Display Name: Sync Type
        * * SQL Data Type: varchar(20)
        * * Default Value: Full
        * * Description: Type of synchronization (Full, Incremental).`),
    StartTime: z.date().describe(`
        * * Field Name: StartTime
        * * Display Name: Start Time
        * * SQL Data Type: datetime`),
    EndTime: z.date().nullable().describe(`
        * * Field Name: EndTime
        * * Display Name: End Time
        * * SQL Data Type: datetime`),
    LastSyncDate: z.date().nullable().describe(`
        * * Field Name: LastSyncDate
        * * Display Name: Last Sync Date
        * * SQL Data Type: datetime
        * * Description: Last successful sync date for incremental syncs.`),
    RecordsRead: z.number().describe(`
        * * Field Name: RecordsRead
        * * Display Name: Records Read
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsCreated: z.number().describe(`
        * * Field Name: RecordsCreated
        * * Display Name: Records Created
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsUpdated: z.number().describe(`
        * * Field Name: RecordsUpdated
        * * Display Name: Records Updated
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsDeleted: z.number().describe(`
        * * Field Name: RecordsDeleted
        * * Display Name: Records Deleted
        * * SQL Data Type: int
        * * Default Value: 0`),
    RecordsFailed: z.number().describe(`
        * * Field Name: RecordsFailed
        * * Display Name: Records Failed
        * * SQL Data Type: int
        * * Default Value: 0`),
    Status: z.string().describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: varchar(20)
        * * Default Value: Running
        * * Description: Sync status (Running, Completed, Failed).`),
    ErrorMessage: z.string().nullable().describe(`
        * * Field Name: ErrorMessage
        * * Display Name: Error Message
        * * SQL Data Type: nvarchar(MAX)`),
    NextSyncDate: z.date().nullable().describe(`
        * * Field Name: NextSyncDate
        * * Display Name: Next Sync Date
        * * SQL Data Type: datetime`),
    CreatedDate: z.date().describe(`
        * * Field Name: CreatedDate
        * * Display Name: Created Date
        * * SQL Data Type: datetime
        * * Default Value: getdate()`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostSyncLogEntityType = z.infer<typeof soundpostSyncLogSchema>;

/**
 * zod schema definition for the entity Trial Balances
 */
export const soundpostTrialBalanceSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key.`),
    RunID: z.string().describe(`
        * * Field Name: RunID
        * * Display Name: Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Recon Runs (vwReconRuns.ID)
        * * Description: Reconciliation run ID.`),
    CompanyIntegrationID: z.string().describe(`
        * * Field Name: CompanyIntegrationID
        * * Display Name: Company Integration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
        * * Description: Integration instance (e.g., Intacct company).`),
    AsOfDate: z.date().describe(`
        * * Field Name: AsOfDate
        * * Display Name: As Of Date
        * * SQL Data Type: datetimeoffset
        * * Description: As-of date for the balance.`),
    GLAccountID: z.string().describe(`
        * * Field Name: GLAccountID
        * * Display Name: GL Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
        * * Description: GL account reference.`),
    EndingBalanceBase: z.number().describe(`
        * * Field Name: EndingBalanceBase
        * * Display Name: Ending Balance Base
        * * SQL Data Type: decimal(18, 2)
        * * Description: Ending balance in base currency.`),
    DebitsYTDBase: z.number().nullable().describe(`
        * * Field Name: DebitsYTDBase
        * * Display Name: Debits YTD Base
        * * SQL Data Type: decimal(18, 2)
        * * Description: Sum of debits YTD (base).`),
    CreditsYTDBase: z.number().nullable().describe(`
        * * Field Name: CreditsYTDBase
        * * Display Name: Credits YTD Base
        * * SQL Data Type: decimal(18, 2)
        * * Description: Sum of credits YTD (base).`),
    CurrencyID: z.string().describe(`
        * * Field Name: CurrencyID
        * * Display Name: Currency ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
        * * Description: Currency of the reported balance.`),
    ExchangeRate: z.number().describe(`
        * * Field Name: ExchangeRate
        * * Display Name: Exchange Rate
        * * SQL Data Type: decimal(12, 6)
        * * Default Value: 1.0
        * * Description: Rate to convert to base.`),
    BookCode: z.string().describe(`
        * * Field Name: BookCode
        * * Display Name: Book Code
        * * SQL Data Type: varchar(20)
        * * Default Value: BASE
        * * Description: Accounting book code (e.g., BASE).`),
    DataOrigin: z.union([z.literal('ExternalGL'), z.literal('Manual'), z.literal('SoundPost'), z.literal('WhatIf')]).describe(`
        * * Field Name: DataOrigin
        * * Display Name: Data Origin
        * * SQL Data Type: varchar(20)
        * * Default Value: ExternalGL
    * * Value List Type: List
    * * Possible Values 
    *   * ExternalGL
    *   * Manual
    *   * SoundPost
    *   * WhatIf
        * * Description: Origin of data (ExternalGL/SoundPost/Manual/WhatIf).`),
    SourceExtractJSON: z.string().nullable().describe(`
        * * Field Name: SourceExtractJSON
        * * Display Name: Source Extract JSON
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Optional raw extract metadata.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC created timestamp.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: UTC updated timestamp.`),
    PeriodStartDate: z.date().describe(`
        * * Field Name: PeriodStartDate
        * * Display Name: Period Start Date
        * * SQL Data Type: datetimeoffset
        * * Default Value: 1900-01-01
        * * Description: Start date used when fetching balances (e.g., Jan 1 of run year).`),
    BeginningBalanceBase: z.number().nullable().describe(`
        * * Field Name: BeginningBalanceBase
        * * Display Name: Beginning Balance Base
        * * SQL Data Type: decimal(18, 2)
        * * Description: Beginning balance at PeriodStartDate in base currency (from Intacct startbalance).`),
    PeriodNetActivityBase: z.number().nullable().describe(`
        * * Field Name: PeriodNetActivityBase
        * * Display Name: Period Net Activity Base
        * * SQL Data Type: decimal(18, 2)
        * * Description: Net activity for the period [PeriodStartDate..AsOfDate] in base currency (from Intacct periodbalance).`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)`),
    CompanyIntegration: z.string().describe(`
        * * Field Name: CompanyIntegration
        * * Display Name: Company Integration
        * * SQL Data Type: nvarchar(255)`),
    GLAccount: z.string().describe(`
        * * Field Name: GLAccount
        * * Display Name: GL Account
        * * SQL Data Type: varchar(100)`),
    Currency: z.string().describe(`
        * * Field Name: Currency
        * * Display Name: Currency
        * * SQL Data Type: varchar(100)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type soundpostTrialBalanceEntityType = z.infer<typeof soundpostTrialBalanceSchema>;
 
 

/**
 * Accounting Allocation Rules - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AccountingAllocationRule
 * * Base View: vwAccountingAllocationRules
 * * @description Defines rules for conditional, partial, and multi-line accounting allocations beyond standard product-level postings.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Accounting Allocation Rules')
export class soundpostAccountingAllocationRuleEntity extends BaseEntity<soundpostAccountingAllocationRuleEntityType> {
    /**
    * Loads the Accounting Allocation Rules record from the database
    * @param ID: string - primary key value to load the Accounting Allocation Rules record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAccountingAllocationRuleEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: RuleName
    * * Display Name: Rule Name
    * * SQL Data Type: nvarchar(200)
    * * Description: Human-readable name for the allocation rule.
    */
    get RuleName(): string {
        return this.Get('RuleName');
    }
    set RuleName(value: string) {
        this.Set('RuleName', value);
    }

    /**
    * * Field Name: RuleType
    * * Display Name: Rule Type
    * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * ContraRevenue
    *   * DeferredRevenueCarveOut
    *   * DimensionOverride
    *   * LiabilityCarveOut
    *   * RevenueSplit
    * * Description: Type of allocation: RevenueSplit, DeferredRevenueCarveOut, DimensionOverride, ContraRevenue, LiabilityCarveOut.
    */
    get RuleType(): 'ContraRevenue' | 'DeferredRevenueCarveOut' | 'DimensionOverride' | 'LiabilityCarveOut' | 'RevenueSplit' {
        return this.Get('RuleType');
    }
    set RuleType(value: 'ContraRevenue' | 'DeferredRevenueCarveOut' | 'DimensionOverride' | 'LiabilityCarveOut' | 'RevenueSplit') {
        this.Set('RuleType', value);
    }

    /**
    * * Field Name: Priority
    * * Display Name: Priority
    * * SQL Data Type: int
    * * Default Value: 100
    */
    get Priority(): number {
        return this.Get('Priority');
    }
    set Priority(value: number) {
        this.Set('Priority', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: ConditionExpression
    * * Display Name: Condition Expression
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON condition tree that determines when this rule applies. Supports AND/OR operators and field-level conditions on product, order, and tag attributes.
    */
    get ConditionExpression(): string | null {
        return this.Get('ConditionExpression');
    }
    set ConditionExpression(value: string | null) {
        this.Set('ConditionExpression', value);
    }

    /**
    * * Field Name: AllocationMethod
    * * Display Name: Allocation Method
    * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * FixedAmount
    *   * Percentage
    *   * Remainder
    * * Description: How to calculate the allocation amount: Percentage (0-100), FixedAmount (dollar amount), or Remainder.
    */
    get AllocationMethod(): 'FixedAmount' | 'Percentage' | 'Remainder' {
        return this.Get('AllocationMethod');
    }
    set AllocationMethod(value: 'FixedAmount' | 'Percentage' | 'Remainder') {
        this.Set('AllocationMethod', value);
    }

    /**
    * * Field Name: AllocationValue
    * * Display Name: Allocation Value
    * * SQL Data Type: decimal(18, 4)
    * * Default Value: 0
    * * Description: The numeric value for the allocation: percentage (0-100) or fixed dollar amount, depending on AllocationMethod.
    */
    get AllocationValue(): number {
        return this.Get('AllocationValue');
    }
    set AllocationValue(value: number) {
        this.Set('AllocationValue', value);
    }

    /**
    * * Field Name: TargetGLAccountID
    * * Display Name: Target GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    * * Description: The GL account that receives the allocation amount.
    */
    get TargetGLAccountID(): string | null {
        return this.Get('TargetGLAccountID');
    }
    set TargetGLAccountID(value: string | null) {
        this.Set('TargetGLAccountID', value);
    }

    /**
    * * Field Name: TargetDimensionMappings
    * * Display Name: Target Dimension Mappings
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON object specifying dimension overrides for the allocation entries, e.g. {"Department": "DimensionID"}.
    */
    get TargetDimensionMappings(): string | null {
        return this.Get('TargetDimensionMappings');
    }
    set TargetDimensionMappings(value: string | null) {
        this.Set('TargetDimensionMappings', value);
    }

    /**
    * * Field Name: AffectsSchedule
    * * Display Name: Affects Schedule
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether this allocation should reduce the revenue recognition schedule amount.
    */
    get AffectsSchedule(): boolean {
        return this.Get('AffectsSchedule');
    }
    set AffectsSchedule(value: boolean) {
        this.Set('AffectsSchedule', value);
    }

    /**
    * * Field Name: ScheduleAdjustmentMethod
    * * Display Name: Schedule Adjustment Method
    * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * ReducePerPeriod
    *   * ReduceTotal
    * * Description: How to adjust the schedule: ReduceTotal (subtract from TotalAmount) or ReducePerPeriod (spread reduction across periods).
    */
    get ScheduleAdjustmentMethod(): 'ReducePerPeriod' | 'ReduceTotal' | null {
        return this.Get('ScheduleAdjustmentMethod');
    }
    set ScheduleAdjustmentMethod(value: 'ReducePerPeriod' | 'ReduceTotal' | null) {
        this.Set('ScheduleAdjustmentMethod', value);
    }

    /**
    * * Field Name: SourceAccountType
    * * Display Name: Source Account Type
    * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * DeferredRevenue
    *   * Revenue
    * * Description: Which account type on the standard entry this allocation draws from: Revenue or DeferredRevenue.
    */
    get SourceAccountType(): 'DeferredRevenue' | 'Revenue' {
        return this.Get('SourceAccountType');
    }
    set SourceAccountType(value: 'DeferredRevenue' | 'Revenue') {
        this.Set('SourceAccountType', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: EffectiveStartDate
    * * Display Name: Effective Start Date
    * * SQL Data Type: date
    */
    get EffectiveStartDate(): Date | null {
        return this.Get('EffectiveStartDate');
    }
    set EffectiveStartDate(value: Date | null) {
        this.Set('EffectiveStartDate', value);
    }

    /**
    * * Field Name: EffectiveEndDate
    * * Display Name: Effective End Date
    * * SQL Data Type: date
    */
    get EffectiveEndDate(): Date | null {
        return this.Get('EffectiveEndDate');
    }
    set EffectiveEndDate(value: Date | null) {
        this.Set('EffectiveEndDate', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: TargetGLAccount
    * * Display Name: Target GL Account
    * * SQL Data Type: varchar(100)
    */
    get TargetGLAccount(): string | null {
        return this.Get('TargetGLAccount');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Accounting Entries - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AccountingEntry
 * * Base View: vwAccountingEntries
 * * @description Journal entries created from business transactions.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Accounting Entries')
export class soundpostAccountingEntryEntity extends BaseEntity<soundpostAccountingEntryEntityType> {
    /**
    * Loads the Accounting Entries record from the database
    * @param ID: string - primary key value to load the Accounting Entries record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAccountingEntryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: EntryDate
    * * Display Name: Entry Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date when the accounting entry is effective.
    */
    get EntryDate(): Date {
        return this.Get('EntryDate');
    }
    set EntryDate(value: Date) {
        this.Set('EntryDate', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: SourceType
    * * Display Name: Source Type
    * * SQL Data Type: varchar(50)
    * * Description: Type of source transaction (Order, Payment, Schedule, etc.).
    */
    get SourceType(): string {
        return this.Get('SourceType');
    }
    set SourceType(value: string) {
        this.Set('SourceType', value);
    }

    /**
    * * Field Name: SourceID
    * * Display Name: Source ID
    * * SQL Data Type: varchar(100)
    */
    get SourceID(): string {
        return this.Get('SourceID');
    }
    set SourceID(value: string) {
        this.Set('SourceID', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
    */
    get CurrencyID(): string | null {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string | null) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: ExchangeRate
    * * Display Name: Exchange Rate
    * * SQL Data Type: decimal(12, 6)
    * * Default Value: 1.0
    * * Description: Exchange rate applied for foreign currency entries.
    */
    get ExchangeRate(): number | null {
        return this.Get('ExchangeRate');
    }
    set ExchangeRate(value: number | null) {
        this.Set('ExchangeRate', value);
    }

    /**
    * * Field Name: IsReversal
    * * Display Name: Is Reversal
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether this is a reversal entry.
    */
    get IsReversal(): boolean {
        return this.Get('IsReversal');
    }
    set IsReversal(value: boolean) {
        this.Set('IsReversal', value);
    }

    /**
    * * Field Name: ReversalOfEntryID
    * * Display Name: Reversal Of Entry ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Entries (vwAccountingEntries.ID)
    */
    get ReversalOfEntryID(): string | null {
        return this.Get('ReversalOfEntryID');
    }
    set ReversalOfEntryID(value: string | null) {
        this.Set('ReversalOfEntryID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: PostingGroupID
    * * Display Name: Posting Group ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Posting Groups (vwPostingGroups.ID)
    * * Description: Links the accounting entry to its posting group, if posted.
    */
    get PostingGroupID(): string | null {
        return this.Get('PostingGroupID');
    }
    set PostingGroupID(value: string | null) {
        this.Set('PostingGroupID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: EntryNumber
    * * Display Name: Entry Number
    * * SQL Data Type: int
    */
    get EntryNumber(): number {
        return this.Get('EntryNumber');
    }
    set EntryNumber(value: number) {
        this.Set('EntryNumber', value);
    }

    /**
    * * Field Name: AccountingAllocationRuleID
    * * Display Name: Accounting Allocation Rule ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Allocation Rules (vwAccountingAllocationRules.ID)
    * * Description: Links this accounting entry to the allocation rule that generated it. NULL for standard product-level entries.
    */
    get AccountingAllocationRuleID(): string | null {
        return this.Get('AccountingAllocationRuleID');
    }
    set AccountingAllocationRuleID(value: string | null) {
        this.Set('AccountingAllocationRuleID', value);
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System Record ID
    * * SQL Data Type: varchar(100)
    * * Description: External system record identifier.
    */
    get ExternalSystemRecordID(): string | null {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string | null) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: Currency
    * * Display Name: Currency
    * * SQL Data Type: varchar(100)
    */
    get Currency(): string | null {
        return this.Get('Currency');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: RootReversalOfEntryID
    * * Display Name: Root Reversal Of Entry ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootReversalOfEntryID(): string | null {
        return this.Get('RootReversalOfEntryID');
    }

    /**
    * * Field Name: ReversalOfEntryIDDepth
    * * Display Name: Reversal Of Entry ID Depth
    * * SQL Data Type: int
    */
    get ReversalOfEntryIDDepth(): number | null {
        return this.Get('ReversalOfEntryIDDepth');
    }

    /**
    * * Field Name: ReversalOfEntryIDPath
    * * Display Name: Reversal Of Entry ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get ReversalOfEntryIDPath(): string | null {
        return this.Get('ReversalOfEntryIDPath');
    }

    /**
    * * Field Name: ReversalOfEntryIDIsLeaf
    * * Display Name: Reversal Of Entry ID Is Leaf
    * * SQL Data Type: bit
    */
    get ReversalOfEntryIDIsLeaf(): boolean | null {
        return this.Get('ReversalOfEntryIDIsLeaf');
    }

    /**
    * * Field Name: ReversalOfEntryIDChildCount
    * * Display Name: Reversal Of Entry ID Child Count
    * * SQL Data Type: int
    */
    get ReversalOfEntryIDChildCount(): number | null {
        return this.Get('ReversalOfEntryIDChildCount');
    }
}


/**
 * Accounting Entry Line Dimensions - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AccountingEntryLineDimension
 * * Base View: vwAccountingEntryLineDimensions
 * * @description Analytical dimensions applied to accounting entry lines.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Accounting Entry Line Dimensions')
export class soundpostAccountingEntryLineDimensionEntity extends BaseEntity<soundpostAccountingEntryLineDimensionEntityType> {
    /**
    * Loads the Accounting Entry Line Dimensions record from the database
    * @param ID: string - primary key value to load the Accounting Entry Line Dimensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAccountingEntryLineDimensionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AccountingEntryLineID
    * * Display Name: Accounting Entry Line ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Entry Lines (vwAccountingEntryLines.ID)
    */
    get AccountingEntryLineID(): string {
        return this.Get('AccountingEntryLineID');
    }
    set AccountingEntryLineID(value: string) {
        this.Set('AccountingEntryLineID', value);
    }

    /**
    * * Field Name: DimensionTypeID
    * * Display Name: Dimension Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Dimension Types (vwDimensionTypes.ID)
    */
    get DimensionTypeID(): string {
        return this.Get('DimensionTypeID');
    }
    set DimensionTypeID(value: string) {
        this.Set('DimensionTypeID', value);
    }

    /**
    * * Field Name: DimensionID
    * * Display Name: Dimension ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Dimensions (vwDimensions.ID)
    */
    get DimensionID(): string {
        return this.Get('DimensionID');
    }
    set DimensionID(value: string) {
        this.Set('DimensionID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: DimensionType
    * * Display Name: Dimension Type
    * * SQL Data Type: varchar(50)
    */
    get DimensionType(): string {
        return this.Get('DimensionType');
    }

    /**
    * * Field Name: Dimension
    * * Display Name: Dimension
    * * SQL Data Type: varchar(100)
    */
    get Dimension(): string {
        return this.Get('Dimension');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Accounting Entry Lines - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AccountingEntryLine
 * * Base View: vwAccountingEntryLines
 * * @description Individual debit and credit lines within journal entries.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Accounting Entry Lines')
export class soundpostAccountingEntryLineEntity extends BaseEntity<soundpostAccountingEntryLineEntityType> {
    /**
    * Loads the Accounting Entry Lines record from the database
    * @param ID: string - primary key value to load the Accounting Entry Lines record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAccountingEntryLineEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AccountingEntryID
    * * Display Name: Accounting Entry ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Entries (vwAccountingEntries.ID)
    */
    get AccountingEntryID(): string {
        return this.Get('AccountingEntryID');
    }
    set AccountingEntryID(value: string) {
        this.Set('AccountingEntryID', value);
    }

    /**
    * * Field Name: LineNumber
    * * Display Name: Line Number
    * * SQL Data Type: int
    * * Default Value: 1
    * * Description: Sequential line number within the entry.
    */
    get LineNumber(): number {
        return this.Get('LineNumber');
    }
    set LineNumber(value: number) {
        this.Set('LineNumber', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: DebitAmount
    * * Display Name: Debit Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Debit amount (must be 0 if credit amount is > 0).
    */
    get DebitAmount(): number {
        return this.Get('DebitAmount');
    }
    set DebitAmount(value: number) {
        this.Set('DebitAmount', value);
    }

    /**
    * * Field Name: CreditAmount
    * * Display Name: Credit Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Credit amount (must be 0 if debit amount is > 0).
    */
    get CreditAmount(): number {
        return this.Get('CreditAmount');
    }
    set CreditAmount(value: number) {
        this.Set('CreditAmount', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
    */
    get CurrencyID(): string | null {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string | null) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: ExchangeRate
    * * Display Name: Exchange Rate
    * * SQL Data Type: decimal(12, 6)
    * * Default Value: 1.0
    */
    get ExchangeRate(): number | null {
        return this.Get('ExchangeRate');
    }
    set ExchangeRate(value: number | null) {
        this.Set('ExchangeRate', value);
    }

    /**
    * * Field Name: BaseCurrencyDebitAmount
    * * Display Name: Base Currency Debit Amount
    * * SQL Data Type: decimal(18, 2)
    * * Description: Base currency equivalent of debit amount.
    */
    get BaseCurrencyDebitAmount(): number | null {
        return this.Get('BaseCurrencyDebitAmount');
    }
    set BaseCurrencyDebitAmount(value: number | null) {
        this.Set('BaseCurrencyDebitAmount', value);
    }

    /**
    * * Field Name: BaseCurrencyCreditAmount
    * * Display Name: Base Currency Credit Amount
    * * SQL Data Type: decimal(18, 2)
    * * Description: Base currency equivalent of credit amount.
    */
    get BaseCurrencyCreditAmount(): number | null {
        return this.Get('BaseCurrencyCreditAmount');
    }
    set BaseCurrencyCreditAmount(value: number | null) {
        this.Set('BaseCurrencyCreditAmount', value);
    }

    /**
    * * Field Name: SourceLineID
    * * Display Name: Source Line ID
    * * SQL Data Type: varchar(100)
    */
    get SourceLineID(): string | null {
        return this.Get('SourceLineID');
    }
    set SourceLineID(value: string | null) {
        this.Set('SourceLineID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string {
        return this.Get('GLAccount');
    }

    /**
    * * Field Name: Currency
    * * Display Name: Currency
    * * SQL Data Type: varchar(100)
    */
    get Currency(): string | null {
        return this.Get('Currency');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Accounting Periods - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AccountingPeriod
 * * Base View: vwAccountingPeriods
 * * @description Time-based accounting periods for financial reporting.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Accounting Periods')
export class soundpostAccountingPeriodEntity extends BaseEntity<soundpostAccountingPeriodEntityType> {
    /**
    * Loads the Accounting Periods record from the database
    * @param ID: string - primary key value to load the Accounting Periods record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAccountingPeriodEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PeriodName
    * * Display Name: Period Name
    * * SQL Data Type: varchar(50)
    * * Description: Display name for the accounting period (e.g., January 2025).
    */
    get PeriodName(): string {
        return this.Get('PeriodName');
    }
    set PeriodName(value: string) {
        this.Set('PeriodName', value);
    }

    /**
    * * Field Name: StartDate
    * * Display Name: Start Date
    * * SQL Data Type: datetimeoffset
    * * Description: Start date of the accounting period.
    */
    get StartDate(): Date {
        return this.Get('StartDate');
    }
    set StartDate(value: Date) {
        this.Set('StartDate', value);
    }

    /**
    * * Field Name: EndDate
    * * Display Name: End Date
    * * SQL Data Type: datetimeoffset
    * * Description: End date of the accounting period.
    */
    get EndDate(): Date {
        return this.Get('EndDate');
    }
    set EndDate(value: Date) {
        this.Set('EndDate', value);
    }

    /**
    * * Field Name: IsClosed
    * * Display Name: Is Closed
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether this period has been closed for accounting.
    */
    get IsClosed(): boolean {
        return this.Get('IsClosed');
    }
    set IsClosed(value: boolean) {
        this.Set('IsClosed', value);
    }

    /**
    * * Field Name: ClosedDate
    * * Display Name: Closed Date
    * * SQL Data Type: datetimeoffset
    */
    get ClosedDate(): Date | null {
        return this.Get('ClosedDate');
    }
    set ClosedDate(value: Date | null) {
        this.Set('ClosedDate', value);
    }

    /**
    * * Field Name: ClosedBy
    * * Display Name: Closed By
    * * SQL Data Type: varchar(100)
    */
    get ClosedBy(): string | null {
        return this.Get('ClosedBy');
    }
    set ClosedBy(value: string | null) {
        this.Set('ClosedBy', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Accounting Schedules - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AccountingSchedule
 * * Base View: vwAccountingSchedules
 * * @description Defines revenue recognition rules for a single OrderLine with deferred revenue.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Accounting Schedules')
export class soundpostAccountingScheduleEntity extends BaseEntity<soundpostAccountingScheduleEntityType> {
    /**
    * Loads the Accounting Schedules record from the database
    * @param ID: string - primary key value to load the Accounting Schedules record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAccountingScheduleEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrderLineID
    * * Display Name: Order Line ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Order Lines (vwOrderLines.ID)
    */
    get OrderLineID(): string {
        return this.Get('OrderLineID');
    }
    set OrderLineID(value: string) {
        this.Set('OrderLineID', value);
    }

    /**
    * * Field Name: RecognitionMethod
    * * Display Name: Recognition Method
    * * SQL Data Type: varchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * Fixed Date
    *   * Manual
    *   * Straight Line
    * * Description: Recognition pattern: Fixed Date, Straight Line, or Manual.
    */
    get RecognitionMethod(): 'Fixed Date' | 'Manual' | 'Straight Line' {
        return this.Get('RecognitionMethod');
    }
    set RecognitionMethod(value: 'Fixed Date' | 'Manual' | 'Straight Line') {
        this.Set('RecognitionMethod', value);
    }

    /**
    * * Field Name: StartDate
    * * Display Name: Start Date
    * * SQL Data Type: datetimeoffset
    * * Description: Start date for revenue recognition.
    */
    get StartDate(): Date {
        return this.Get('StartDate');
    }
    set StartDate(value: Date) {
        this.Set('StartDate', value);
    }

    /**
    * * Field Name: EndDate
    * * Display Name: End Date
    * * SQL Data Type: datetimeoffset
    * * Description: End date for revenue recognition.
    */
    get EndDate(): Date {
        return this.Get('EndDate');
    }
    set EndDate(value: Date) {
        this.Set('EndDate', value);
    }

    /**
    * * Field Name: TotalAmount
    * * Display Name: Total Amount
    * * SQL Data Type: decimal(18, 2)
    * * Description: Total amount to be recognized over the schedule period.
    */
    get TotalAmount(): number {
        return this.Get('TotalAmount');
    }
    set TotalAmount(value: number) {
        this.Set('TotalAmount', value);
    }

    /**
    * * Field Name: PeriodsTotal
    * * Display Name: Periods Total
    * * SQL Data Type: int
    * * Description: Total number of recognition periods for this schedule.
    */
    get PeriodsTotal(): number | null {
        return this.Get('PeriodsTotal');
    }
    set PeriodsTotal(value: number | null) {
        this.Set('PeriodsTotal', value);
    }

    /**
    * * Field Name: PeriodsProcessed
    * * Display Name: Periods Processed
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Number of periods already processed (tracks progress).
    */
    get PeriodsProcessed(): number {
        return this.Get('PeriodsProcessed');
    }
    set PeriodsProcessed(value: number) {
        this.Set('PeriodsProcessed', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
    */
    get CurrencyID(): string | null {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string | null) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: LastProcessedDate
    * * Display Name: Last Processed Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date when this schedule was last processed.
    */
    get LastProcessedDate(): Date | null {
        return this.Get('LastProcessedDate');
    }
    set LastProcessedDate(value: Date | null) {
        this.Set('LastProcessedDate', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(20)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Cancelled
    *   * Complete
    *   * In Progress
    *   * Pending
    * * Description: Current status of the schedule: Pending, In Progress, Complete, or Cancelled.
    */
    get Status(): 'Cancelled' | 'Complete' | 'In Progress' | 'Pending' {
        return this.Get('Status');
    }
    set Status(value: 'Cancelled' | 'Complete' | 'In Progress' | 'Pending') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: RelatedScheduleID
    * * Display Name: Related Schedule ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Schedules (vwAccountingSchedules.ID)
    * * Description: Links reversal schedules to the original schedule.
    */
    get RelatedScheduleID(): string | null {
        return this.Get('RelatedScheduleID');
    }
    set RelatedScheduleID(value: string | null) {
        this.Set('RelatedScheduleID', value);
    }

    /**
    * * Field Name: IsReversal
    * * Display Name: Is Reversal
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Indicates if this schedule is a reversal of another schedule.
    */
    get IsReversal(): boolean {
        return this.Get('IsReversal');
    }
    set IsReversal(value: boolean) {
        this.Set('IsReversal', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System Record ID
    * * SQL Data Type: varchar(100)
    * * Description: System identifier for this record in the external source system.
    */
    get ExternalSystemRecordID(): string | null {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string | null) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: Currency
    * * Display Name: Currency
    * * SQL Data Type: varchar(100)
    */
    get Currency(): string | null {
        return this.Get('Currency');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: RootRelatedScheduleID
    * * Display Name: Root Related Schedule ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootRelatedScheduleID(): string | null {
        return this.Get('RootRelatedScheduleID');
    }

    /**
    * * Field Name: RelatedScheduleIDDepth
    * * Display Name: Related Schedule ID Depth
    * * SQL Data Type: int
    */
    get RelatedScheduleIDDepth(): number | null {
        return this.Get('RelatedScheduleIDDepth');
    }

    /**
    * * Field Name: RelatedScheduleIDPath
    * * Display Name: Related Schedule ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get RelatedScheduleIDPath(): string | null {
        return this.Get('RelatedScheduleIDPath');
    }

    /**
    * * Field Name: RelatedScheduleIDIsLeaf
    * * Display Name: Related Schedule ID Is Leaf
    * * SQL Data Type: bit
    */
    get RelatedScheduleIDIsLeaf(): boolean | null {
        return this.Get('RelatedScheduleIDIsLeaf');
    }

    /**
    * * Field Name: RelatedScheduleIDChildCount
    * * Display Name: Related Schedule ID Child Count
    * * SQL Data Type: int
    */
    get RelatedScheduleIDChildCount(): number | null {
        return this.Get('RelatedScheduleIDChildCount');
    }
}


/**
 * Agreement Lines - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AgreementLine
 * * Base View: vwAgreementLines
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Agreement Lines')
export class soundpostAgreementLineEntity extends BaseEntity<soundpostAgreementLineEntityType> {
    /**
    * Loads the Agreement Lines record from the database
    * @param ID: string - primary key value to load the Agreement Lines record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAgreementLineEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AgreementID
    * * Display Name: Agreement ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Agreements (vwAgreements.ID)
    */
    get AgreementID(): string {
        return this.Get('AgreementID');
    }
    set AgreementID(value: string) {
        this.Set('AgreementID', value);
    }

    /**
    * * Field Name: LineNumber
    * * Display Name: Line Number
    * * SQL Data Type: int
    * * Default Value: 1
    */
    get LineNumber(): number {
        return this.Get('LineNumber');
    }
    set LineNumber(value: number) {
        this.Set('LineNumber', value);
    }

    /**
    * * Field Name: ProductID
    * * Display Name: Product ID
    * * SQL Data Type: uniqueidentifier
    */
    get ProductID(): string {
        return this.Get('ProductID');
    }
    set ProductID(value: string) {
        this.Set('ProductID', value);
    }

    /**
    * * Field Name: AllocatedAmount
    * * Display Name: Allocated Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get AllocatedAmount(): number {
        return this.Get('AllocatedAmount');
    }
    set AllocatedAmount(value: number) {
        this.Set('AllocatedAmount', value);
    }

    /**
    * * Field Name: AdjustmentAmount
    * * Display Name: Adjustment Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get AdjustmentAmount(): number {
        return this.Get('AdjustmentAmount');
    }
    set AdjustmentAmount(value: number) {
        this.Set('AdjustmentAmount', value);
    }

    /**
    * * Field Name: Comments
    * * Display Name: Comments
    * * SQL Data Type: nvarchar(1000)
    */
    get Comments(): string | null {
        return this.Get('Comments');
    }
    set Comments(value: string | null) {
        this.Set('Comments', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Agreement Status - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AgreementStatus
 * * Base View: vwAgreementStatus
 * * @description Lookup table of agreement statuses (e.g., Draft, Active, Completed, Cancelled).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Agreement Status')
export class soundpostAgreementStatusEntity extends BaseEntity<soundpostAgreementStatusEntityType> {
    /**
    * Loads the Agreement Status record from the database
    * @param ID: string - primary key value to load the Agreement Status record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAgreementStatusEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(200)
    * * Description: Display name of the agreement status.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(1000)
    * * Description: Description of the agreement status.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: DisplayOrder
    * * Display Name: Display Order
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Sort/display order for UI lists.
    */
    get DisplayOrder(): number {
        return this.Get('DisplayOrder');
    }
    set DisplayOrder(value: number) {
        this.Set('DisplayOrder', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Indicates if this status is active/available.
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Agreement Types - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: AgreementType
 * * Base View: vwAgreementTypes
 * * @description Lookup table of agreement types (e.g., Sponsorship, Grant, Advertising Package).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Agreement Types')
export class soundpostAgreementTypeEntity extends BaseEntity<soundpostAgreementTypeEntityType> {
    /**
    * Loads the Agreement Types record from the database
    * @param ID: string - primary key value to load the Agreement Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAgreementTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(200)
    * * Description: Display name of the agreement type.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(1000)
    * * Description: Description of the agreement type.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: DisplayOrder
    * * Display Name: Display Order
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Sort/display order for UI lists.
    */
    get DisplayOrder(): number {
        return this.Get('DisplayOrder');
    }
    set DisplayOrder(value: number) {
        this.Set('DisplayOrder', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Indicates if this type is active/available.
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Agreements - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: Agreement
 * * Base View: vwAgreements
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Agreements')
export class soundpostAgreementEntity extends BaseEntity<soundpostAgreementEntityType> {
    /**
    * Loads the Agreements record from the database
    * @param ID: string - primary key value to load the Agreements record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostAgreementEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AccountName
    * * Display Name: Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get AccountName(): string {
        return this.Get('AccountName');
    }
    set AccountName(value: string) {
        this.Set('AccountName', value);
    }

    /**
    * * Field Name: ContactName
    * * Display Name: Contact Name
    * * SQL Data Type: nvarchar(255)
    */
    get ContactName(): string | null {
        return this.Get('ContactName');
    }
    set ContactName(value: string | null) {
        this.Set('ContactName', value);
    }

    /**
    * * Field Name: StartDate
    * * Display Name: Start Date
    * * SQL Data Type: datetimeoffset
    */
    get StartDate(): Date {
        return this.Get('StartDate');
    }
    set StartDate(value: Date) {
        this.Set('StartDate', value);
    }

    /**
    * * Field Name: EndDate
    * * Display Name: End Date
    * * SQL Data Type: datetimeoffset
    */
    get EndDate(): Date | null {
        return this.Get('EndDate');
    }
    set EndDate(value: Date | null) {
        this.Set('EndDate', value);
    }

    /**
    * * Field Name: AgreementTotal
    * * Display Name: Agreement Total
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get AgreementTotal(): number {
        return this.Get('AgreementTotal');
    }
    set AgreementTotal(value: number) {
        this.Set('AgreementTotal', value);
    }

    /**
    * * Field Name: TermSequence
    * * Display Name: Term Sequence
    * * SQL Data Type: int
    * * Default Value: 1
    */
    get TermSequence(): number {
        return this.Get('TermSequence');
    }
    set TermSequence(value: number) {
        this.Set('TermSequence', value);
    }

    /**
    * * Field Name: Comments
    * * Display Name: Comments
    * * SQL Data Type: nvarchar(2000)
    */
    get Comments(): string | null {
        return this.Get('Comments');
    }
    set Comments(value: string | null) {
        this.Set('Comments', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: AgreementTypeID
    * * Display Name: Agreement Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Agreement Types (vwAgreementTypes.ID)
    * * Description: Foreign key to AgreementType lookup.
    */
    get AgreementTypeID(): string | null {
        return this.Get('AgreementTypeID');
    }
    set AgreementTypeID(value: string | null) {
        this.Set('AgreementTypeID', value);
    }

    /**
    * * Field Name: AgreementStatusID
    * * Display Name: Agreement Status ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Agreement Status (vwAgreementStatus.ID)
    * * Description: Foreign key to AgreementStatus lookup.
    */
    get AgreementStatusID(): string | null {
        return this.Get('AgreementStatusID');
    }
    set AgreementStatusID(value: string | null) {
        this.Set('AgreementStatusID', value);
    }

    /**
    * * Field Name: AgreementCode
    * * Display Name: Agreement Code
    * * SQL Data Type: varchar(20)
    */
    get AgreementCode(): string | null {
        return this.Get('AgreementCode');
    }
    set AgreementCode(value: string | null) {
        this.Set('AgreementCode', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: AgreementType
    * * Display Name: Agreement Type
    * * SQL Data Type: nvarchar(200)
    */
    get AgreementType(): string | null {
        return this.Get('AgreementType');
    }

    /**
    * * Field Name: AgreementStatus
    * * Display Name: Agreement Status
    * * SQL Data Type: nvarchar(200)
    */
    get AgreementStatus(): string | null {
        return this.Get('AgreementStatus');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
}


/**
 * Company Sequences - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: CompanySequence
 * * Base View: vwCompanySequences
 * * Primary Keys: CompanyID, SequenceName
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Company Sequences')
export class soundpostCompanySequenceEntity extends BaseEntity<soundpostCompanySequenceEntityType> {
    /**
    * Loads the Company Sequences record from the database
    * @param CompanyID: string - primary key value to load the Company Sequences record.
    * @param SequenceName: string - primary key value to load the Company Sequences record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostCompanySequenceEntity
    * @method
    * @override
    */
    public async Load(CompanyID: string, SequenceName: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'CompanyID', Value: CompanyID });
        compositeKey.KeyValuePairs.push({ FieldName: 'SequenceName', Value: SequenceName });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: SequenceName
    * * Display Name: Sequence Name
    * * SQL Data Type: varchar(50)
    */
    get SequenceName(): string {
        return this.Get('SequenceName');
    }
    set SequenceName(value: string) {
        this.Set('SequenceName', value);
    }

    /**
    * * Field Name: CurrentValue
    * * Display Name: Current Value
    * * SQL Data Type: int
    */
    get CurrentValue(): number {
        return this.Get('CurrentValue');
    }
    set CurrentValue(value: number) {
        this.Set('CurrentValue', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Config Keys - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ConfigKey
 * * Base View: vwConfigKeys
 * * @description System configuration settings for the SoundPost platform.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Config Keys')
export class soundpostConfigKeyEntity extends BaseEntity<soundpostConfigKeyEntityType> {
    /**
    * Loads the Config Keys record from the database
    * @param ID: string - primary key value to load the Config Keys record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostConfigKeyEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ConfigKey
    * * Display Name: Config Key
    * * SQL Data Type: nvarchar(200)
    * * Description: Unique configuration key (e.g., woocommerce.baseUrl).
    */
    get ConfigKey(): string {
        return this.Get('ConfigKey');
    }
    set ConfigKey(value: string) {
        this.Set('ConfigKey', value);
    }

    /**
    * * Field Name: ConfigValue
    * * Display Name: Config Value
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Configuration value (stored as string, parsed based on DataType).
    */
    get ConfigValue(): string | null {
        return this.Get('ConfigValue');
    }
    set ConfigValue(value: string | null) {
        this.Set('ConfigValue', value);
    }

    /**
    * * Field Name: DataType
    * * Display Name: Data Type
    * * SQL Data Type: nvarchar(20)
    * * Default Value: string
    * * Description: Data type of the configuration value (string, number, boolean, json).
    */
    get DataType(): string {
        return this.Get('DataType');
    }
    set DataType(value: string) {
        this.Set('DataType', value);
    }

    /**
    * * Field Name: Category
    * * Display Name: Category
    * * SQL Data Type: nvarchar(50)
    * * Description: Configuration category for grouping (core, integration, woocommerce, etc.).
    */
    get Category(): string {
        return this.Get('Category');
    }
    set Category(value: string) {
        this.Set('Category', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: IsSecure
    * * Display Name: Is Secure
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether this setting contains sensitive information.
    */
    get IsSecure(): boolean {
        return this.Get('IsSecure');
    }
    set IsSecure(value: boolean) {
        this.Set('IsSecure', value);
    }

    /**
    * * Field Name: IsRequired
    * * Display Name: Is Required
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get IsRequired(): boolean {
        return this.Get('IsRequired');
    }
    set IsRequired(value: boolean) {
        this.Set('IsRequired', value);
    }

    /**
    * * Field Name: DefaultValue
    * * Display Name: Default Value
    * * SQL Data Type: nvarchar(MAX)
    */
    get DefaultValue(): string | null {
        return this.Get('DefaultValue');
    }
    set DefaultValue(value: string | null) {
        this.Set('DefaultValue', value);
    }

    /**
    * * Field Name: ValidationPattern
    * * Display Name: Validation Pattern
    * * SQL Data Type: nvarchar(500)
    */
    get ValidationPattern(): string | null {
        return this.Get('ValidationPattern');
    }
    set ValidationPattern(value: string | null) {
        this.Set('ValidationPattern', value);
    }

    /**
    * * Field Name: DisplayOrder
    * * Display Name: Display Order
    * * SQL Data Type: int
    * * Default Value: 100
    */
    get DisplayOrder(): number {
        return this.Get('DisplayOrder');
    }
    set DisplayOrder(value: number) {
        this.Set('DisplayOrder', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Links config to a specific company integration if applicable.
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Links config key to a specific company if applicable.
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Control Account Types - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ControlAccountType
 * * Base View: vwControlAccountTypes
 * * @description Control account categories for reconciliation (e.g., AR, Deferred Revenue).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Control Account Types')
export class soundpostControlAccountTypeEntity extends BaseEntity<soundpostControlAccountTypeEntityType> {
    /**
    * Loads the Control Account Types record from the database
    * @param ID: string - primary key value to load the Control Account Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostControlAccountTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Code
    * * Display Name: Code
    * * SQL Data Type: varchar(30)
    * * Description: Unique short code (e.g., AR, DEFREV).
    */
    get Code(): string {
        return this.Get('Code');
    }
    set Code(value: string) {
        this.Set('Code', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: varchar(100)
    * * Description: Display name.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    * * Description: Optional description.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Active flag.
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC created timestamp.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC updated timestamp.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Currencies - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: Currency
 * * Base View: vwCurrencies
 * * @description Reference table for currency codes used throughout the system.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Currencies')
export class soundpostCurrencyEntity extends BaseEntity<soundpostCurrencyEntityType> {
    /**
    * Loads the Currencies record from the database
    * @param ID: string - primary key value to load the Currencies record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostCurrencyEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Unique identifier for the currency.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CurrencyCode
    * * Display Name: Currency Code
    * * SQL Data Type: varchar(3)
    * * Description: ISO 4217 three-letter currency code (e.g., USD, EUR).
    */
    get CurrencyCode(): string {
        return this.Get('CurrencyCode');
    }
    set CurrencyCode(value: string) {
        this.Set('CurrencyCode', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: varchar(100)
    * * Description: Full name of the currency.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Symbol
    * * Display Name: Symbol
    * * SQL Data Type: varchar(10)
    * * Description: Currency symbol (e.g., $, €, £).
    */
    get Symbol(): string | null {
        return this.Get('Symbol');
    }
    set Symbol(value: string | null) {
        this.Set('Symbol', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Customers - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: Customer
 * * Base View: vwCustomers
 * * @description Customer master records synchronized from source systems.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Customers')
export class soundpostCustomerEntity extends BaseEntity<soundpostCustomerEntityType> {
    /**
    * Loads the Customers record from the database
    * @param ID: string - primary key value to load the Customers record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostCustomerEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System Record ID
    * * SQL Data Type: varchar(100)
    * * Description: External system customer identifier.
    */
    get ExternalSystemRecordID(): string {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: CustomerNumber
    * * Display Name: Customer Number
    * * SQL Data Type: varchar(50)
    */
    get CustomerNumber(): string | null {
        return this.Get('CustomerNumber');
    }
    set CustomerNumber(value: string | null) {
        this.Set('CustomerNumber', value);
    }

    /**
    * * Field Name: CustomerName
    * * Display Name: Customer Name
    * * SQL Data Type: nvarchar(200)
    * * Description: Display name for the customer.
    */
    get CustomerName(): string {
        return this.Get('CustomerName');
    }
    set CustomerName(value: string) {
        this.Set('CustomerName', value);
    }

    /**
    * * Field Name: Email
    * * Display Name: Email
    * * SQL Data Type: varchar(200)
    */
    get Email(): string | null {
        return this.Get('Email');
    }
    set Email(value: string | null) {
        this.Set('Email', value);
    }

    /**
    * * Field Name: Phone
    * * Display Name: Phone
    * * SQL Data Type: varchar(50)
    */
    get Phone(): string | null {
        return this.Get('Phone');
    }
    set Phone(value: string | null) {
        this.Set('Phone', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: CustomerType
    * * Display Name: Customer Type
    * * SQL Data Type: varchar(50)
    * * Description: Type of customer (Individual, Organization, etc.).
    */
    get CustomerType(): string | null {
        return this.Get('CustomerType');
    }
    set CustomerType(value: string | null) {
        this.Set('CustomerType', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
}


/**
 * Dimension Types - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: DimensionType
 * * Base View: vwDimensionTypes
 * * @description Types of analytical dimensions for financial reporting.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Dimension Types')
export class soundpostDimensionTypeEntity extends BaseEntity<soundpostDimensionTypeEntityType> {
    /**
    * Loads the Dimension Types record from the database
    * @param ID: string - primary key value to load the Dimension Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostDimensionTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: varchar(50)
    * * Description: Name of the dimension type (Department, Project, Location, etc.).
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Code
    * * Display Name: Code
    * * SQL Data Type: varchar(50)
    * * Description: Optional short code to identify the dimension type.
    */
    get Code(): string | null {
        return this.Get('Code');
    }
    set Code(value: string | null) {
        this.Set('Code', value);
    }

    /**
    * * Field Name: Prefix
    * * Display Name: Prefix
    * * SQL Data Type: varchar(5)
    * * Description: Prefix used in generating display numbers or identifiers.
    */
    get Prefix(): string | null {
        return this.Get('Prefix');
    }
    set Prefix(value: string | null) {
        this.Set('Prefix', value);
    }

    /**
    * * Field Name: SortOrder
    * * Display Name: Sort Order
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Controls the display or processing order of dimension types. Lower values sort first.
    */
    get SortOrder(): number {
        return this.Get('SortOrder');
    }
    set SortOrder(value: number) {
        this.Set('SortOrder', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
}


/**
 * Dimensions - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: Dimension
 * * Base View: vwDimensions
 * * @description Specific dimension values within each dimension type.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Dimensions')
export class soundpostDimensionEntity extends BaseEntity<soundpostDimensionEntityType> {
    /**
    * Loads the Dimensions record from the database
    * @param ID: string - primary key value to load the Dimensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostDimensionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: DimensionTypeID
    * * Display Name: Dimension Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Dimension Types (vwDimensionTypes.ID)
    */
    get DimensionTypeID(): string {
        return this.Get('DimensionTypeID');
    }
    set DimensionTypeID(value: string) {
        this.Set('DimensionTypeID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: varchar(100)
    * * Description: Dimension name or label.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Code
    * * Display Name: Code
    * * SQL Data Type: varchar(50)
    * * Description: Short code for the dimension.
    */
    get Code(): string | null {
        return this.Get('Code');
    }
    set Code(value: string | null) {
        this.Set('Code', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: DimensionType
    * * Display Name: Dimension Type
    * * SQL Data Type: varchar(50)
    */
    get DimensionType(): string {
        return this.Get('DimensionType');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
}


/**
 * Exchange Rates - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ExchangeRate
 * * Base View: vwExchangeRates
 * * @description Currency exchange rates for multi-currency support.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Exchange Rates')
export class soundpostExchangeRateEntity extends BaseEntity<soundpostExchangeRateEntityType> {
    /**
    * Loads the Exchange Rates record from the database
    * @param ID: string - primary key value to load the Exchange Rates record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostExchangeRateEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: BaseCurrencyCode
    * * Display Name: Base Currency Code
    * * SQL Data Type: varchar(3)
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.CurrencyCode)
    * * Description: Base currency in the exchange rate pair (1 unit of this currency).
    */
    get BaseCurrencyCode(): string {
        return this.Get('BaseCurrencyCode');
    }
    set BaseCurrencyCode(value: string) {
        this.Set('BaseCurrencyCode', value);
    }

    /**
    * * Field Name: QuoteCurrencyCode
    * * Display Name: Quote Currency Code
    * * SQL Data Type: varchar(3)
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.CurrencyCode)
    * * Description: Quote currency in the exchange rate pair (equals this many units).
    */
    get QuoteCurrencyCode(): string {
        return this.Get('QuoteCurrencyCode');
    }
    set QuoteCurrencyCode(value: string) {
        this.Set('QuoteCurrencyCode', value);
    }

    /**
    * * Field Name: Rate
    * * Display Name: Rate
    * * SQL Data Type: decimal(12, 6)
    * * Description: Exchange rate value.
    */
    get Rate(): number {
        return this.Get('Rate');
    }
    set Rate(value: number) {
        this.Set('Rate', value);
    }

    /**
    * * Field Name: EffectiveDate
    * * Display Name: Effective Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date when this exchange rate is effective.
    */
    get EffectiveDate(): Date {
        return this.Get('EffectiveDate');
    }
    set EffectiveDate(value: Date) {
        this.Set('EffectiveDate', value);
    }

    /**
    * * Field Name: RateType
    * * Display Name: Rate Type
    * * SQL Data Type: varchar(20)
    * * Default Value: Spot
    */
    get RateType(): string {
        return this.Get('RateType');
    }
    set RateType(value: string) {
        this.Set('RateType', value);
    }

    /**
    * * Field Name: RateSource
    * * Display Name: Rate Source
    * * SQL Data Type: varchar(50)
    */
    get RateSource(): string | null {
        return this.Get('RateSource');
    }
    set RateSource(value: string | null) {
        this.Set('RateSource', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: BaseCurrencyCode_Virtual
    * * Display Name: Base Currency Code Virtual
    * * SQL Data Type: varchar(100)
    */
    get BaseCurrencyCode_Virtual(): string {
        return this.Get('BaseCurrencyCode_Virtual');
    }

    /**
    * * Field Name: QuoteCurrencyCode_Virtual
    * * Display Name: Quote Currency Code Virtual
    * * SQL Data Type: varchar(100)
    */
    get QuoteCurrencyCode_Virtual(): string {
        return this.Get('QuoteCurrencyCode_Virtual');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
}


/**
 * GL Account Mappings - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: GLAccountMapping
 * * Base View: vwGLAccountMappings
 * * @description Rules for mapping transactions to GL accounts.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'GL Account Mappings')
export class soundpostGLAccountMappingEntity extends BaseEntity<soundpostGLAccountMappingEntityType> {
    /**
    * Loads the GL Account Mappings record from the database
    * @param ID: string - primary key value to load the GL Account Mappings record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostGLAccountMappingEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: MappingType
    * * Display Name: Mapping Type
    * * SQL Data Type: varchar(50)
    * * Description: Type of mapping (Product, Category, PaymentMethod, etc.).
    */
    get MappingType(): string {
        return this.Get('MappingType');
    }
    set MappingType(value: string) {
        this.Set('MappingType', value);
    }

    /**
    * * Field Name: AccountType
    * * Display Name: Account Type
    * * SQL Data Type: varchar(100)
    * * Description: Account type (Revenue, DeferredRevenue, Receivable, etc.).
    */
    get AccountType(): string {
        return this.Get('AccountType');
    }
    set AccountType(value: string) {
        this.Set('AccountType', value);
    }

    /**
    * * Field Name: CategoryMatch
    * * Display Name: Category Match
    * * SQL Data Type: varchar(200)
    */
    get CategoryMatch(): string | null {
        return this.Get('CategoryMatch');
    }
    set CategoryMatch(value: string | null) {
        this.Set('CategoryMatch', value);
    }

    /**
    * * Field Name: ProductTypeMatch
    * * Display Name: Product Type Match
    * * SQL Data Type: varchar(100)
    */
    get ProductTypeMatch(): string | null {
        return this.Get('ProductTypeMatch');
    }
    set ProductTypeMatch(value: string | null) {
        this.Set('ProductTypeMatch', value);
    }

    /**
    * * Field Name: EventTypeMatch
    * * Display Name: Event Type Match
    * * SQL Data Type: varchar(100)
    */
    get EventTypeMatch(): string | null {
        return this.Get('EventTypeMatch');
    }
    set EventTypeMatch(value: string | null) {
        this.Set('EventTypeMatch', value);
    }

    /**
    * * Field Name: PaymentMethodMatch
    * * Display Name: Payment Method Match
    * * SQL Data Type: varchar(100)
    */
    get PaymentMethodMatch(): string | null {
        return this.Get('PaymentMethodMatch');
    }
    set PaymentMethodMatch(value: string | null) {
        this.Set('PaymentMethodMatch', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    */
    get GLAccountID(): string | null {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string | null) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: DimensionMappings
    * * Display Name: Dimension Mappings
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON structure for dimension mappings.
    */
    get DimensionMappings(): string | null {
        return this.Get('DimensionMappings');
    }
    set DimensionMappings(value: string | null) {
        this.Set('DimensionMappings', value);
    }

    /**
    * * Field Name: Priority
    * * Display Name: Priority
    * * SQL Data Type: int
    * * Default Value: 100
    */
    get Priority(): number {
        return this.Get('Priority');
    }
    set Priority(value: number) {
        this.Set('Priority', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: SourceValue
    * * Display Name: Source Value
    * * SQL Data Type: varchar(200)
    * * Description: The specific value to match for this mapping type (e.g., "CreditCard", "Member", "Webinar").
    */
    get SourceValue(): string | null {
        return this.Get('SourceValue');
    }
    set SourceValue(value: string | null) {
        this.Set('SourceValue', value);
    }

    /**
    * * Field Name: SourceFieldName
    * * Display Name: Source Field Name
    * * SQL Data Type: varchar(100)
    * * Description: The metadata field name to read from the source entity (e.g., "_sage_gl", "_deferred_revenue_required").
    */
    get SourceFieldName(): string | null {
        return this.Get('SourceFieldName');
    }
    set SourceFieldName(value: string | null) {
        this.Set('SourceFieldName', value);
    }

    /**
    * * Field Name: EntityType
    * * Display Name: Entity Type
    * * SQL Data Type: varchar(50)
    * * Description: The entity type this mapping applies to (Product, Order, Customer, Payment).
    */
    get EntityType(): string | null {
        return this.Get('EntityType');
    }
    set EntityType(value: string | null) {
        this.Set('EntityType', value);
    }

    /**
    * * Field Name: ConditionExpression
    * * Display Name: Condition Expression
    * * SQL Data Type: varchar(500)
    * * Description: Optional conditional expression to evaluate (e.g., "value='yes'", "state='VA'").
    */
    get ConditionExpression(): string | null {
        return this.Get('ConditionExpression');
    }
    set ConditionExpression(value: string | null) {
        this.Set('ConditionExpression', value);
    }

    /**
    * * Field Name: SegmentDelimiter
    * * Display Name: Segment Delimiter
    * * SQL Data Type: varchar(10)
    * * Description: Delimiter used to parse GL account strings (e.g., "-" for "1234-N456-P789").
    */
    get SegmentDelimiter(): string | null {
        return this.Get('SegmentDelimiter');
    }
    set SegmentDelimiter(value: string | null) {
        this.Set('SegmentDelimiter', value);
    }

    /**
    * * Field Name: AutoCreateGLAccount
    * * Display Name: Auto Create GL Account
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether to automatically create GL accounts if they don't exist.
    */
    get AutoCreateGLAccount(): boolean {
        return this.Get('AutoCreateGLAccount');
    }
    set AutoCreateGLAccount(value: boolean) {
        this.Set('AutoCreateGLAccount', value);
    }

    /**
    * * Field Name: AutoCreateDimensions
    * * Display Name: Auto Create Dimensions
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether to automatically create dimension types if they don't exist.
    */
    get AutoCreateDimensions(): boolean {
        return this.Get('AutoCreateDimensions');
    }
    set AutoCreateDimensions(value: boolean) {
        this.Set('AutoCreateDimensions', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Reference to the company integration this mapping belongs to (optional, null = global).
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(255)
    * * Description: Optional short description of this GL account mapping rule.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string | null {
        return this.Get('GLAccount');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * GL Account Types - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: GLAccountType
 * * Base View: vwGLAccountTypes
 * * @description Categories of GL accounts with hierarchical support.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'GL Account Types')
export class soundpostGLAccountTypeEntity extends BaseEntity<soundpostGLAccountTypeEntityType> {
    /**
    * Loads the GL Account Types record from the database
    * @param ID: string - primary key value to load the GL Account Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostGLAccountTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ParentID
    * * Display Name: Parent ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Account Types (vwGLAccountTypes.ID)
    * * Description: Parent GL Account Type for hierarchical classification.
    */
    get ParentID(): string | null {
        return this.Get('ParentID');
    }
    set ParentID(value: string | null) {
        this.Set('ParentID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: varchar(100)
    * * Description: Name of the GL account type (Asset, Liability, Revenue, etc.).
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: SortOrder
    * * Display Name: Sort Order
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Controls the display or processing order of GL account types. Lower values sort first.
    */
    get SortOrder(): number {
        return this.Get('SortOrder');
    }
    set SortOrder(value: number) {
        this.Set('SortOrder', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: Parent
    * * Display Name: Parent
    * * SQL Data Type: varchar(100)
    */
    get Parent(): string | null {
        return this.Get('Parent');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: RootParentID
    * * Display Name: Root Parent ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootParentID(): string | null {
        return this.Get('RootParentID');
    }

    /**
    * * Field Name: ParentIDDepth
    * * Display Name: Parent ID Depth
    * * SQL Data Type: int
    */
    get ParentIDDepth(): number | null {
        return this.Get('ParentIDDepth');
    }

    /**
    * * Field Name: ParentIDPath
    * * Display Name: Parent ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get ParentIDPath(): string | null {
        return this.Get('ParentIDPath');
    }

    /**
    * * Field Name: ParentIDIsLeaf
    * * Display Name: Parent ID Is Leaf
    * * SQL Data Type: bit
    */
    get ParentIDIsLeaf(): boolean | null {
        return this.Get('ParentIDIsLeaf');
    }

    /**
    * * Field Name: ParentIDChildCount
    * * Display Name: Parent ID Child Count
    * * SQL Data Type: int
    */
    get ParentIDChildCount(): number | null {
        return this.Get('ParentIDChildCount');
    }
}


/**
 * GL Accounts - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: GLAccount
 * * Base View: vwGLAccounts
 * * @description Chart of accounts master list.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'GL Accounts')
export class soundpostGLAccountEntity extends BaseEntity<soundpostGLAccountEntityType> {
    /**
    * Loads the GL Accounts record from the database
    * @param ID: string - primary key value to load the GL Accounts record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostGLAccountEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AccountNumber
    * * Display Name: Account Number
    * * SQL Data Type: varchar(50)
    * * Description: Chart of account number used in accounting entries.
    */
    get AccountNumber(): string {
        return this.Get('AccountNumber');
    }
    set AccountNumber(value: string) {
        this.Set('AccountNumber', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: varchar(100)
    * * Description: Name or label of the GL account.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: GLAccountTypeID
    * * Display Name: GL Account Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Account Types (vwGLAccountTypes.ID)
    */
    get GLAccountTypeID(): string {
        return this.Get('GLAccountTypeID');
    }
    set GLAccountTypeID(value: string) {
        this.Set('GLAccountTypeID', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: ControlAccountTypeID
    * * Display Name: Control Account Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Control Account Types (vwControlAccountTypes.ID)
    * * Description: Control account classification for reconciliation (e.g., AR, Deferred Revenue).
    */
    get ControlAccountTypeID(): string | null {
        return this.Get('ControlAccountTypeID');
    }
    set ControlAccountTypeID(value: string | null) {
        this.Set('ControlAccountTypeID', value);
    }

    /**
    * * Field Name: ReconFlowCategoryID
    * * Display Name: Recon Flow Category ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Recon Flow Categories (vwReconFlowCategories.ID)
    * * Description: Reconciliation flow category for GL rollups.
    */
    get ReconFlowCategoryID(): string | null {
        return this.Get('ReconFlowCategoryID');
    }
    set ReconFlowCategoryID(value: string | null) {
        this.Set('ReconFlowCategoryID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: ReconClassification
    * * Display Name: Recon Classification
    * * SQL Data Type: varchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Owned
    *   * Reclassified
    *   * Shared
    * * Description: Reconciliation policy for this account: Owned (SoundPost is the only feed; difference is scored against ReconTolerance), Shared (other feeds post here; difference reported, never failed), Reclassified (accounting sweeps SoundPost postings elsewhere; informational). NULL = not reconciled.
    */
    get ReconClassification(): 'Owned' | 'Reclassified' | 'Shared' | null {
        return this.Get('ReconClassification');
    }
    set ReconClassification(value: 'Owned' | 'Reclassified' | 'Shared' | null) {
        this.Set('ReconClassification', value);
    }

    /**
    * * Field Name: ReconTolerance
    * * Display Name: Recon Tolerance
    * * SQL Data Type: decimal(18, 2)
    * * Description: Absolute tolerance (base currency) within which an Owned account is considered tied out. NULL = company default (1.00).
    */
    get ReconTolerance(): number | null {
        return this.Get('ReconTolerance');
    }
    set ReconTolerance(value: number | null) {
        this.Set('ReconTolerance', value);
    }

    /**
    * * Field Name: GLAccountType
    * * Display Name: GL Account Type
    * * SQL Data Type: varchar(100)
    */
    get GLAccountType(): string {
        return this.Get('GLAccountType');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: ControlAccountType
    * * Display Name: Control Account Type
    * * SQL Data Type: varchar(100)
    */
    get ControlAccountType(): string | null {
        return this.Get('ControlAccountType');
    }

    /**
    * * Field Name: ReconFlowCategory
    * * Display Name: Recon Flow Category
    * * SQL Data Type: varchar(100)
    */
    get ReconFlowCategory(): string | null {
        return this.Get('ReconFlowCategory');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Orchestration Run Steps - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: OrchestrationRunStep
 * * Base View: vwOrchestrationRunSteps
 * * @description Tracks each step within a pipeline run. Steps execute sequentially and map to MJ Actions.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Orchestration Run Steps')
export class soundpostOrchestrationRunStepEntity extends BaseEntity<soundpostOrchestrationRunStepEntityType> {
    /**
    * Loads the Orchestration Run Steps record from the database
    * @param ID: string - primary key value to load the Orchestration Run Steps record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostOrchestrationRunStepEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrchestrationRunID
    * * Display Name: Orchestration Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Orchestration Runs (vwOrchestrationRuns.ID)
    * * Description: Parent orchestration run.
    */
    get OrchestrationRunID(): string {
        return this.Get('OrchestrationRunID');
    }
    set OrchestrationRunID(value: string) {
        this.Set('OrchestrationRunID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Company this step belongs to. Denormalized from OrchestrationRun for multi-tenancy filtering.
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: StepNumber
    * * Display Name: Step Number
    * * SQL Data Type: int
    * * Description: Execution order within the pipeline (1-based). Assigned dynamically based on active integrations and capabilities.
    */
    get StepNumber(): number {
        return this.Get('StepNumber');
    }
    set StepNumber(value: number) {
        this.Set('StepNumber', value);
    }

    /**
    * * Field Name: StepName
    * * Display Name: Step Name
    * * SQL Data Type: nvarchar(200)
    * * Description: Human-readable step name including integration context (e.g. Sync WooCommerce Orders).
    */
    get StepName(): string {
        return this.Get('StepName');
    }
    set StepName(value: string) {
        this.Set('StepName', value);
    }

    /**
    * * Field Name: ActionName
    * * Display Name: Action Name
    * * SQL Data Type: nvarchar(425)
    * * Description: MJ Action name executed for this step (e.g. SoundPost: Sync Orders).
    */
    get ActionName(): string | null {
        return this.Get('ActionName');
    }
    set ActionName(value: string | null) {
        this.Set('ActionName', value);
    }

    /**
    * * Field Name: ActionExecutionLogID
    * * Display Name: Action Execution Log ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Action Execution Logs (vwActionExecutionLogs.ID)
    * * Description: Links to the MJ ActionExecutionLog record for this step invocation.
    */
    get ActionExecutionLogID(): string | null {
        return this.Get('ActionExecutionLogID');
    }
    set ActionExecutionLogID(value: string | null) {
        this.Set('ActionExecutionLogID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(20)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Completed
    *   * Failed
    *   * Pending
    *   * Running
    *   * Skipped
    * * Description: Step execution status: Pending, Running, Completed, Failed, or Skipped.
    */
    get Status(): 'Completed' | 'Failed' | 'Pending' | 'Running' | 'Skipped' {
        return this.Get('Status');
    }
    set Status(value: 'Completed' | 'Failed' | 'Pending' | 'Running' | 'Skipped') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: StartedAt
    * * Display Name: Started At
    * * SQL Data Type: datetimeoffset
    */
    get StartedAt(): Date | null {
        return this.Get('StartedAt');
    }
    set StartedAt(value: Date | null) {
        this.Set('StartedAt', value);
    }

    /**
    * * Field Name: CompletedAt
    * * Display Name: Completed At
    * * SQL Data Type: datetimeoffset
    */
    get CompletedAt(): Date | null {
        return this.Get('CompletedAt');
    }
    set CompletedAt(value: Date | null) {
        this.Set('CompletedAt', value);
    }

    /**
    * * Field Name: DurationMs
    * * Display Name: Duration Ms
    * * SQL Data Type: int
    */
    get DurationMs(): number | null {
        return this.Get('DurationMs');
    }
    set DurationMs(value: number | null) {
        this.Set('DurationMs', value);
    }

    /**
    * * Field Name: RecordsProcessed
    * * Display Name: Records Processed
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsProcessed(): number {
        return this.Get('RecordsProcessed');
    }
    set RecordsProcessed(value: number) {
        this.Set('RecordsProcessed', value);
    }

    /**
    * * Field Name: RecordsSucceeded
    * * Display Name: Records Succeeded
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsSucceeded(): number {
        return this.Get('RecordsSucceeded');
    }
    set RecordsSucceeded(value: number) {
        this.Set('RecordsSucceeded', value);
    }

    /**
    * * Field Name: RecordsFailed
    * * Display Name: Records Failed
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsFailed(): number {
        return this.Get('RecordsFailed');
    }
    set RecordsFailed(value: number) {
        this.Set('RecordsFailed', value);
    }

    /**
    * * Field Name: ErrorMessage
    * * Display Name: Error Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get ErrorMessage(): string | null {
        return this.Get('ErrorMessage');
    }
    set ErrorMessage(value: string | null) {
        this.Set('ErrorMessage', value);
    }

    /**
    * * Field Name: ResultDetailJson
    * * Display Name: Result Detail Json
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Step-specific output data serialized as JSON.
    */
    get ResultDetailJson(): string | null {
        return this.Get('ResultDetailJson');
    }
    set ResultDetailJson(value: string | null) {
        this.Set('ResultDetailJson', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string {
        return this.Get('Company');
    }

    /**
    * * Field Name: ActionExecutionLog
    * * Display Name: Action Execution Log
    * * SQL Data Type: nvarchar(425)
    */
    get ActionExecutionLog(): string | null {
        return this.Get('ActionExecutionLog');
    }
}


/**
 * Orchestration Runs - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: OrchestrationRun
 * * Base View: vwOrchestrationRuns
 * * @description Tracks pipeline execution for the SoundPost orchestration system. Each record represents one run of a named pipeline (NightlySync, ManualPosting, ResyncOrder, MonthlyClose, etc.).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Orchestration Runs')
export class soundpostOrchestrationRunEntity extends BaseEntity<soundpostOrchestrationRunEntityType> {
    /**
    * Loads the Orchestration Runs record from the database
    * @param ID: string - primary key value to load the Orchestration Runs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostOrchestrationRunEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ScheduledJobRunID
    * * Display Name: Scheduled Job Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Scheduled Job Runs (vwScheduledJobRuns.ID)
    * * Description: Links to MJ ScheduledJobRun for scheduled executions. NULL for ad-hoc runs triggered via REST endpoint.
    */
    get ScheduledJobRunID(): string | null {
        return this.Get('ScheduledJobRunID');
    }
    set ScheduledJobRunID(value: string | null) {
        this.Set('ScheduledJobRunID', value);
    }

    /**
    * * Field Name: PipelineName
    * * Display Name: Pipeline Name
    * * SQL Data Type: varchar(50)
    * * Description: Pipeline identifier: NightlySync, ManualPosting, ResyncOrder, MonthlyClose, RefreshReports, GenerateSnapshot.
    */
    get PipelineName(): string {
        return this.Get('PipelineName');
    }
    set PipelineName(value: string) {
        this.Set('PipelineName', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(20)
    * * Default Value: Running
    * * Value List Type: List
    * * Possible Values 
    *   * Cancelled
    *   * Completed
    *   * Failed
    *   * Queued
    *   * Running
    *   * TimedOut
    * * Description: Current execution status: Queued, Running, Completed, Failed, TimedOut, or Cancelled.
    */
    get Status(): 'Cancelled' | 'Completed' | 'Failed' | 'Queued' | 'Running' | 'TimedOut' {
        return this.Get('Status');
    }
    set Status(value: 'Cancelled' | 'Completed' | 'Failed' | 'Queued' | 'Running' | 'TimedOut') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: ParametersJson
    * * Display Name: Parameters Json
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Run-specific input parameters serialized as JSON.
    */
    get ParametersJson(): string | null {
        return this.Get('ParametersJson');
    }
    set ParametersJson(value: string | null) {
        this.Set('ParametersJson', value);
    }

    /**
    * * Field Name: RequestedByID
    * * Display Name: Requested By ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Employees (vwEmployees.ID)
    * * Description: Employee who requested the run. For scheduled executions, this is the service account employee. Set server-side, not from client input.
    */
    get RequestedByID(): string {
        return this.Get('RequestedByID');
    }
    set RequestedByID(value: string) {
        this.Set('RequestedByID', value);
    }

    /**
    * * Field Name: StartedAt
    * * Display Name: Started At
    * * SQL Data Type: datetimeoffset
    */
    get StartedAt(): Date | null {
        return this.Get('StartedAt');
    }
    set StartedAt(value: Date | null) {
        this.Set('StartedAt', value);
    }

    /**
    * * Field Name: CompletedAt
    * * Display Name: Completed At
    * * SQL Data Type: datetimeoffset
    */
    get CompletedAt(): Date | null {
        return this.Get('CompletedAt');
    }
    set CompletedAt(value: Date | null) {
        this.Set('CompletedAt', value);
    }

    /**
    * * Field Name: DurationMs
    * * Display Name: Duration Ms
    * * SQL Data Type: int
    * * Description: Wall-clock duration in milliseconds, computed on completion.
    */
    get DurationMs(): number | null {
        return this.Get('DurationMs');
    }
    set DurationMs(value: number | null) {
        this.Set('DurationMs', value);
    }

    /**
    * * Field Name: TotalSteps
    * * Display Name: Total Steps
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get TotalSteps(): number {
        return this.Get('TotalSteps');
    }
    set TotalSteps(value: number) {
        this.Set('TotalSteps', value);
    }

    /**
    * * Field Name: CompletedSteps
    * * Display Name: Completed Steps
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get CompletedSteps(): number {
        return this.Get('CompletedSteps');
    }
    set CompletedSteps(value: number) {
        this.Set('CompletedSteps', value);
    }

    /**
    * * Field Name: RecordsProcessed
    * * Display Name: Records Processed
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Aggregate records processed across all steps.
    */
    get RecordsProcessed(): number {
        return this.Get('RecordsProcessed');
    }
    set RecordsProcessed(value: number) {
        this.Set('RecordsProcessed', value);
    }

    /**
    * * Field Name: RecordsSucceeded
    * * Display Name: Records Succeeded
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsSucceeded(): number {
        return this.Get('RecordsSucceeded');
    }
    set RecordsSucceeded(value: number) {
        this.Set('RecordsSucceeded', value);
    }

    /**
    * * Field Name: RecordsFailed
    * * Display Name: Records Failed
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsFailed(): number {
        return this.Get('RecordsFailed');
    }
    set RecordsFailed(value: number) {
        this.Set('RecordsFailed', value);
    }

    /**
    * * Field Name: ErrorMessage
    * * Display Name: Error Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get ErrorMessage(): string | null {
        return this.Get('ErrorMessage');
    }
    set ErrorMessage(value: string | null) {
        this.Set('ErrorMessage', value);
    }

    /**
    * * Field Name: ResultSummaryJson
    * * Display Name: Result Summary Json
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Pipeline-specific result data serialized as JSON.
    */
    get ResultSummaryJson(): string | null {
        return this.Get('ResultSummaryJson');
    }
    set ResultSummaryJson(value: string | null) {
        this.Set('ResultSummaryJson', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Company this orchestration run belongs to. Required for multi-tenancy.
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: ScheduledJobRun
    * * Display Name: Scheduled Job Run
    * * SQL Data Type: nvarchar(200)
    */
    get ScheduledJobRun(): string | null {
        return this.Get('ScheduledJobRun');
    }

    /**
    * * Field Name: RequestedBy
    * * Display Name: Requested By
    * * SQL Data Type: nvarchar(81)
    */
    get RequestedBy(): string | null {
        return this.Get('RequestedBy');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string {
        return this.Get('Company');
    }
}


/**
 * Order Lines - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: OrderLine
 * * Base View: vwOrderLines
 * * @description Line items within sales orders.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Order Lines')
export class soundpostOrderLineEntity extends BaseEntity<soundpostOrderLineEntityType> {
    /**
    * Loads the Order Lines record from the database
    * @param ID: string - primary key value to load the Order Lines record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostOrderLineEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrderID
    * * Display Name: Order ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Orders (vwOrders.ID)
    */
    get OrderID(): string {
        return this.Get('OrderID');
    }
    set OrderID(value: string) {
        this.Set('OrderID', value);
    }

    /**
    * * Field Name: LineNumber
    * * Display Name: Line Number
    * * SQL Data Type: int
    * * Description: Sequential line number within the order.
    */
    get LineNumber(): number {
        return this.Get('LineNumber');
    }
    set LineNumber(value: number) {
        this.Set('LineNumber', value);
    }

    /**
    * * Field Name: ProductID
    * * Display Name: Product ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Products (vwProducts.ID)
    */
    get ProductID(): string {
        return this.Get('ProductID');
    }
    set ProductID(value: string) {
        this.Set('ProductID', value);
    }

    /**
    * * Field Name: ProductName
    * * Display Name: Product Name
    * * SQL Data Type: nvarchar(500)
    * * Description: Product name at time of order (for historical reference).
    */
    get ProductName(): string | null {
        return this.Get('ProductName');
    }
    set ProductName(value: string | null) {
        this.Set('ProductName', value);
    }

    /**
    * * Field Name: Quantity
    * * Display Name: Quantity
    * * SQL Data Type: decimal(18, 4)
    * * Default Value: 1
    * * Description: Quantity ordered.
    */
    get Quantity(): number {
        return this.Get('Quantity');
    }
    set Quantity(value: number) {
        this.Set('Quantity', value);
    }

    /**
    * * Field Name: UnitPrice
    * * Display Name: Unit Price
    * * SQL Data Type: decimal(18, 4)
    * * Description: Unit price at time of order.
    */
    get UnitPrice(): number {
        return this.Get('UnitPrice');
    }
    set UnitPrice(value: number) {
        this.Set('UnitPrice', value);
    }

    /**
    * * Field Name: LineAmount
    * * Display Name: Line Amount
    * * SQL Data Type: decimal(18, 2)
    * * Description: Total line amount (quantity × unit price - discounts + tax).
    */
    get LineAmount(): number {
        return this.Get('LineAmount');
    }
    set LineAmount(value: number) {
        this.Set('LineAmount', value);
    }

    /**
    * * Field Name: TaxAmount
    * * Display Name: Tax Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get TaxAmount(): number | null {
        return this.Get('TaxAmount');
    }
    set TaxAmount(value: number | null) {
        this.Set('TaxAmount', value);
    }

    /**
    * * Field Name: DiscountAmount
    * * Display Name: Discount Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get DiscountAmount(): number | null {
        return this.Get('DiscountAmount');
    }
    set DiscountAmount(value: number | null) {
        this.Set('DiscountAmount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System Record ID
    * * SQL Data Type: nvarchar(100)
    * * Description: External system identifier for the order line item (e.g., WooCommerce line item ID)
    */
    get ExternalSystemRecordID(): string | null {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string | null) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: RecognitionStartDate
    * * Display Name: Recognition Start Date
    * * SQL Data Type: datetimeoffset
    * * Description: Start date for revenue recognition (NULL for immediate recognition).
    */
    get RecognitionStartDate(): Date | null {
        return this.Get('RecognitionStartDate');
    }
    set RecognitionStartDate(value: Date | null) {
        this.Set('RecognitionStartDate', value);
    }

    /**
    * * Field Name: RecognitionEndDate
    * * Display Name: Recognition End Date
    * * SQL Data Type: datetimeoffset
    * * Description: End date for revenue recognition period (NULL for immediate recognition).
    */
    get RecognitionEndDate(): Date | null {
        return this.Get('RecognitionEndDate');
    }
    set RecognitionEndDate(value: Date | null) {
        this.Set('RecognitionEndDate', value);
    }

    /**
    * * Field Name: RecipientName
    * * Display Name: Recipient Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Full name of the recipient for this order line item.
    */
    get RecipientName(): string | null {
        return this.Get('RecipientName');
    }
    set RecipientName(value: string | null) {
        this.Set('RecipientName', value);
    }

    /**
    * * Field Name: RecipientCompany
    * * Display Name: Recipient Company
    * * SQL Data Type: nvarchar(255)
    * * Description: Name of the company associated with the recipient for this order line item.
    */
    get RecipientCompany(): string | null {
        return this.Get('RecipientCompany');
    }
    set RecipientCompany(value: string | null) {
        this.Set('RecipientCompany', value);
    }

    /**
    * * Field Name: ReconFlowCategoryID
    * * Display Name: Recon Flow Category ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Recon Flow Categories (vwReconFlowCategories.ID)
    * * Description: Reconciliation flow category snapshot frozen at time of order line creation.
    */
    get ReconFlowCategoryID(): string | null {
        return this.Get('ReconFlowCategoryID');
    }
    set ReconFlowCategoryID(value: string | null) {
        this.Set('ReconFlowCategoryID', value);
    }

    /**
    * * Field Name: ReversalOfLineID
    * * Display Name: Reversal Of Line ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Order Lines (vwOrderLines.ID)
    * * Description: Links to the original order line being reversed.
    */
    get ReversalOfLineID(): string | null {
        return this.Get('ReversalOfLineID');
    }
    set ReversalOfLineID(value: string | null) {
        this.Set('ReversalOfLineID', value);
    }

    /**
    * * Field Name: ReversalReason
    * * Display Name: Reversal Reason
    * * SQL Data Type: nvarchar(500)
    * * Description: Reason for the reversal (e.g., Product cancelled, Quantity adjusted).
    */
    get ReversalReason(): string | null {
        return this.Get('ReversalReason');
    }
    set ReversalReason(value: string | null) {
        this.Set('ReversalReason', value);
    }

    /**
    * * Field Name: IsReversal
    * * Display Name: Is Reversal
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Indicates if this line is a reversal of another line.
    */
    get IsReversal(): boolean {
        return this.Get('IsReversal');
    }
    set IsReversal(value: boolean) {
        this.Set('IsReversal', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: ReconFlowCategory
    * * Display Name: Recon Flow Category
    * * SQL Data Type: varchar(100)
    */
    get ReconFlowCategory(): string | null {
        return this.Get('ReconFlowCategory');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: RootReversalOfLineID
    * * Display Name: Root Reversal Of Line ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootReversalOfLineID(): string | null {
        return this.Get('RootReversalOfLineID');
    }

    /**
    * * Field Name: ReversalOfLineIDDepth
    * * Display Name: Reversal Of Line ID Depth
    * * SQL Data Type: int
    */
    get ReversalOfLineIDDepth(): number | null {
        return this.Get('ReversalOfLineIDDepth');
    }

    /**
    * * Field Name: ReversalOfLineIDPath
    * * Display Name: Reversal Of Line ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get ReversalOfLineIDPath(): string | null {
        return this.Get('ReversalOfLineIDPath');
    }

    /**
    * * Field Name: ReversalOfLineIDIsLeaf
    * * Display Name: Reversal Of Line ID Is Leaf
    * * SQL Data Type: bit
    */
    get ReversalOfLineIDIsLeaf(): boolean | null {
        return this.Get('ReversalOfLineIDIsLeaf');
    }

    /**
    * * Field Name: ReversalOfLineIDChildCount
    * * Display Name: Reversal Of Line ID Child Count
    * * SQL Data Type: int
    */
    get ReversalOfLineIDChildCount(): number | null {
        return this.Get('ReversalOfLineIDChildCount');
    }
}


/**
 * Orders - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: Order
 * * Base View: vwOrders
 * * @description Sales orders synchronized from source systems.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Orders')
export class soundpostOrderEntity extends BaseEntity<soundpostOrderEntityType> {

  /**
   * Retrieves all descendant records in the PreviousVersionID hierarchy under this record using a single RunView query.
   * @param maxDepth Optional maximum relative depth to retrieve.
   * @returns Array of descendant entity instances ordered by hierarchy depth.
   */
  public async GetPreviousVersionIDDescendants<T extends BaseEntity = this>(maxDepth?: number): Promise<T[]> {
    return this.GetDescendants<T>({ parentFieldName: 'PreviousVersionID', maxDepth });
  }

  /**
   * Retrieves all ancestor records in the PreviousVersionID hierarchy from the top-level root down to this record using a single RunView query.
   * @returns Array of ancestor entity instances ordered from root down to parent.
   */
  public async GetPreviousVersionIDAncestors<T extends BaseEntity = this>(): Promise<T[]> {
    return this.GetAncestors<T>('PreviousVersionID');
  }

  /**
   * Retrieves all direct child records in the PreviousVersionID hierarchy of this record using a single RunView query.
   * @returns Array of direct child entity instances.
   */
  public async GetPreviousVersionIDChildren<T extends BaseEntity = this>(): Promise<T[]> {
    return this.GetChildren<T>('PreviousVersionID');
  }

  /**
   * Retrieves all descendant records in the OriginalOrderID hierarchy under this record using a single RunView query.
   * @param maxDepth Optional maximum relative depth to retrieve.
   * @returns Array of descendant entity instances ordered by hierarchy depth.
   */
  public async GetOriginalOrderIDDescendants<T extends BaseEntity = this>(maxDepth?: number): Promise<T[]> {
    return this.GetDescendants<T>({ parentFieldName: 'OriginalOrderID', maxDepth });
  }

  /**
   * Retrieves all ancestor records in the OriginalOrderID hierarchy from the top-level root down to this record using a single RunView query.
   * @returns Array of ancestor entity instances ordered from root down to parent.
   */
  public async GetOriginalOrderIDAncestors<T extends BaseEntity = this>(): Promise<T[]> {
    return this.GetAncestors<T>('OriginalOrderID');
  }

  /**
   * Retrieves all direct child records in the OriginalOrderID hierarchy of this record using a single RunView query.
   * @returns Array of direct child entity instances.
   */
  public async GetOriginalOrderIDChildren<T extends BaseEntity = this>(): Promise<T[]> {
    return this.GetChildren<T>('OriginalOrderID');
  }
    /**
    * Loads the Orders record from the database
    * @param ID: string - primary key value to load the Orders record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostOrderEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System Record ID
    * * SQL Data Type: varchar(100)
    * * Description: External system order identifier.
    */
    get ExternalSystemRecordID(): string | null {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string | null) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: OrderNumber
    * * Display Name: Order Number
    * * SQL Data Type: varchar(50)
    */
    get OrderNumber(): string {
        return this.Get('OrderNumber');
    }
    set OrderNumber(value: string) {
        this.Set('OrderNumber', value);
    }

    /**
    * * Field Name: OrderDate
    * * Display Name: Order Date
    * * SQL Data Type: datetimeoffset
    * * Description: Order date, typically the transaction or purchase date.
    */
    get OrderDate(): Date {
        return this.Get('OrderDate');
    }
    set OrderDate(value: Date) {
        this.Set('OrderDate', value);
    }

    /**
    * * Field Name: CustomerID
    * * Display Name: Customer ID
    * * SQL Data Type: varchar(100)
    * * Description: Customer identifier from source system.
    */
    get CustomerID(): string | null {
        return this.Get('CustomerID');
    }
    set CustomerID(value: string | null) {
        this.Set('CustomerID', value);
    }

    /**
    * * Field Name: CustomerType
    * * Display Name: Customer Type
    * * SQL Data Type: varchar(50)
    * * Description: Type of customer (Individual, Organization, etc.).
    */
    get CustomerType(): string | null {
        return this.Get('CustomerType');
    }
    set CustomerType(value: string | null) {
        this.Set('CustomerType', value);
    }

    /**
    * * Field Name: TotalAmount
    * * Display Name: Total Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Total order amount including tax and shipping.
    */
    get TotalAmount(): number {
        return this.Get('TotalAmount');
    }
    set TotalAmount(value: number) {
        this.Set('TotalAmount', value);
    }

    /**
    * * Field Name: TaxAmount
    * * Display Name: Tax Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get TaxAmount(): number | null {
        return this.Get('TaxAmount');
    }
    set TaxAmount(value: number | null) {
        this.Set('TaxAmount', value);
    }

    /**
    * * Field Name: ShippingAmount
    * * Display Name: Shipping Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get ShippingAmount(): number | null {
        return this.Get('ShippingAmount');
    }
    set ShippingAmount(value: number | null) {
        this.Set('ShippingAmount', value);
    }

    /**
    * * Field Name: DiscountAmount
    * * Display Name: Discount Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get DiscountAmount(): number | null {
        return this.Get('DiscountAmount');
    }
    set DiscountAmount(value: number | null) {
        this.Set('DiscountAmount', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
    */
    get CurrencyID(): string | null {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string | null) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(50)
    */
    get Status(): string | null {
        return this.Get('Status');
    }
    set Status(value: string | null) {
        this.Set('Status', value);
    }

    /**
    * * Field Name: PaymentMethod
    * * Display Name: Payment Method
    * * SQL Data Type: varchar(50)
    */
    get PaymentMethod(): string | null {
        return this.Get('PaymentMethod');
    }
    set PaymentMethod(value: string | null) {
        this.Set('PaymentMethod', value);
    }

    /**
    * * Field Name: OrderChannel
    * * Display Name: Order Channel
    * * SQL Data Type: varchar(50)
    */
    get OrderChannel(): string | null {
        return this.Get('OrderChannel');
    }
    set OrderChannel(value: string | null) {
        this.Set('OrderChannel', value);
    }

    /**
    * * Field Name: State
    * * Display Name: State
    * * SQL Data Type: varchar(50)
    */
    get State(): string | null {
        return this.Get('State');
    }
    set State(value: string | null) {
        this.Set('State', value);
    }

    /**
    * * Field Name: ShippingState
    * * Display Name: Shipping State
    * * SQL Data Type: varchar(50)
    */
    get ShippingState(): string | null {
        return this.Get('ShippingState');
    }
    set ShippingState(value: string | null) {
        this.Set('ShippingState', value);
    }

    /**
    * * Field Name: BillingState
    * * Display Name: Billing State
    * * SQL Data Type: varchar(50)
    */
    get BillingState(): string | null {
        return this.Get('BillingState');
    }
    set BillingState(value: string | null) {
        this.Set('BillingState', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Reference to the CompanyIntegration that created/updated this record
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: CustomerName
    * * Display Name: Customer Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Full name of the customer placing the order.
    */
    get CustomerName(): string | null {
        return this.Get('CustomerName');
    }
    set CustomerName(value: string | null) {
        this.Set('CustomerName', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    * * Description: Name of the company associated with the order.
    */
    get Company(): string | null {
        return this.Get('Company');
    }
    set Company(value: string | null) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: DueDate
    * * Display Name: Due Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date when payment for the order is due.
    */
    get DueDate(): Date | null {
        return this.Get('DueDate');
    }
    set DueDate(value: Date | null) {
        this.Set('DueDate', value);
    }

    /**
    * * Field Name: PreviousVersionID
    * * Display Name: Previous Version ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Orders (vwOrders.ID)
    * * Description: Links to the previous version of this order.
    */
    get PreviousVersionID(): string | null {
        return this.Get('PreviousVersionID');
    }
    set PreviousVersionID(value: string | null) {
        this.Set('PreviousVersionID', value);
    }

    /**
    * * Field Name: OriginalOrderID
    * * Display Name: Original Order ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Orders (vwOrders.ID)
    * * Description: ID of the original order (version 1). Set to own ID after initial save.
    */
    get OriginalOrderID(): string | null {
        return this.Get('OriginalOrderID');
    }
    set OriginalOrderID(value: string | null) {
        this.Set('OriginalOrderID', value);
    }

    /**
    * * Field Name: VersionReason
    * * Display Name: Version Reason
    * * SQL Data Type: nvarchar(500)
    * * Description: Reason for creating this version (e.g., Order refunded, Status changed).
    */
    get VersionReason(): string | null {
        return this.Get('VersionReason');
    }
    set VersionReason(value: string | null) {
        this.Set('VersionReason', value);
    }

    /**
    * * Field Name: CanonicalHash
    * * Display Name: Canonical Hash
    * * SQL Data Type: varchar(64)
    * * Description: SHA-256 hash of canonical order data for change detection.
    */
    get CanonicalHash(): string | null {
        return this.Get('CanonicalHash');
    }
    set CanonicalHash(value: string | null) {
        this.Set('CanonicalHash', value);
    }

    /**
    * * Field Name: Version
    * * Display Name: Version
    * * SQL Data Type: int
    * * Default Value: 1
    * * Description: Version number of this order record, starting at 1.
    */
    get Version(): number {
        return this.Get('Version');
    }
    set Version(value: number) {
        this.Set('Version', value);
    }

    /**
    * * Field Name: IsActiveVersion
    * * Display Name: Is Active Version
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Indicates if this is the current active version of the order.
    */
    get IsActiveVersion(): boolean {
        return this.Get('IsActiveVersion');
    }
    set IsActiveVersion(value: boolean) {
        this.Set('IsActiveVersion', value);
    }

    /**
    * * Field Name: RefundState
    * * Display Name: Refund State
    * * SQL Data Type: varchar(50)
    * * Default Value: None
    * * Description: Refund state: None, Partial, or Full.
    */
    get RefundState(): string {
        return this.Get('RefundState');
    }
    set RefundState(value: string) {
        this.Set('RefundState', value);
    }

    /**
    * * Field Name: RefundTotal
    * * Display Name: Refund Total
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Total amount refunded on this order.
    */
    get RefundTotal(): number {
        return this.Get('RefundTotal');
    }
    set RefundTotal(value: number) {
        this.Set('RefundTotal', value);
    }

    /**
    * * Field Name: TaxRate
    * * Display Name: Tax Rate
    * * SQL Data Type: nvarchar(50)
    * * Description: Tax rate description or code applicable to this order (e.g., 7.5%, GST, VAT).
    */
    get TaxRate(): string | null {
        return this.Get('TaxRate');
    }
    set TaxRate(value: string | null) {
        this.Set('TaxRate', value);
    }

    /**
    * * Field Name: AgreementCode
    * * Display Name: Agreement Code
    * * SQL Data Type: nvarchar(20)
    * * Description: Agreement code used to associate orders with an Agreement (text).
    */
    get AgreementCode(): string | null {
        return this.Get('AgreementCode');
    }
    set AgreementCode(value: string | null) {
        this.Set('AgreementCode', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: AccountingTags
    * * Display Name: Accounting Tags
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON object containing accounting-relevant tags for this order, e.g. {"International": "true", "GrantNumber": "Grant-2025-001"}. Used by the Accounting Allocation Rules Engine to evaluate order-level conditions.
    */
    get AccountingTags(): string | null {
        return this.Get('AccountingTags');
    }
    set AccountingTags(value: string | null) {
        this.Set('AccountingTags', value);
    }

    /**
    * * Field Name: Currency
    * * Display Name: Currency
    * * SQL Data Type: varchar(100)
    */
    get Currency(): string | null {
        return this.Get('Currency');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string | null {
        return this.Get('Company_Virtual');
    }

    /**
    * * Field Name: RootPreviousVersionID
    * * Display Name: Root Previous Version ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootPreviousVersionID(): string | null {
        return this.Get('RootPreviousVersionID');
    }

    /**
    * * Field Name: PreviousVersionIDDepth
    * * Display Name: Previous Version ID Depth
    * * SQL Data Type: int
    */
    get PreviousVersionIDDepth(): number | null {
        return this.Get('PreviousVersionIDDepth');
    }

    /**
    * * Field Name: PreviousVersionIDPath
    * * Display Name: Previous Version ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get PreviousVersionIDPath(): string | null {
        return this.Get('PreviousVersionIDPath');
    }

    /**
    * * Field Name: PreviousVersionIDIsLeaf
    * * Display Name: Previous Version ID Is Leaf
    * * SQL Data Type: bit
    */
    get PreviousVersionIDIsLeaf(): boolean | null {
        return this.Get('PreviousVersionIDIsLeaf');
    }

    /**
    * * Field Name: PreviousVersionIDChildCount
    * * Display Name: Previous Version ID Child Count
    * * SQL Data Type: int
    */
    get PreviousVersionIDChildCount(): number | null {
        return this.Get('PreviousVersionIDChildCount');
    }

    /**
    * * Field Name: RootOriginalOrderID
    * * Display Name: Root Original Order ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootOriginalOrderID(): string | null {
        return this.Get('RootOriginalOrderID');
    }

    /**
    * * Field Name: OriginalOrderIDDepth
    * * Display Name: Original Order ID Depth
    * * SQL Data Type: int
    */
    get OriginalOrderIDDepth(): number | null {
        return this.Get('OriginalOrderIDDepth');
    }

    /**
    * * Field Name: OriginalOrderIDPath
    * * Display Name: Original Order ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get OriginalOrderIDPath(): string | null {
        return this.Get('OriginalOrderIDPath');
    }

    /**
    * * Field Name: OriginalOrderIDIsLeaf
    * * Display Name: Original Order ID Is Leaf
    * * SQL Data Type: bit
    */
    get OriginalOrderIDIsLeaf(): boolean | null {
        return this.Get('OriginalOrderIDIsLeaf');
    }

    /**
    * * Field Name: OriginalOrderIDChildCount
    * * Display Name: Original Order ID Child Count
    * * SQL Data Type: int
    */
    get OriginalOrderIDChildCount(): number | null {
        return this.Get('OriginalOrderIDChildCount');
    }
}


/**
 * Payment Lines - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: PaymentLine
 * * Base View: vwPaymentLines
 * * @description Line items showing payment allocation to specific orders.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Payment Lines')
export class soundpostPaymentLineEntity extends BaseEntity<soundpostPaymentLineEntityType> {
    /**
    * Loads the Payment Lines record from the database
    * @param ID: string - primary key value to load the Payment Lines record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostPaymentLineEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PaymentID
    * * Display Name: Payment ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Payments (vwPayments.ID)
    */
    get PaymentID(): string {
        return this.Get('PaymentID');
    }
    set PaymentID(value: string) {
        this.Set('PaymentID', value);
    }

    /**
    * * Field Name: OrderLineID
    * * Display Name: Order Line ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Order Lines (vwOrderLines.ID)
    */
    get OrderLineID(): string | null {
        return this.Get('OrderLineID');
    }
    set OrderLineID(value: string | null) {
        this.Set('OrderLineID', value);
    }

    /**
    * * Field Name: AmountApplied
    * * Display Name: Amount Applied
    * * SQL Data Type: decimal(18, 2)
    * * Description: Amount of payment applied to this line.
    */
    get AmountApplied(): number {
        return this.Get('AmountApplied');
    }
    set AmountApplied(value: number) {
        this.Set('AmountApplied', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Payments - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: Payment
 * * Base View: vwPayments
 * * @description Payment transactions from customers.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Payments')
export class soundpostPaymentEntity extends BaseEntity<soundpostPaymentEntityType> {
    /**
    * Loads the Payments record from the database
    * @param ID: string - primary key value to load the Payments record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostPaymentEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PaymentNumber
    * * Display Name: Payment Number
    * * SQL Data Type: varchar(50)
    * * Description: System-generated payment number.
    */
    get PaymentNumber(): string | null {
        return this.Get('PaymentNumber');
    }
    set PaymentNumber(value: string | null) {
        this.Set('PaymentNumber', value);
    }

    /**
    * * Field Name: PaymentDate
    * * Display Name: Payment Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date when payment was received.
    */
    get PaymentDate(): Date {
        return this.Get('PaymentDate');
    }
    set PaymentDate(value: Date) {
        this.Set('PaymentDate', value);
    }

    /**
    * * Field Name: CustomerID
    * * Display Name: Customer ID
    * * SQL Data Type: varchar(100)
    * * Description: Customer identifier from source system.
    */
    get CustomerID(): string | null {
        return this.Get('CustomerID');
    }
    set CustomerID(value: string | null) {
        this.Set('CustomerID', value);
    }

    /**
    * * Field Name: CustomerSource
    * * Display Name: Customer Source
    * * SQL Data Type: varchar(50)
    */
    get CustomerSource(): string | null {
        return this.Get('CustomerSource');
    }
    set CustomerSource(value: string | null) {
        this.Set('CustomerSource', value);
    }

    /**
    * * Field Name: Amount
    * * Display Name: Amount
    * * SQL Data Type: decimal(18, 2)
    */
    get Amount(): number {
        return this.Get('Amount');
    }
    set Amount(value: number) {
        this.Set('Amount', value);
    }

    /**
    * * Field Name: PaymentMethod
    * * Display Name: Payment Method
    * * SQL Data Type: varchar(50)
    * * Description: Payment method (Credit Card, ACH, Check, etc.).
    */
    get PaymentMethod(): string | null {
        return this.Get('PaymentMethod');
    }
    set PaymentMethod(value: string | null) {
        this.Set('PaymentMethod', value);
    }

    /**
    * * Field Name: ReferenceNumber
    * * Display Name: Reference Number
    * * SQL Data Type: varchar(100)
    * * Description: External reference number (transaction ID, check number, etc.).
    */
    get ReferenceNumber(): string | null {
        return this.Get('ReferenceNumber');
    }
    set ReferenceNumber(value: string | null) {
        this.Set('ReferenceNumber', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(50)
    * * Default Value: Pending
    * * Description: Payment status (Pending, Completed, Failed, Refunded).
    */
    get Status(): string {
        return this.Get('Status');
    }
    set Status(value: string) {
        this.Set('Status', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
    */
    get CurrencyID(): string {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: ExchangeRateID
    * * Display Name: Exchange Rate ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Exchange Rates (vwExchangeRates.ID)
    */
    get ExchangeRateID(): string | null {
        return this.Get('ExchangeRateID');
    }
    set ExchangeRateID(value: string | null) {
        this.Set('ExchangeRateID', value);
    }

    /**
    * * Field Name: Notes
    * * Display Name: Notes
    * * SQL Data Type: nvarchar(MAX)
    */
    get Notes(): string | null {
        return this.Get('Notes');
    }
    set Notes(value: string | null) {
        this.Set('Notes', value);
    }

    /**
    * * Field Name: IsPosted
    * * Display Name: Is Posted
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get IsPosted(): boolean {
        return this.Get('IsPosted');
    }
    set IsPosted(value: boolean) {
        this.Set('IsPosted', value);
    }

    /**
    * * Field Name: PostedDate
    * * Display Name: Posted Date
    * * SQL Data Type: datetimeoffset
    */
    get PostedDate(): Date | null {
        return this.Get('PostedDate');
    }
    set PostedDate(value: Date | null) {
        this.Set('PostedDate', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System Record ID
    * * SQL Data Type: varchar(100)
    */
    get ExternalSystemRecordID(): string | null {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string | null) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: ProcessingFee
    * * Display Name: Processing Fee
    * * SQL Data Type: decimal(12, 2)
    * * Default Value: 0
    * * Description: Payment processor fee amount (e.g., credit card processing fees).
    */
    get ProcessingFee(): number {
        return this.Get('ProcessingFee');
    }
    set ProcessingFee(value: number) {
        this.Set('ProcessingFee', value);
    }

    /**
    * * Field Name: CustomerName
    * * Display Name: Customer Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Full name of the customer making the payment.
    */
    get CustomerName(): string | null {
        return this.Get('CustomerName');
    }
    set CustomerName(value: string | null) {
        this.Set('CustomerName', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    * * Description: Name of the company associated with the payment.
    */
    get Company(): string | null {
        return this.Get('Company');
    }
    set Company(value: string | null) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: RelatedPaymentID
    * * Display Name: Related Payment ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Payments (vwPayments.ID)
    * * Description: Links refunds to the original payment.
    */
    get RelatedPaymentID(): string | null {
        return this.Get('RelatedPaymentID');
    }
    set RelatedPaymentID(value: string | null) {
        this.Set('RelatedPaymentID', value);
    }

    /**
    * * Field Name: PaymentType
    * * Display Name: Payment Type
    * * SQL Data Type: varchar(50)
    * * Default Value: Payment
    * * Description: Type of payment: Payment, Refund, or PartialRefund.
    */
    get PaymentType(): string {
        return this.Get('PaymentType');
    }
    set PaymentType(value: string) {
        this.Set('PaymentType', value);
    }

    /**
    * * Field Name: IsReversal
    * * Display Name: Is Reversal
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Indicates if this payment is a reversal/refund.
    */
    get IsReversal(): boolean {
        return this.Get('IsReversal');
    }
    set IsReversal(value: boolean) {
        this.Set('IsReversal', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Currency
    * * Display Name: Currency
    * * SQL Data Type: varchar(100)
    */
    get Currency(): string {
        return this.Get('Currency');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string | null {
        return this.Get('Company_Virtual');
    }

    /**
    * * Field Name: RootRelatedPaymentID
    * * Display Name: Root Related Payment ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootRelatedPaymentID(): string | null {
        return this.Get('RootRelatedPaymentID');
    }

    /**
    * * Field Name: RelatedPaymentIDDepth
    * * Display Name: Related Payment ID Depth
    * * SQL Data Type: int
    */
    get RelatedPaymentIDDepth(): number | null {
        return this.Get('RelatedPaymentIDDepth');
    }

    /**
    * * Field Name: RelatedPaymentIDPath
    * * Display Name: Related Payment ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get RelatedPaymentIDPath(): string | null {
        return this.Get('RelatedPaymentIDPath');
    }

    /**
    * * Field Name: RelatedPaymentIDIsLeaf
    * * Display Name: Related Payment ID Is Leaf
    * * SQL Data Type: bit
    */
    get RelatedPaymentIDIsLeaf(): boolean | null {
        return this.Get('RelatedPaymentIDIsLeaf');
    }

    /**
    * * Field Name: RelatedPaymentIDChildCount
    * * Display Name: Related Payment ID Child Count
    * * SQL Data Type: int
    */
    get RelatedPaymentIDChildCount(): number | null {
        return this.Get('RelatedPaymentIDChildCount');
    }
}


/**
 * Posting Group Line Dimensions - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: PostingGroupLineDimension
 * * Base View: vwPostingGroupLineDimensions
 * * @description Mapping of posting group lines to financial dimensions (e.g., department, project).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Posting Group Line Dimensions')
export class soundpostPostingGroupLineDimensionEntity extends BaseEntity<soundpostPostingGroupLineDimensionEntityType> {
    /**
    * Loads the Posting Group Line Dimensions record from the database
    * @param ID: string - primary key value to load the Posting Group Line Dimensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostPostingGroupLineDimensionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PostingGroupLineID
    * * Display Name: Posting Group Line ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Posting Group Lines (vwPostingGroupLines.ID)
    */
    get PostingGroupLineID(): string {
        return this.Get('PostingGroupLineID');
    }
    set PostingGroupLineID(value: string) {
        this.Set('PostingGroupLineID', value);
    }

    /**
    * * Field Name: DimensionTypeID
    * * Display Name: Dimension Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Dimension Types (vwDimensionTypes.ID)
    */
    get DimensionTypeID(): string {
        return this.Get('DimensionTypeID');
    }
    set DimensionTypeID(value: string) {
        this.Set('DimensionTypeID', value);
    }

    /**
    * * Field Name: DimensionID
    * * Display Name: Dimension ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Dimensions (vwDimensions.ID)
    */
    get DimensionID(): string {
        return this.Get('DimensionID');
    }
    set DimensionID(value: string) {
        this.Set('DimensionID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: DimensionType
    * * Display Name: Dimension Type
    * * SQL Data Type: varchar(50)
    */
    get DimensionType(): string {
        return this.Get('DimensionType');
    }

    /**
    * * Field Name: Dimension
    * * Display Name: Dimension
    * * SQL Data Type: varchar(100)
    */
    get Dimension(): string {
        return this.Get('Dimension');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Posting Group Lines - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: PostingGroupLine
 * * Base View: vwPostingGroupLines
 * * @description Detail record representing a debit or credit to a GL account for a posting group.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Posting Group Lines')
export class soundpostPostingGroupLineEntity extends BaseEntity<soundpostPostingGroupLineEntityType> {
    /**
    * Loads the Posting Group Lines record from the database
    * @param ID: string - primary key value to load the Posting Group Lines record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostPostingGroupLineEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PostingGroupID
    * * Display Name: Posting Group ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Posting Groups (vwPostingGroups.ID)
    */
    get PostingGroupID(): string {
        return this.Get('PostingGroupID');
    }
    set PostingGroupID(value: string) {
        this.Set('PostingGroupID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: DebitAmount
    * * Display Name: Debit Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get DebitAmount(): number {
        return this.Get('DebitAmount');
    }
    set DebitAmount(value: number) {
        this.Set('DebitAmount', value);
    }

    /**
    * * Field Name: CreditAmount
    * * Display Name: Credit Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get CreditAmount(): number {
        return this.Get('CreditAmount');
    }
    set CreditAmount(value: number) {
        this.Set('CreditAmount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string {
        return this.Get('GLAccount');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Posting Groups - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: PostingGroup
 * * Base View: vwPostingGroups
 * * @description Header record for grouped journal entries or transactions by posting date and currency.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Posting Groups')
export class soundpostPostingGroupEntity extends BaseEntity<soundpostPostingGroupEntityType> {
    /**
    * Loads the Posting Groups record from the database
    * @param ID: string - primary key value to load the Posting Groups record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostPostingGroupEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key for the posting group.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PostingDate
    * * Display Name: Posting Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date the transactions in this group are considered posted.
    */
    get PostingDate(): Date {
        return this.Get('PostingDate');
    }
    set PostingDate(value: Date) {
        this.Set('PostingDate', value);
    }

    /**
    * * Field Name: AccountingPeriodID
    * * Display Name: Accounting Period ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)
    * * Description: Optional accounting period the posting group is associated with.
    */
    get AccountingPeriodID(): string | null {
        return this.Get('AccountingPeriodID');
    }
    set AccountingPeriodID(value: string | null) {
        this.Set('AccountingPeriodID', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
    * * Description: Currency in which this posting group is denominated.
    */
    get CurrencyID(): string {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: Date/time the posting group was created in the system.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: Date/time the posting group was last updated.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: PostedDate
    * * Display Name: Posted Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date/time the posting group was marked as posted.
    */
    get PostedDate(): Date | null {
        return this.Get('PostedDate');
    }
    set PostedDate(value: Date | null) {
        this.Set('PostedDate', value);
    }

    /**
    * * Field Name: PostingStatus
    * * Display Name: Posting Status
    * * SQL Data Type: varchar(20)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Cutover
    *   * Error
    *   * Excluded
    *   * Pending
    *   * Posted
    * * Description: Status of the posting group: Pending, Posted, Error, Cutover (included in reporting), or Excluded (hidden and excluded from reporting).
    */
    get PostingStatus(): 'Cutover' | 'Error' | 'Excluded' | 'Pending' | 'Posted' {
        return this.Get('PostingStatus');
    }
    set PostingStatus(value: 'Cutover' | 'Error' | 'Excluded' | 'Pending' | 'Posted') {
        this.Set('PostingStatus', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Company integration context, if applicable (e.g., Intacct, BC).
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System Record ID
    * * SQL Data Type: varchar(100)
    * * Description: Identifier of the record in the external accounting system.
    */
    get ExternalSystemRecordID(): string | null {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string | null) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: StartDate
    * * Display Name: Start Date
    * * SQL Data Type: datetimeoffset
    * * Description: Start of the posting group's effective date range.
    */
    get StartDate(): Date {
        return this.Get('StartDate');
    }
    set StartDate(value: Date) {
        this.Set('StartDate', value);
    }

    /**
    * * Field Name: EndDate
    * * Display Name: End Date
    * * SQL Data Type: datetimeoffset
    * * Description: End of the posting group's effective date range.
    */
    get EndDate(): Date {
        return this.Get('EndDate');
    }
    set EndDate(value: Date) {
        this.Set('EndDate', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Owning company; references __mj.Company(ID).
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: PostingNumber
    * * Display Name: Posting Number
    * * SQL Data Type: int
    */
    get PostingNumber(): number {
        return this.Get('PostingNumber');
    }
    set PostingNumber(value: number) {
        this.Set('PostingNumber', value);
    }

    /**
    * * Field Name: ExternalSystemDisplayID
    * * Display Name: External System Display ID
    * * SQL Data Type: varchar(100)
    * * Description: User-facing identifier for this record as displayed in the external accounting system UI (e.g., Intacct batch number, QuickBooks document number). Distinct from ExternalSystemRecordID, which holds the internal API/record key used for system-to-system operations.
    */
    get ExternalSystemDisplayID(): string | null {
        return this.Get('ExternalSystemDisplayID');
    }
    set ExternalSystemDisplayID(value: string | null) {
        this.Set('ExternalSystemDisplayID', value);
    }

    /**
    * * Field Name: ExternalSystemURL
    * * Display Name: External System URL
    * * SQL Data Type: nvarchar(500)
    * * Description: Full URL to this record in the external accounting system web UI. Stored as a complete URL because external systems (e.g., Intacct) use hashed or session-encoded paths that cannot be reconstructed from the record ID alone.
    */
    get ExternalSystemURL(): string | null {
        return this.Get('ExternalSystemURL');
    }
    set ExternalSystemURL(value: string | null) {
        this.Set('ExternalSystemURL', value);
    }

    /**
    * * Field Name: Currency
    * * Display Name: Currency
    * * SQL Data Type: varchar(100)
    */
    get Currency(): string {
        return this.Get('Currency');
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string {
        return this.Get('Company');
    }
}


/**
 * Processing Batches - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ProcessingBatch
 * * Base View: vwProcessingBatches
 * * @description Tracks batch processing runs from source systems.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Processing Batches')
export class soundpostProcessingBatchEntity extends BaseEntity<soundpostProcessingBatchEntityType> {
    /**
    * Loads the Processing Batches record from the database
    * @param ID: string - primary key value to load the Processing Batches record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostProcessingBatchEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: BatchNumber
    * * Display Name: Batch Number
    * * SQL Data Type: varchar(50)
    * * Description: System-generated batch number for tracking.
    */
    get BatchNumber(): string | null {
        return this.Get('BatchNumber');
    }
    set BatchNumber(value: string | null) {
        this.Set('BatchNumber', value);
    }

    /**
    * * Field Name: SourceSystem
    * * Display Name: Source System
    * * SQL Data Type: varchar(50)
    */
    get SourceSystem(): string {
        return this.Get('SourceSystem');
    }
    set SourceSystem(value: string) {
        this.Set('SourceSystem', value);
    }

    /**
    * * Field Name: EntityType
    * * Display Name: Entity Type
    * * SQL Data Type: varchar(50)
    * * Description: Type of entity being processed (Order, Product, etc.).
    */
    get EntityType(): string {
        return this.Get('EntityType');
    }
    set EntityType(value: string) {
        this.Set('EntityType', value);
    }

    /**
    * * Field Name: StartTime
    * * Display Name: Start Time
    * * SQL Data Type: datetime
    */
    get StartTime(): Date {
        return this.Get('StartTime');
    }
    set StartTime(value: Date) {
        this.Set('StartTime', value);
    }

    /**
    * * Field Name: EndTime
    * * Display Name: End Time
    * * SQL Data Type: datetime
    */
    get EndTime(): Date | null {
        return this.Get('EndTime');
    }
    set EndTime(value: Date | null) {
        this.Set('EndTime', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(20)
    * * Default Value: Pending
    * * Description: Processing status (Pending, Running, Completed, Failed).
    */
    get Status(): string {
        return this.Get('Status');
    }
    set Status(value: string) {
        this.Set('Status', value);
    }

    /**
    * * Field Name: RecordsProcessed
    * * Display Name: Records Processed
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsProcessed(): number {
        return this.Get('RecordsProcessed');
    }
    set RecordsProcessed(value: number) {
        this.Set('RecordsProcessed', value);
    }

    /**
    * * Field Name: RecordsSucceeded
    * * Display Name: Records Succeeded
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsSucceeded(): number {
        return this.Get('RecordsSucceeded');
    }
    set RecordsSucceeded(value: number) {
        this.Set('RecordsSucceeded', value);
    }

    /**
    * * Field Name: RecordsFailed
    * * Display Name: Records Failed
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsFailed(): number {
        return this.Get('RecordsFailed');
    }
    set RecordsFailed(value: number) {
        this.Set('RecordsFailed', value);
    }

    /**
    * * Field Name: ProcessingDuration
    * * Display Name: Processing Duration
    * * SQL Data Type: int
    * * Description: Duration of processing in milliseconds.
    */
    get ProcessingDuration(): number | null {
        return this.Get('ProcessingDuration');
    }
    set ProcessingDuration(value: number | null) {
        this.Set('ProcessingDuration', value);
    }

    /**
    * * Field Name: ErrorMessage
    * * Display Name: Error Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get ErrorMessage(): string | null {
        return this.Get('ErrorMessage');
    }
    set ErrorMessage(value: string | null) {
        this.Set('ErrorMessage', value);
    }

    /**
    * * Field Name: CreatedBy
    * * Display Name: Created By
    * * SQL Data Type: varchar(100)
    */
    get CreatedBy(): string | null {
        return this.Get('CreatedBy');
    }
    set CreatedBy(value: string | null) {
        this.Set('CreatedBy', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Processing Errors - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ProcessingError
 * * Base View: vwProcessingErrors
 * * @description Detailed error tracking for processing failures.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Processing Errors')
export class soundpostProcessingErrorEntity extends BaseEntity<soundpostProcessingErrorEntityType> {
    /**
    * Loads the Processing Errors record from the database
    * @param ID: string - primary key value to load the Processing Errors record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostProcessingErrorEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProcessingBatchID
    * * Display Name: Processing Batch ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Processing Batches (vwProcessingBatches.ID)
    */
    get ProcessingBatchID(): string | null {
        return this.Get('ProcessingBatchID');
    }
    set ProcessingBatchID(value: string | null) {
        this.Set('ProcessingBatchID', value);
    }

    /**
    * * Field Name: RecordID
    * * Display Name: Record ID
    * * SQL Data Type: varchar(100)
    * * Description: Identifier of the record that failed processing.
    */
    get RecordID(): string | null {
        return this.Get('RecordID');
    }
    set RecordID(value: string | null) {
        this.Set('RecordID', value);
    }

    /**
    * * Field Name: ErrorType
    * * Display Name: Error Type
    * * SQL Data Type: varchar(50)
    * * Description: Type of error (Validation, Mapping, System, etc.).
    */
    get ErrorType(): string {
        return this.Get('ErrorType');
    }
    set ErrorType(value: string) {
        this.Set('ErrorType', value);
    }

    /**
    * * Field Name: ErrorCode
    * * Display Name: Error Code
    * * SQL Data Type: varchar(50)
    */
    get ErrorCode(): string | null {
        return this.Get('ErrorCode');
    }
    set ErrorCode(value: string | null) {
        this.Set('ErrorCode', value);
    }

    /**
    * * Field Name: ErrorMessage
    * * Display Name: Error Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get ErrorMessage(): string {
        return this.Get('ErrorMessage');
    }
    set ErrorMessage(value: string) {
        this.Set('ErrorMessage', value);
    }

    /**
    * * Field Name: FieldName
    * * Display Name: Field Name
    * * SQL Data Type: varchar(100)
    * * Description: Field that caused the error if applicable.
    */
    get FieldName(): string | null {
        return this.Get('FieldName');
    }
    set FieldName(value: string | null) {
        this.Set('FieldName', value);
    }

    /**
    * * Field Name: FieldValue
    * * Display Name: Field Value
    * * SQL Data Type: nvarchar(MAX)
    */
    get FieldValue(): string | null {
        return this.Get('FieldValue');
    }
    set FieldValue(value: string | null) {
        this.Set('FieldValue', value);
    }

    /**
    * * Field Name: SourceData
    * * Display Name: Source Data
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON representation of source data that failed.
    */
    get SourceData(): string | null {
        return this.Get('SourceData');
    }
    set SourceData(value: string | null) {
        this.Set('SourceData', value);
    }

    /**
    * * Field Name: ErrorDate
    * * Display Name: Error Date
    * * SQL Data Type: datetime
    * * Default Value: getdate()
    */
    get ErrorDate(): Date {
        return this.Get('ErrorDate');
    }
    set ErrorDate(value: Date) {
        this.Set('ErrorDate', value);
    }

    /**
    * * Field Name: IsResolved
    * * Display Name: Is Resolved
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether the error has been resolved.
    */
    get IsResolved(): boolean {
        return this.Get('IsResolved');
    }
    set IsResolved(value: boolean) {
        this.Set('IsResolved', value);
    }

    /**
    * * Field Name: ResolvedDate
    * * Display Name: Resolved Date
    * * SQL Data Type: datetime
    */
    get ResolvedDate(): Date | null {
        return this.Get('ResolvedDate');
    }
    set ResolvedDate(value: Date | null) {
        this.Set('ResolvedDate', value);
    }

    /**
    * * Field Name: ResolvedBy
    * * Display Name: Resolved By
    * * SQL Data Type: varchar(100)
    */
    get ResolvedBy(): string | null {
        return this.Get('ResolvedBy');
    }
    set ResolvedBy(value: string | null) {
        this.Set('ResolvedBy', value);
    }

    /**
    * * Field Name: Resolution
    * * Display Name: Resolution
    * * SQL Data Type: nvarchar(MAX)
    */
    get Resolution(): string | null {
        return this.Get('Resolution');
    }
    set Resolution(value: string | null) {
        this.Set('Resolution', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Product GL Account Dimensions - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ProductGLAccountDimension
 * * Base View: vwProductGLAccountDimensions
 * * @description Dimension mappings for product GL accounts.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Product GL Account Dimensions')
export class soundpostProductGLAccountDimensionEntity extends BaseEntity<soundpostProductGLAccountDimensionEntityType> {
    /**
    * Loads the Product GL Account Dimensions record from the database
    * @param ID: string - primary key value to load the Product GL Account Dimensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostProductGLAccountDimensionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProductGLAccountID
    * * Display Name: Product GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Product GL Accounts (vwProductGLAccounts.ID)
    */
    get ProductGLAccountID(): string {
        return this.Get('ProductGLAccountID');
    }
    set ProductGLAccountID(value: string) {
        this.Set('ProductGLAccountID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: DimensionTypeID
    * * Display Name: Dimension Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Dimension Types (vwDimensionTypes.ID)
    * * Description: Reference to the dimension type.
    */
    get DimensionTypeID(): string {
        return this.Get('DimensionTypeID');
    }
    set DimensionTypeID(value: string) {
        this.Set('DimensionTypeID', value);
    }

    /**
    * * Field Name: DimensionID
    * * Display Name: Dimension ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Dimensions (vwDimensions.ID)
    * * Description: Reference to the specific dimension value.
    */
    get DimensionID(): string {
        return this.Get('DimensionID');
    }
    set DimensionID(value: string) {
        this.Set('DimensionID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: DimensionType
    * * Display Name: Dimension Type
    * * SQL Data Type: varchar(50)
    */
    get DimensionType(): string {
        return this.Get('DimensionType');
    }

    /**
    * * Field Name: Dimension
    * * Display Name: Dimension
    * * SQL Data Type: varchar(100)
    */
    get Dimension(): string {
        return this.Get('Dimension');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Product GL Accounts - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ProductGLAccount
 * * Base View: vwProductGLAccounts
 * * @description Links products to their GL accounts by type (row-based structure)
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Product GL Accounts')
export class soundpostProductGLAccountEntity extends BaseEntity<soundpostProductGLAccountEntityType> {
    /**
    * Loads the Product GL Accounts record from the database
    * @param ID: string - primary key value to load the Product GL Accounts record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostProductGLAccountEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Unique identifier for the product GL account mapping
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProductID
    * * Display Name: Product ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Products (vwProducts.ID)
    * * Description: Foreign key to the Product table
    */
    get ProductID(): string {
        return this.Get('ProductID');
    }
    set ProductID(value: string) {
        this.Set('ProductID', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Indicates if this GL account mapping is active
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: GLAccountTypeID
    * * Display Name: GL Account Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Account Types (vwGLAccountTypes.ID)
    * * Description: Foreign key to the GLAccountType table (Revenue, Receivable, etc.)
    */
    get GLAccountTypeID(): string | null {
        return this.Get('GLAccountTypeID');
    }
    set GLAccountTypeID(value: string | null) {
        this.Set('GLAccountTypeID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    * * Description: Foreign key to the GLAccount table
    */
    get GLAccountID(): string | null {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string | null) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: GLAccountType
    * * Display Name: GL Account Type
    * * SQL Data Type: varchar(100)
    */
    get GLAccountType(): string | null {
        return this.Get('GLAccountType');
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string | null {
        return this.Get('GLAccount');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Products - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: Product
 * * Base View: vwProducts
 * * @description Master product catalog synchronized from source systems.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Products')
export class soundpostProductEntity extends BaseEntity<soundpostProductEntityType> {
    /**
    * Loads the Products record from the database
    * @param ID: string - primary key value to load the Products record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostProductEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System Record ID
    * * SQL Data Type: varchar(100)
    * * Description: External system product identifier.
    */
    get ExternalSystemRecordID(): string | null {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string | null) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: ProductName
    * * Display Name: Product Name
    * * SQL Data Type: nvarchar(500)
    * * Description: Name of the product.
    */
    get ProductName(): string {
        return this.Get('ProductName');
    }
    set ProductName(value: string) {
        this.Set('ProductName', value);
    }

    /**
    * * Field Name: SKU
    * * Display Name: SKU
    * * SQL Data Type: varchar(100)
    * * Description: Stock Keeping Unit for inventory tracking.
    */
    get SKU(): string | null {
        return this.Get('SKU');
    }
    set SKU(value: string | null) {
        this.Set('SKU', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: Category
    * * Display Name: Category
    * * SQL Data Type: varchar(200)
    * * Description: Product category for grouping and mapping.
    */
    get Category(): string | null {
        return this.Get('Category');
    }
    set Category(value: string | null) {
        this.Set('Category', value);
    }

    /**
    * * Field Name: Price
    * * Display Name: Price
    * * SQL Data Type: decimal(18, 2)
    */
    get Price(): number | null {
        return this.Get('Price');
    }
    set Price(value: number | null) {
        this.Set('Price', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: ProductType
    * * Display Name: Product Type
    * * SQL Data Type: varchar(50)
    * * Description: Type of product (Physical, Digital, Service, Membership, etc.).
    */
    get ProductType(): string | null {
        return this.Get('ProductType');
    }
    set ProductType(value: string | null) {
        this.Set('ProductType', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: ReconFlowCategoryID
    * * Display Name: Recon Flow Category ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Recon Flow Categories (vwReconFlowCategories.ID)
    * * Description: Default reconciliation flow category applied to new order lines for this product.
    */
    get ReconFlowCategoryID(): string | null {
        return this.Get('ReconFlowCategoryID');
    }
    set ReconFlowCategoryID(value: string | null) {
        this.Set('ReconFlowCategoryID', value);
    }

    /**
    * * Field Name: SourceMetadata
    * * Display Name: Source Metadata
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON metadata from source system for GL mapping and integration-specific fields.
    */
    get SourceMetadata(): string | null {
        return this.Get('SourceMetadata');
    }
    set SourceMetadata(value: string | null) {
        this.Set('SourceMetadata', value);
    }

    /**
    * * Field Name: ParentID
    * * Display Name: Parent ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Products (vwProducts.ID)
    * * Description: Parent product identifier. Links to another record in the Product table. Nullable.
    */
    get ParentID(): string | null {
        return this.Get('ParentID');
    }
    set ParentID(value: string | null) {
        this.Set('ParentID', value);
    }

    /**
    * * Field Name: RevenueRecognitionDate
    * * Display Name: Revenue Recognition Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date used for revenue recognition, if applicable. Nullable.
    */
    get RevenueRecognitionDate(): Date | null {
        return this.Get('RevenueRecognitionDate');
    }
    set RevenueRecognitionDate(value: Date | null) {
        this.Set('RevenueRecognitionDate', value);
    }

    /**
    * * Field Name: HasGLSetupIssue
    * * Display Name: Has GL Setup Issue
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Indicates if this product has a GL account setup issue (1 = issue, 0 = none).
    */
    get HasGLSetupIssue(): boolean {
        return this.Get('HasGLSetupIssue');
    }
    set HasGLSetupIssue(value: boolean) {
        this.Set('HasGLSetupIssue', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: ReconFlowCategory
    * * Display Name: Recon Flow Category
    * * SQL Data Type: varchar(100)
    */
    get ReconFlowCategory(): string | null {
        return this.Get('ReconFlowCategory');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }

    /**
    * * Field Name: RootParentID
    * * Display Name: Root Parent ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootParentID(): string | null {
        return this.Get('RootParentID');
    }

    /**
    * * Field Name: ParentIDDepth
    * * Display Name: Parent ID Depth
    * * SQL Data Type: int
    */
    get ParentIDDepth(): number | null {
        return this.Get('ParentIDDepth');
    }

    /**
    * * Field Name: ParentIDPath
    * * Display Name: Parent ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get ParentIDPath(): string | null {
        return this.Get('ParentIDPath');
    }

    /**
    * * Field Name: ParentIDIsLeaf
    * * Display Name: Parent ID Is Leaf
    * * SQL Data Type: bit
    */
    get ParentIDIsLeaf(): boolean | null {
        return this.Get('ParentIDIsLeaf');
    }

    /**
    * * Field Name: ParentIDChildCount
    * * Display Name: Parent ID Child Count
    * * SQL Data Type: int
    */
    get ParentIDChildCount(): number | null {
        return this.Get('ParentIDChildCount');
    }
}


/**
 * Recon Balance Snapshots - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReconBalanceSnapshot
 * * Base View: vwReconBalanceSnapshots
 * * @description Balance snapshot (detail-side and control-side) by control type and optional GL account.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Recon Balance Snapshots')
export class soundpostReconBalanceSnapshotEntity extends BaseEntity<soundpostReconBalanceSnapshotEntityType> {
    /**
    * Loads the Recon Balance Snapshots record from the database
    * @param ID: string - primary key value to load the Recon Balance Snapshots record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReconBalanceSnapshotEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: RunID
    * * Display Name: Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Recon Runs (vwReconRuns.ID)
    * * Description: Reconciliation run.
    */
    get RunID(): string {
        return this.Get('RunID');
    }
    set RunID(value: string) {
        this.Set('RunID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Integration instance.
    */
    get CompanyIntegrationID(): string {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: ControlAccountTypeID
    * * Display Name: Control Account Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Control Account Types (vwControlAccountTypes.ID)
    * * Description: Control account type (e.g., AR).
    */
    get ControlAccountTypeID(): string {
        return this.Get('ControlAccountTypeID');
    }
    set ControlAccountTypeID(value: string) {
        this.Set('ControlAccountTypeID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    * * Description: Optional GL account when aggregating by account.
    */
    get GLAccountID(): string | null {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string | null) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: AmountBase
    * * Display Name: Amount Base
    * * SQL Data Type: decimal(18, 2)
    * * Description: Amount in base currency.
    */
    get AmountBase(): number {
        return this.Get('AmountBase');
    }
    set AmountBase(value: number) {
        this.Set('AmountBase', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Description: Currency of the amount.
    */
    get CurrencyID(): string {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: ExchangeRate
    * * Display Name: Exchange Rate
    * * SQL Data Type: decimal(12, 6)
    * * Default Value: 1.0
    * * Description: Rate to convert to base.
    */
    get ExchangeRate(): number {
        return this.Get('ExchangeRate');
    }
    set ExchangeRate(value: number) {
        this.Set('ExchangeRate', value);
    }

    /**
    * * Field Name: RowCount
    * * Display Name: Row Count
    * * SQL Data Type: bigint
    * * Description: Count of detail rows aggregated.
    */
    get RowCount(): number | null {
        return this.Get('RowCount');
    }
    set RowCount(value: number | null) {
        this.Set('RowCount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC created timestamp.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC updated timestamp.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: ControlAccountType
    * * Display Name: Control Account Type
    * * SQL Data Type: varchar(100)
    */
    get ControlAccountType(): string {
        return this.Get('ControlAccountType');
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string | null {
        return this.Get('GLAccount');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Recon Flow Categories - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReconFlowCategory
 * * Base View: vwReconFlowCategories
 * * @description Categories for reconciliation flows (e.g., Registration, Membership/Dues).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Recon Flow Categories')
export class soundpostReconFlowCategoryEntity extends BaseEntity<soundpostReconFlowCategoryEntityType> {
    /**
    * Loads the Recon Flow Categories record from the database
    * @param ID: string - primary key value to load the Recon Flow Categories record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReconFlowCategoryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Code
    * * Display Name: Code
    * * SQL Data Type: varchar(50)
    * * Description: Unique short code.
    */
    get Code(): string {
        return this.Get('Code');
    }
    set Code(value: string) {
        this.Set('Code', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: varchar(100)
    * * Description: Display name.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    * * Description: Optional description.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: CategoryDomain
    * * Display Name: Category Domain
    * * SQL Data Type: varchar(30)
    * * Default Value: Revenue
    * * Value List Type: List
    * * Possible Values 
    *   * COGS
    *   * Other
    *   * OtherIncome
    *   * Revenue
    * * Description: Logical domain for rollups.
    */
    get CategoryDomain(): 'COGS' | 'Other' | 'OtherIncome' | 'Revenue' {
        return this.Get('CategoryDomain');
    }
    set CategoryDomain(value: 'COGS' | 'Other' | 'OtherIncome' | 'Revenue') {
        this.Set('CategoryDomain', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Active flag.
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC created timestamp.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC updated timestamp.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Recon Flow Snapshots - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReconFlowSnapshot
 * * Base View: vwReconFlowSnapshots
 * * @description Flow snapshot (period and YTD) by integration, category, and optional GL account.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Recon Flow Snapshots')
export class soundpostReconFlowSnapshotEntity extends BaseEntity<soundpostReconFlowSnapshotEntityType> {
    /**
    * Loads the Recon Flow Snapshots record from the database
    * @param ID: string - primary key value to load the Recon Flow Snapshots record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReconFlowSnapshotEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: RunID
    * * Display Name: Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Recon Runs (vwReconRuns.ID)
    * * Description: Reconciliation run.
    */
    get RunID(): string {
        return this.Get('RunID');
    }
    set RunID(value: string) {
        this.Set('RunID', value);
    }

    /**
    * * Field Name: PeriodStart
    * * Display Name: Period Start
    * * SQL Data Type: datetimeoffset
    * * Description: Period start date.
    */
    get PeriodStart(): Date {
        return this.Get('PeriodStart');
    }
    set PeriodStart(value: Date) {
        this.Set('PeriodStart', value);
    }

    /**
    * * Field Name: PeriodEnd
    * * Display Name: Period End
    * * SQL Data Type: datetimeoffset
    * * Description: Period end date.
    */
    get PeriodEnd(): Date {
        return this.Get('PeriodEnd');
    }
    set PeriodEnd(value: Date) {
        this.Set('PeriodEnd', value);
    }

    /**
    * * Field Name: YTDThrough
    * * Display Name: YTD Through
    * * SQL Data Type: datetimeoffset
    * * Description: YTD through date (usually same as AsOf).
    */
    get YTDThrough(): Date {
        return this.Get('YTDThrough');
    }
    set YTDThrough(value: Date) {
        this.Set('YTDThrough', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Integration instance.
    */
    get CompanyIntegrationID(): string {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: ReconFlowCategoryID
    * * Display Name: Recon Flow Category ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Recon Flow Categories (vwReconFlowCategories.ID)
    * * Description: Flow category (e.g., Registration).
    */
    get ReconFlowCategoryID(): string {
        return this.Get('ReconFlowCategoryID');
    }
    set ReconFlowCategoryID(value: string) {
        this.Set('ReconFlowCategoryID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    * * Description: Optional GL account for GL-side rollups.
    */
    get GLAccountID(): string | null {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string | null) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: AmountBase
    * * Display Name: Amount Base
    * * SQL Data Type: decimal(18, 2)
    * * Description: Amount in base currency.
    */
    get AmountBase(): number {
        return this.Get('AmountBase');
    }
    set AmountBase(value: number) {
        this.Set('AmountBase', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Description: Currency of the amount.
    */
    get CurrencyID(): string {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: ExchangeRate
    * * Display Name: Exchange Rate
    * * SQL Data Type: decimal(12, 6)
    * * Default Value: 1.0
    * * Description: Rate to convert to base.
    */
    get ExchangeRate(): number {
        return this.Get('ExchangeRate');
    }
    set ExchangeRate(value: number) {
        this.Set('ExchangeRate', value);
    }

    /**
    * * Field Name: RowCount
    * * Display Name: Row Count
    * * SQL Data Type: bigint
    * * Description: Number of detailed rows aggregated.
    */
    get RowCount(): number | null {
        return this.Get('RowCount');
    }
    set RowCount(value: number | null) {
        this.Set('RowCount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC created timestamp.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC updated timestamp.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: ReconFlowCategory
    * * Display Name: Recon Flow Category
    * * SQL Data Type: varchar(100)
    */
    get ReconFlowCategory(): string {
        return this.Get('ReconFlowCategory');
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string | null {
        return this.Get('GLAccount');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Recon Run Integrations - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReconRunIntegration
 * * Base View: vwReconRunIntegrations
 * * @description Per-run status for each participating integration (Woo, PheedLoop, Intacct).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Recon Run Integrations')
export class soundpostReconRunIntegrationEntity extends BaseEntity<soundpostReconRunIntegrationEntityType> {
    /**
    * Loads the Recon Run Integrations record from the database
    * @param ID: string - primary key value to load the Recon Run Integrations record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReconRunIntegrationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: RunID
    * * Display Name: Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Recon Runs (vwReconRuns.ID)
    * * Description: Parent reconciliation run.
    */
    get RunID(): string {
        return this.Get('RunID');
    }
    set RunID(value: string) {
        this.Set('RunID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Integration instance (source).
    */
    get CompanyIntegrationID(): string {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: IsAccountingSystem
    * * Display Name: Is Accounting System
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Flags accounting system (e.g., Intacct).
    */
    get IsAccountingSystem(): boolean {
        return this.Get('IsAccountingSystem');
    }
    set IsAccountingSystem(value: boolean) {
        this.Set('IsAccountingSystem', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(20)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Complete
    *   * Error
    *   * Pending
    * * Description: Extract/processing status.
    */
    get Status(): 'Complete' | 'Error' | 'Pending' {
        return this.Get('Status');
    }
    set Status(value: 'Complete' | 'Error' | 'Pending') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: ExtractRecordCount
    * * Display Name: Extract Record Count
    * * SQL Data Type: int
    * * Description: Row count fetched from the integration.
    */
    get ExtractRecordCount(): number | null {
        return this.Get('ExtractRecordCount');
    }
    set ExtractRecordCount(value: number | null) {
        this.Set('ExtractRecordCount', value);
    }

    /**
    * * Field Name: ExtractStartedAt
    * * Display Name: Extract Started At
    * * SQL Data Type: datetimeoffset
    * * Description: UTC start of extract.
    */
    get ExtractStartedAt(): Date | null {
        return this.Get('ExtractStartedAt');
    }
    set ExtractStartedAt(value: Date | null) {
        this.Set('ExtractStartedAt', value);
    }

    /**
    * * Field Name: ExtractEndedAt
    * * Display Name: Extract Ended At
    * * SQL Data Type: datetimeoffset
    * * Description: UTC end of extract.
    */
    get ExtractEndedAt(): Date | null {
        return this.Get('ExtractEndedAt');
    }
    set ExtractEndedAt(value: Date | null) {
        this.Set('ExtractEndedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC created timestamp.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC updated timestamp.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Message
    * * Display Name: Message
    * * SQL Data Type: nvarchar(1000)
    * * Description: Outcome message for this integration extract (error text or a short summary).
    */
    get Message(): string | null {
        return this.Get('Message');
    }
    set Message(value: string | null) {
        this.Set('Message', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Recon Runs - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReconRun
 * * Base View: vwReconRuns
 * * @description A single reconciliation run using a shared cutoff date across systems.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Recon Runs')
export class soundpostReconRunEntity extends BaseEntity<soundpostReconRunEntityType> {
    /**
    * Loads the Recon Runs record from the database
    * @param ID: string - primary key value to load the Recon Runs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReconRunEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AsOfDate
    * * Display Name: As Of Date
    * * SQL Data Type: datetimeoffset
    * * Description: Cutoff date for the reconciliation (as-of).
    */
    get AsOfDate(): Date {
        return this.Get('AsOfDate');
    }
    set AsOfDate(value: Date) {
        this.Set('AsOfDate', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC created timestamp.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC updated timestamp.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Optional link to the related company record.
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: PeriodStartDate
    * * Display Name: Period Start Date
    * * SQL Data Type: date
    * * Description: Start of the comparison window (activity is compared for PeriodStartDate..AsOfDate; balances as of AsOfDate). NULL = fiscal-year-to-date (legacy runs).
    */
    get PeriodStartDate(): Date | null {
        return this.Get('PeriodStartDate');
    }
    set PeriodStartDate(value: Date | null) {
        this.Set('PeriodStartDate', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(20)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Complete
    *   * Error
    *   * Pending
    *   * Warning
    * * Description: Pending (created, not yet compared), Complete (all Owned accounts within tolerance and readiness gates passed), Warning (an Owned account is outside tolerance or a readiness gate failed), Error (the GL pull failed).
    */
    get Status(): 'Complete' | 'Error' | 'Pending' | 'Warning' {
        return this.Get('Status');
    }
    set Status(value: 'Complete' | 'Error' | 'Pending' | 'Warning') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: Summary
    * * Display Name: Summary
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON: per-account comparison rows (GL vs SoundPost buckets, classification, status) and readiness gate results for this run.
    */
    get Summary(): string | null {
        return this.Get('Summary');
    }
    set Summary(value: string | null) {
        this.Set('Summary', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Report Output AR Agings - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputARAging
 * * Base View: vwReportOutputARAgings
 * * @description Precomputed A/R aging balances by order, GL account, and location for a given report run.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output AR Agings')
export class soundpostReportOutputARAgingEntity extends BaseEntity<soundpostReportOutputARAgingEntityType> {
    /**
    * Loads the Report Output AR Agings record from the database
    * @param ID: string - primary key value to load the Report Output AR Agings record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputARAgingEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReportRunID
    * * Display Name: Report Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Report Runs (vwReportRuns.ID)
    */
    get ReportRunID(): string {
        return this.Get('ReportRunID');
    }
    set ReportRunID(value: string) {
        this.Set('ReportRunID', value);
    }

    /**
    * * Field Name: OrderID
    * * Display Name: Order ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderID(): string | null {
        return this.Get('OrderID');
    }
    set OrderID(value: string | null) {
        this.Set('OrderID', value);
    }

    /**
    * * Field Name: OrderNumber
    * * Display Name: Order Number
    * * SQL Data Type: varchar(50)
    */
    get OrderNumber(): string {
        return this.Get('OrderNumber');
    }
    set OrderNumber(value: string) {
        this.Set('OrderNumber', value);
    }

    /**
    * * Field Name: OrderDate
    * * Display Name: Order Date
    * * SQL Data Type: datetimeoffset
    */
    get OrderDate(): Date {
        return this.Get('OrderDate');
    }
    set OrderDate(value: Date) {
        this.Set('OrderDate', value);
    }

    /**
    * * Field Name: OrderTotal
    * * Display Name: Order Total
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    */
    get OrderTotal(): number {
        return this.Get('OrderTotal');
    }
    set OrderTotal(value: number) {
        this.Set('OrderTotal', value);
    }

    /**
    * * Field Name: OrderStatus
    * * Display Name: Order Status
    * * SQL Data Type: varchar(50)
    */
    get OrderStatus(): string | null {
        return this.Get('OrderStatus');
    }
    set OrderStatus(value: string | null) {
        this.Set('OrderStatus', value);
    }

    /**
    * * Field Name: Integration
    * * Display Name: Integration
    * * SQL Data Type: varchar(100)
    */
    get Integration(): string | null {
        return this.Get('Integration');
    }
    set Integration(value: string | null) {
        this.Set('Integration', value);
    }

    /**
    * * Field Name: DueDate
    * * Display Name: Due Date
    * * SQL Data Type: datetimeoffset
    */
    get DueDate(): Date | null {
        return this.Get('DueDate');
    }
    set DueDate(value: Date | null) {
        this.Set('DueDate', value);
    }

    /**
    * * Field Name: DaysAged
    * * Display Name: Days Aged
    * * SQL Data Type: int
    */
    get DaysAged(): number {
        return this.Get('DaysAged');
    }
    set DaysAged(value: number) {
        this.Set('DaysAged', value);
    }

    /**
    * * Field Name: Account
    * * Display Name: Account
    * * SQL Data Type: nvarchar(255)
    */
    get Account(): string | null {
        return this.Get('Account');
    }
    set Account(value: string | null) {
        this.Set('Account', value);
    }

    /**
    * * Field Name: CustomerName
    * * Display Name: Customer Name
    * * SQL Data Type: nvarchar(255)
    */
    get CustomerName(): string | null {
        return this.Get('CustomerName');
    }
    set CustomerName(value: string | null) {
        this.Set('CustomerName', value);
    }

    /**
    * * Field Name: GLAccountNumber
    * * Display Name: GL Account Number
    * * SQL Data Type: varchar(50)
    */
    get GLAccountNumber(): string {
        return this.Get('GLAccountNumber');
    }
    set GLAccountNumber(value: string) {
        this.Set('GLAccountNumber', value);
    }

    /**
    * * Field Name: GLAccountName
    * * Display Name: GL Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get GLAccountName(): string | null {
        return this.Get('GLAccountName');
    }
    set GLAccountName(value: string | null) {
        this.Set('GLAccountName', value);
    }

    /**
    * * Field Name: Location
    * * Display Name: Location
    * * SQL Data Type: nvarchar(255)
    */
    get Location(): string | null {
        return this.Get('Location');
    }
    set Location(value: string | null) {
        this.Set('Location', value);
    }

    /**
    * * Field Name: Balance
    * * Display Name: Balance
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Total outstanding A/R balance for this row (sum of all aging buckets).
    */
    get Balance(): number {
        return this.Get('Balance');
    }
    set Balance(value: number) {
        this.Set('Balance', value);
    }

    /**
    * * Field Name: Bucket0Amount
    * * Display Name: Bucket 0 Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Aging bucket 0 amount (e.g., Not Due).
    */
    get Bucket0Amount(): number {
        return this.Get('Bucket0Amount');
    }
    set Bucket0Amount(value: number) {
        this.Set('Bucket0Amount', value);
    }

    /**
    * * Field Name: Bucket1Amount
    * * Display Name: Bucket 1 Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Aging bucket 1 amount (e.g., 0–30 days).
    */
    get Bucket1Amount(): number {
        return this.Get('Bucket1Amount');
    }
    set Bucket1Amount(value: number) {
        this.Set('Bucket1Amount', value);
    }

    /**
    * * Field Name: Bucket2Amount
    * * Display Name: Bucket 2 Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Aging bucket 2 amount (e.g., 31–60 days).
    */
    get Bucket2Amount(): number {
        return this.Get('Bucket2Amount');
    }
    set Bucket2Amount(value: number) {
        this.Set('Bucket2Amount', value);
    }

    /**
    * * Field Name: Bucket3Amount
    * * Display Name: Bucket 3 Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Aging bucket 3 amount (e.g., 61–90 days).
    */
    get Bucket3Amount(): number {
        return this.Get('Bucket3Amount');
    }
    set Bucket3Amount(value: number) {
        this.Set('Bucket3Amount', value);
    }

    /**
    * * Field Name: Bucket4Amount
    * * Display Name: Bucket 4 Amount
    * * SQL Data Type: decimal(18, 2)
    * * Default Value: 0
    * * Description: Aging bucket 4 amount (e.g., 90+ days).
    */
    get Bucket4Amount(): number {
        return this.Get('Bucket4Amount');
    }
    set Bucket4Amount(value: number) {
        this.Set('Bucket4Amount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Link to CompanyIntegration for external URL resolution (e.g., WooCommerce, Pheedloop).
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Report Output Account Activity Summaries - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputAccountActivitySummary
 * * Base View: vwReportOutputAccountActivitySummaries
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Account Activity Summaries')
export class soundpostReportOutputAccountActivitySummaryEntity extends BaseEntity<soundpostReportOutputAccountActivitySummaryEntityType> {
    /**
    * Loads the Report Output Account Activity Summaries record from the database
    * @param ID: string - primary key value to load the Report Output Account Activity Summaries record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputAccountActivitySummaryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: AccountingPeriodID
    * * Display Name: Accounting Period ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)
    */
    get AccountingPeriodID(): string {
        return this.Get('AccountingPeriodID');
    }
    set AccountingPeriodID(value: string) {
        this.Set('AccountingPeriodID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: LocationDimensionCode
    * * Display Name: Location Dimension Code
    * * SQL Data Type: nvarchar(50)
    */
    get LocationDimensionCode(): string | null {
        return this.Get('LocationDimensionCode');
    }
    set LocationDimensionCode(value: string | null) {
        this.Set('LocationDimensionCode', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    */
    get Company(): string {
        return this.Get('Company');
    }
    set Company(value: string) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: AccountingPeriodName
    * * Display Name: Accounting Period Name
    * * SQL Data Type: nvarchar(50)
    */
    get AccountingPeriodName(): string {
        return this.Get('AccountingPeriodName');
    }
    set AccountingPeriodName(value: string) {
        this.Set('AccountingPeriodName', value);
    }

    /**
    * * Field Name: AccountNumber
    * * Display Name: Account Number
    * * SQL Data Type: nvarchar(50)
    */
    get AccountNumber(): string {
        return this.Get('AccountNumber');
    }
    set AccountNumber(value: string) {
        this.Set('AccountNumber', value);
    }

    /**
    * * Field Name: GLAccountName
    * * Display Name: GL Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get GLAccountName(): string {
        return this.Get('GLAccountName');
    }
    set GLAccountName(value: string) {
        this.Set('GLAccountName', value);
    }

    /**
    * * Field Name: GLAccountType
    * * Display Name: GL Account Type
    * * SQL Data Type: nvarchar(50)
    */
    get GLAccountType(): string {
        return this.Get('GLAccountType');
    }
    set GLAccountType(value: string) {
        this.Set('GLAccountType', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
    set CompanyIntegration(value: string | null) {
        this.Set('CompanyIntegration', value);
    }

    /**
    * * Field Name: LocationDimensionName
    * * Display Name: Location Dimension Name
    * * SQL Data Type: nvarchar(255)
    */
    get LocationDimensionName(): string | null {
        return this.Get('LocationDimensionName');
    }
    set LocationDimensionName(value: string | null) {
        this.Set('LocationDimensionName', value);
    }

    /**
    * * Field Name: TotalDebits
    * * Display Name: Total Debits
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get TotalDebits(): number {
        return this.Get('TotalDebits');
    }
    set TotalDebits(value: number) {
        this.Set('TotalDebits', value);
    }

    /**
    * * Field Name: TotalCredits
    * * Display Name: Total Credits
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get TotalCredits(): number {
        return this.Get('TotalCredits');
    }
    set TotalCredits(value: number) {
        this.Set('TotalCredits', value);
    }

    /**
    * * Field Name: NetActivity
    * * Display Name: Net Activity
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get NetActivity(): number {
        return this.Get('NetActivity');
    }
    set NetActivity(value: number) {
        this.Set('NetActivity', value);
    }

    /**
    * * Field Name: TransactionCount
    * * Display Name: Transaction Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get TransactionCount(): number {
        return this.Get('TransactionCount');
    }
    set TransactionCount(value: number) {
        this.Set('TransactionCount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string {
        return this.Get('Company_Virtual');
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string {
        return this.Get('GLAccount');
    }
}


/**
 * Report Output Accounting Entry Lines - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputAccountingEntryLine
 * * Base View: vwReportOutputAccountingEntryLines
 * * @description Denormalized reporting table for accounting entry line data. Pre-computes complex joins for fast dashboard queries. Refreshed via stored procedure.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Accounting Entry Lines')
export class soundpostReportOutputAccountingEntryLineEntity extends BaseEntity<soundpostReportOutputAccountingEntryLineEntityType> {
    /**
    * Loads the Report Output Accounting Entry Lines record from the database
    * @param ID: string - primary key value to load the Report Output Accounting Entry Lines record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputAccountingEntryLineEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AccountingEntryID
    * * Display Name: Accounting Entry ID
    * * SQL Data Type: uniqueidentifier
    */
    get AccountingEntryID(): string {
        return this.Get('AccountingEntryID');
    }
    set AccountingEntryID(value: string) {
        this.Set('AccountingEntryID', value);
    }

    /**
    * * Field Name: AccountingEntryLineID
    * * Display Name: Accounting Entry Line ID
    * * SQL Data Type: uniqueidentifier
    */
    get AccountingEntryLineID(): string {
        return this.Get('AccountingEntryLineID');
    }
    set AccountingEntryLineID(value: string) {
        this.Set('AccountingEntryLineID', value);
    }

    /**
    * * Field Name: EntryNumber
    * * Display Name: Entry Number
    * * SQL Data Type: nvarchar(50)
    */
    get EntryNumber(): string | null {
        return this.Get('EntryNumber');
    }
    set EntryNumber(value: string | null) {
        this.Set('EntryNumber', value);
    }

    /**
    * * Field Name: EntryDate
    * * Display Name: Entry Date
    * * SQL Data Type: datetimeoffset
    */
    get EntryDate(): Date | null {
        return this.Get('EntryDate');
    }
    set EntryDate(value: Date | null) {
        this.Set('EntryDate', value);
    }

    /**
    * * Field Name: EntryDescription
    * * Display Name: Entry Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get EntryDescription(): string | null {
        return this.Get('EntryDescription');
    }
    set EntryDescription(value: string | null) {
        this.Set('EntryDescription', value);
    }

    /**
    * * Field Name: SourceType
    * * Display Name: Source Type
    * * SQL Data Type: nvarchar(50)
    */
    get SourceType(): string | null {
        return this.Get('SourceType');
    }
    set SourceType(value: string | null) {
        this.Set('SourceType', value);
    }

    /**
    * * Field Name: SourceID
    * * Display Name: Source ID
    * * SQL Data Type: uniqueidentifier
    */
    get SourceID(): string | null {
        return this.Get('SourceID');
    }
    set SourceID(value: string | null) {
        this.Set('SourceID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    */
    get GLAccountID(): string | null {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string | null) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: AccountNumber
    * * Display Name: Account Number
    * * SQL Data Type: nvarchar(50)
    */
    get AccountNumber(): string | null {
        return this.Get('AccountNumber');
    }
    set AccountNumber(value: string | null) {
        this.Set('AccountNumber', value);
    }

    /**
    * * Field Name: GLAccountName
    * * Display Name: GL Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get GLAccountName(): string | null {
        return this.Get('GLAccountName');
    }
    set GLAccountName(value: string | null) {
        this.Set('GLAccountName', value);
    }

    /**
    * * Field Name: GLAccountType
    * * Display Name: GL Account Type
    * * SQL Data Type: nvarchar(50)
    */
    get GLAccountType(): string | null {
        return this.Get('GLAccountType');
    }
    set GLAccountType(value: string | null) {
        this.Set('GLAccountType', value);
    }

    /**
    * * Field Name: DebitAmount
    * * Display Name: Debit Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get DebitAmount(): number | null {
        return this.Get('DebitAmount');
    }
    set DebitAmount(value: number | null) {
        this.Set('DebitAmount', value);
    }

    /**
    * * Field Name: CreditAmount
    * * Display Name: Credit Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get CreditAmount(): number | null {
        return this.Get('CreditAmount');
    }
    set CreditAmount(value: number | null) {
        this.Set('CreditAmount', value);
    }

    /**
    * * Field Name: NetAmount
    * * Display Name: Net Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get NetAmount(): number | null {
        return this.Get('NetAmount');
    }
    set NetAmount(value: number | null) {
        this.Set('NetAmount', value);
    }

    /**
    * * Field Name: LineDescription
    * * Display Name: Line Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get LineDescription(): string | null {
        return this.Get('LineDescription');
    }
    set LineDescription(value: string | null) {
        this.Set('LineDescription', value);
    }

    /**
    * * Field Name: LocationDimensionCode
    * * Display Name: Location Dimension Code
    * * SQL Data Type: nvarchar(50)
    */
    get LocationDimensionCode(): string | null {
        return this.Get('LocationDimensionCode');
    }
    set LocationDimensionCode(value: string | null) {
        this.Set('LocationDimensionCode', value);
    }

    /**
    * * Field Name: LocationDimensionName
    * * Display Name: Location Dimension Name
    * * SQL Data Type: nvarchar(255)
    */
    get LocationDimensionName(): string | null {
        return this.Get('LocationDimensionName');
    }
    set LocationDimensionName(value: string | null) {
        this.Set('LocationDimensionName', value);
    }

    /**
    * * Field Name: DepartmentDimensionCode
    * * Display Name: Department Dimension Code
    * * SQL Data Type: nvarchar(50)
    */
    get DepartmentDimensionCode(): string | null {
        return this.Get('DepartmentDimensionCode');
    }
    set DepartmentDimensionCode(value: string | null) {
        this.Set('DepartmentDimensionCode', value);
    }

    /**
    * * Field Name: DepartmentDimensionName
    * * Display Name: Department Dimension Name
    * * SQL Data Type: nvarchar(255)
    */
    get DepartmentDimensionName(): string | null {
        return this.Get('DepartmentDimensionName');
    }
    set DepartmentDimensionName(value: string | null) {
        this.Set('DepartmentDimensionName', value);
    }

    /**
    * * Field Name: ProjectDimensionCode
    * * Display Name: Project Dimension Code
    * * SQL Data Type: nvarchar(50)
    */
    get ProjectDimensionCode(): string | null {
        return this.Get('ProjectDimensionCode');
    }
    set ProjectDimensionCode(value: string | null) {
        this.Set('ProjectDimensionCode', value);
    }

    /**
    * * Field Name: ProjectDimensionName
    * * Display Name: Project Dimension Name
    * * SQL Data Type: nvarchar(255)
    */
    get ProjectDimensionName(): string | null {
        return this.Get('ProjectDimensionName');
    }
    set ProjectDimensionName(value: string | null) {
        this.Set('ProjectDimensionName', value);
    }

    /**
    * * Field Name: ClassDimensionCode
    * * Display Name: Class Dimension Code
    * * SQL Data Type: nvarchar(50)
    */
    get ClassDimensionCode(): string | null {
        return this.Get('ClassDimensionCode');
    }
    set ClassDimensionCode(value: string | null) {
        this.Set('ClassDimensionCode', value);
    }

    /**
    * * Field Name: ClassDimensionName
    * * Display Name: Class Dimension Name
    * * SQL Data Type: nvarchar(255)
    */
    get ClassDimensionName(): string | null {
        return this.Get('ClassDimensionName');
    }
    set ClassDimensionName(value: string | null) {
        this.Set('ClassDimensionName', value);
    }

    /**
    * * Field Name: PostingGroupID
    * * Display Name: Posting Group ID
    * * SQL Data Type: uniqueidentifier
    */
    get PostingGroupID(): string | null {
        return this.Get('PostingGroupID');
    }
    set PostingGroupID(value: string | null) {
        this.Set('PostingGroupID', value);
    }

    /**
    * * Field Name: PostingNumber
    * * Display Name: Posting Number
    * * SQL Data Type: nvarchar(50)
    */
    get PostingNumber(): string | null {
        return this.Get('PostingNumber');
    }
    set PostingNumber(value: string | null) {
        this.Set('PostingNumber', value);
    }

    /**
    * * Field Name: PostingDate
    * * Display Name: Posting Date
    * * SQL Data Type: datetimeoffset
    */
    get PostingDate(): Date | null {
        return this.Get('PostingDate');
    }
    set PostingDate(value: Date | null) {
        this.Set('PostingDate', value);
    }

    /**
    * * Field Name: JENumber
    * * Display Name: JE Number
    * * SQL Data Type: nvarchar(100)
    */
    get JENumber(): string | null {
        return this.Get('JENumber');
    }
    set JENumber(value: string | null) {
        this.Set('JENumber', value);
    }

    /**
    * * Field Name: AccountingPeriodID
    * * Display Name: Accounting Period ID
    * * SQL Data Type: uniqueidentifier
    * * Description: The accounting period this entry belongs to (from PostingGroup).
    */
    get AccountingPeriodID(): string | null {
        return this.Get('AccountingPeriodID');
    }
    set AccountingPeriodID(value: string | null) {
        this.Set('AccountingPeriodID', value);
    }

    /**
    * * Field Name: AccountingPeriodName
    * * Display Name: Accounting Period Name
    * * SQL Data Type: nvarchar(50)
    * * Description: Display name of the accounting period (e.g., January 2025).
    */
    get AccountingPeriodName(): string | null {
        return this.Get('AccountingPeriodName');
    }
    set AccountingPeriodName(value: string | null) {
        this.Set('AccountingPeriodName', value);
    }

    /**
    * * Field Name: AccountingPeriodStartDate
    * * Display Name: Accounting Period Start Date
    * * SQL Data Type: datetimeoffset
    */
    get AccountingPeriodStartDate(): Date | null {
        return this.Get('AccountingPeriodStartDate');
    }
    set AccountingPeriodStartDate(value: Date | null) {
        this.Set('AccountingPeriodStartDate', value);
    }

    /**
    * * Field Name: AccountingPeriodEndDate
    * * Display Name: Accounting Period End Date
    * * SQL Data Type: datetimeoffset
    */
    get AccountingPeriodEndDate(): Date | null {
        return this.Get('AccountingPeriodEndDate');
    }
    set AccountingPeriodEndDate(value: Date | null) {
        this.Set('AccountingPeriodEndDate', value);
    }

    /**
    * * Field Name: AccountingPeriodIsClosed
    * * Display Name: Accounting Period Is Closed
    * * SQL Data Type: bit
    * * Description: Whether the accounting period has been closed.
    */
    get AccountingPeriodIsClosed(): boolean | null {
        return this.Get('AccountingPeriodIsClosed');
    }
    set AccountingPeriodIsClosed(value: boolean | null) {
        this.Set('AccountingPeriodIsClosed', value);
    }

    /**
    * * Field Name: OrderID
    * * Display Name: Order ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderID(): string | null {
        return this.Get('OrderID');
    }
    set OrderID(value: string | null) {
        this.Set('OrderID', value);
    }

    /**
    * * Field Name: OrderNumber
    * * Display Name: Order Number
    * * SQL Data Type: nvarchar(50)
    */
    get OrderNumber(): string | null {
        return this.Get('OrderNumber');
    }
    set OrderNumber(value: string | null) {
        this.Set('OrderNumber', value);
    }

    /**
    * * Field Name: OrderDate
    * * Display Name: Order Date
    * * SQL Data Type: datetimeoffset
    */
    get OrderDate(): Date | null {
        return this.Get('OrderDate');
    }
    set OrderDate(value: Date | null) {
        this.Set('OrderDate', value);
    }

    /**
    * * Field Name: CustomerName
    * * Display Name: Customer Name
    * * SQL Data Type: nvarchar(255)
    */
    get CustomerName(): string | null {
        return this.Get('CustomerName');
    }
    set CustomerName(value: string | null) {
        this.Set('CustomerName', value);
    }

    /**
    * * Field Name: AgreementCode
    * * Display Name: Agreement Code
    * * SQL Data Type: nvarchar(50)
    */
    get AgreementCode(): string | null {
        return this.Get('AgreementCode');
    }
    set AgreementCode(value: string | null) {
        this.Set('AgreementCode', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
    set Company(value: string | null) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
    set CompanyIntegration(value: string | null) {
        this.Set('CompanyIntegration', value);
    }

    /**
    * * Field Name: ProductID
    * * Display Name: Product ID
    * * SQL Data Type: uniqueidentifier
    */
    get ProductID(): string | null {
        return this.Get('ProductID');
    }
    set ProductID(value: string | null) {
        this.Set('ProductID', value);
    }

    /**
    * * Field Name: ProductExternalSystemRecordID
    * * Display Name: Product External System Record ID
    * * SQL Data Type: nvarchar(255)
    */
    get ProductExternalSystemRecordID(): string | null {
        return this.Get('ProductExternalSystemRecordID');
    }
    set ProductExternalSystemRecordID(value: string | null) {
        this.Set('ProductExternalSystemRecordID', value);
    }

    /**
    * * Field Name: SKU
    * * Display Name: SKU
    * * SQL Data Type: nvarchar(100)
    */
    get SKU(): string | null {
        return this.Get('SKU');
    }
    set SKU(value: string | null) {
        this.Set('SKU', value);
    }

    /**
    * * Field Name: ProductName
    * * Display Name: Product Name
    * * SQL Data Type: nvarchar(255)
    */
    get ProductName(): string | null {
        return this.Get('ProductName');
    }
    set ProductName(value: string | null) {
        this.Set('ProductName', value);
    }

    /**
    * * Field Name: ProductCategory
    * * Display Name: Product Category
    * * SQL Data Type: nvarchar(100)
    */
    get ProductCategory(): string | null {
        return this.Get('ProductCategory');
    }
    set ProductCategory(value: string | null) {
        this.Set('ProductCategory', value);
    }

    /**
    * * Field Name: ProductType
    * * Display Name: Product Type
    * * SQL Data Type: nvarchar(100)
    */
    get ProductType(): string | null {
        return this.Get('ProductType');
    }
    set ProductType(value: string | null) {
        this.Set('ProductType', value);
    }

    /**
    * * Field Name: ProductRevenueRecognitionDate
    * * Display Name: Product Revenue Recognition Date
    * * SQL Data Type: datetimeoffset
    */
    get ProductRevenueRecognitionDate(): Date | null {
        return this.Get('ProductRevenueRecognitionDate');
    }
    set ProductRevenueRecognitionDate(value: Date | null) {
        this.Set('ProductRevenueRecognitionDate', value);
    }

    /**
    * * Field Name: ParentProductID
    * * Display Name: Parent Product ID
    * * SQL Data Type: uniqueidentifier
    */
    get ParentProductID(): string | null {
        return this.Get('ParentProductID');
    }
    set ParentProductID(value: string | null) {
        this.Set('ParentProductID', value);
    }

    /**
    * * Field Name: ParentProductExternalSystemRecordID
    * * Display Name: Parent Product External System Record ID
    * * SQL Data Type: nvarchar(255)
    */
    get ParentProductExternalSystemRecordID(): string | null {
        return this.Get('ParentProductExternalSystemRecordID');
    }
    set ParentProductExternalSystemRecordID(value: string | null) {
        this.Set('ParentProductExternalSystemRecordID', value);
    }

    /**
    * * Field Name: ParentProductSKU
    * * Display Name: Parent Product SKU
    * * SQL Data Type: nvarchar(100)
    */
    get ParentProductSKU(): string | null {
        return this.Get('ParentProductSKU');
    }
    set ParentProductSKU(value: string | null) {
        this.Set('ParentProductSKU', value);
    }

    /**
    * * Field Name: ParentProductName
    * * Display Name: Parent Product Name
    * * SQL Data Type: nvarchar(255)
    */
    get ParentProductName(): string | null {
        return this.Get('ParentProductName');
    }
    set ParentProductName(value: string | null) {
        this.Set('ParentProductName', value);
    }

    /**
    * * Field Name: ParentProductCategory
    * * Display Name: Parent Product Category
    * * SQL Data Type: nvarchar(100)
    */
    get ParentProductCategory(): string | null {
        return this.Get('ParentProductCategory');
    }
    set ParentProductCategory(value: string | null) {
        this.Set('ParentProductCategory', value);
    }

    /**
    * * Field Name: ParentProductType
    * * Display Name: Parent Product Type
    * * SQL Data Type: nvarchar(100)
    */
    get ParentProductType(): string | null {
        return this.Get('ParentProductType');
    }
    set ParentProductType(value: string | null) {
        this.Set('ParentProductType', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: AgreementAccountName
    * * Display Name: Agreement Account Name
    * * SQL Data Type: nvarchar(255)
    * * Description: The account name from the Agreement associated with this entry line, used for grouping and filtering in the Agreement GL Activity Dashboard
    */
    get AgreementAccountName(): string | null {
        return this.Get('AgreementAccountName');
    }
    set AgreementAccountName(value: string | null) {
        this.Set('AgreementAccountName', value);
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string | null {
        return this.Get('Company_Virtual');
    }
}


/**
 * Report Output Agreement Balances - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputAgreementBalance
 * * Base View: vwReportOutputAgreementBalances
 * * @description Pre-aggregated agreement balance data for sponsorship management dashboard. Balance = TotalAllocated + TotalAdjustment - TotalUsage. Refreshed via stored procedure.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Agreement Balances')
export class soundpostReportOutputAgreementBalanceEntity extends BaseEntity<soundpostReportOutputAgreementBalanceEntityType> {
    /**
    * Loads the Report Output Agreement Balances record from the database
    * @param ID: string - primary key value to load the Report Output Agreement Balances record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputAgreementBalanceEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AgreementID
    * * Display Name: Agreement ID
    * * SQL Data Type: uniqueidentifier
    */
    get AgreementID(): string {
        return this.Get('AgreementID');
    }
    set AgreementID(value: string) {
        this.Set('AgreementID', value);
    }

    /**
    * * Field Name: AgreementCode
    * * Display Name: Agreement Code
    * * SQL Data Type: nvarchar(50)
    */
    get AgreementCode(): string | null {
        return this.Get('AgreementCode');
    }
    set AgreementCode(value: string | null) {
        this.Set('AgreementCode', value);
    }

    /**
    * * Field Name: AccountName
    * * Display Name: Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get AccountName(): string | null {
        return this.Get('AccountName');
    }
    set AccountName(value: string | null) {
        this.Set('AccountName', value);
    }

    /**
    * * Field Name: ContactName
    * * Display Name: Contact Name
    * * SQL Data Type: nvarchar(255)
    */
    get ContactName(): string | null {
        return this.Get('ContactName');
    }
    set ContactName(value: string | null) {
        this.Set('ContactName', value);
    }

    /**
    * * Field Name: AgreementType
    * * Display Name: Agreement Type
    * * SQL Data Type: nvarchar(200)
    */
    get AgreementType(): string | null {
        return this.Get('AgreementType');
    }
    set AgreementType(value: string | null) {
        this.Set('AgreementType', value);
    }

    /**
    * * Field Name: AgreementStatus
    * * Display Name: Agreement Status
    * * SQL Data Type: nvarchar(200)
    */
    get AgreementStatus(): string | null {
        return this.Get('AgreementStatus');
    }
    set AgreementStatus(value: string | null) {
        this.Set('AgreementStatus', value);
    }

    /**
    * * Field Name: StartDate
    * * Display Name: Start Date
    * * SQL Data Type: datetimeoffset
    */
    get StartDate(): Date | null {
        return this.Get('StartDate');
    }
    set StartDate(value: Date | null) {
        this.Set('StartDate', value);
    }

    /**
    * * Field Name: EndDate
    * * Display Name: End Date
    * * SQL Data Type: datetimeoffset
    */
    get EndDate(): Date | null {
        return this.Get('EndDate');
    }
    set EndDate(value: Date | null) {
        this.Set('EndDate', value);
    }

    /**
    * * Field Name: TotalAllocated
    * * Display Name: Total Allocated
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    * * Description: Sum of AllocatedAmount from all AgreementLines for this agreement.
    */
    get TotalAllocated(): number {
        return this.Get('TotalAllocated');
    }
    set TotalAllocated(value: number) {
        this.Set('TotalAllocated', value);
    }

    /**
    * * Field Name: TotalAdjustment
    * * Display Name: Total Adjustment
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    * * Description: Sum of AdjustmentAmount from all AgreementLines for this agreement.
    */
    get TotalAdjustment(): number {
        return this.Get('TotalAdjustment');
    }
    set TotalAdjustment(value: number) {
        this.Set('TotalAdjustment', value);
    }

    /**
    * * Field Name: TotalUsage
    * * Display Name: Total Usage
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    * * Description: Sum of LineAmount from all Orders with matching AgreementCode.
    */
    get TotalUsage(): number {
        return this.Get('TotalUsage');
    }
    set TotalUsage(value: number) {
        this.Set('TotalUsage', value);
    }

    /**
    * * Field Name: CurrentBalance
    * * Display Name: Current Balance
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    * * Description: Calculated balance: TotalAllocated + TotalAdjustment - TotalUsage.
    */
    get CurrentBalance(): number {
        return this.Get('CurrentBalance');
    }
    set CurrentBalance(value: number) {
        this.Set('CurrentBalance', value);
    }

    /**
    * * Field Name: UsageThisMonth
    * * Display Name: Usage This Month
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get UsageThisMonth(): number | null {
        return this.Get('UsageThisMonth');
    }
    set UsageThisMonth(value: number | null) {
        this.Set('UsageThisMonth', value);
    }

    /**
    * * Field Name: UsageLastMonth
    * * Display Name: Usage Last Month
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get UsageLastMonth(): number | null {
        return this.Get('UsageLastMonth');
    }
    set UsageLastMonth(value: number | null) {
        this.Set('UsageLastMonth', value);
    }

    /**
    * * Field Name: AgreementLineCount
    * * Display Name: Agreement Line Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get AgreementLineCount(): number | null {
        return this.Get('AgreementLineCount');
    }
    set AgreementLineCount(value: number | null) {
        this.Set('AgreementLineCount', value);
    }

    /**
    * * Field Name: OrderCount
    * * Display Name: Order Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get OrderCount(): number | null {
        return this.Get('OrderCount');
    }
    set OrderCount(value: number | null) {
        this.Set('OrderCount', value);
    }

    /**
    * * Field Name: LastCalculatedAt
    * * Display Name: Last Calculated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get LastCalculatedAt(): Date {
        return this.Get('LastCalculatedAt');
    }
    set LastCalculatedAt(value: Date) {
        this.Set('LastCalculatedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Report Output Agreement GL Activity Summaries - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputAgreementGLActivitySummary
 * * Base View: vwReportOutputAgreementGLActivitySummaries
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Agreement GL Activity Summaries')
export class soundpostReportOutputAgreementGLActivitySummaryEntity extends BaseEntity<soundpostReportOutputAgreementGLActivitySummaryEntityType> {
    /**
    * Loads the Report Output Agreement GL Activity Summaries record from the database
    * @param ID: string - primary key value to load the Report Output Agreement GL Activity Summaries record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputAgreementGLActivitySummaryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: AccountingPeriodID
    * * Display Name: Accounting Period ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)
    */
    get AccountingPeriodID(): string {
        return this.Get('AccountingPeriodID');
    }
    set AccountingPeriodID(value: string) {
        this.Set('AccountingPeriodID', value);
    }

    /**
    * * Field Name: AgreementCode
    * * Display Name: Agreement Code
    * * SQL Data Type: nvarchar(50)
    */
    get AgreementCode(): string {
        return this.Get('AgreementCode');
    }
    set AgreementCode(value: string) {
        this.Set('AgreementCode', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: LocationDimensionCode
    * * Display Name: Location Dimension Code
    * * SQL Data Type: nvarchar(50)
    */
    get LocationDimensionCode(): string | null {
        return this.Get('LocationDimensionCode');
    }
    set LocationDimensionCode(value: string | null) {
        this.Set('LocationDimensionCode', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    */
    get Company(): string {
        return this.Get('Company');
    }
    set Company(value: string) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: AccountingPeriodName
    * * Display Name: Accounting Period Name
    * * SQL Data Type: nvarchar(50)
    */
    get AccountingPeriodName(): string {
        return this.Get('AccountingPeriodName');
    }
    set AccountingPeriodName(value: string) {
        this.Set('AccountingPeriodName', value);
    }

    /**
    * * Field Name: AgreementAccountName
    * * Display Name: Agreement Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get AgreementAccountName(): string | null {
        return this.Get('AgreementAccountName');
    }
    set AgreementAccountName(value: string | null) {
        this.Set('AgreementAccountName', value);
    }

    /**
    * * Field Name: AccountNumber
    * * Display Name: Account Number
    * * SQL Data Type: nvarchar(50)
    */
    get AccountNumber(): string {
        return this.Get('AccountNumber');
    }
    set AccountNumber(value: string) {
        this.Set('AccountNumber', value);
    }

    /**
    * * Field Name: GLAccountName
    * * Display Name: GL Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get GLAccountName(): string {
        return this.Get('GLAccountName');
    }
    set GLAccountName(value: string) {
        this.Set('GLAccountName', value);
    }

    /**
    * * Field Name: GLAccountType
    * * Display Name: GL Account Type
    * * SQL Data Type: nvarchar(50)
    */
    get GLAccountType(): string {
        return this.Get('GLAccountType');
    }
    set GLAccountType(value: string) {
        this.Set('GLAccountType', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
    set CompanyIntegration(value: string | null) {
        this.Set('CompanyIntegration', value);
    }

    /**
    * * Field Name: LocationDimensionName
    * * Display Name: Location Dimension Name
    * * SQL Data Type: nvarchar(255)
    */
    get LocationDimensionName(): string | null {
        return this.Get('LocationDimensionName');
    }
    set LocationDimensionName(value: string | null) {
        this.Set('LocationDimensionName', value);
    }

    /**
    * * Field Name: TotalDebits
    * * Display Name: Total Debits
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get TotalDebits(): number {
        return this.Get('TotalDebits');
    }
    set TotalDebits(value: number) {
        this.Set('TotalDebits', value);
    }

    /**
    * * Field Name: TotalCredits
    * * Display Name: Total Credits
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get TotalCredits(): number {
        return this.Get('TotalCredits');
    }
    set TotalCredits(value: number) {
        this.Set('TotalCredits', value);
    }

    /**
    * * Field Name: NetActivity
    * * Display Name: Net Activity
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get NetActivity(): number {
        return this.Get('NetActivity');
    }
    set NetActivity(value: number) {
        this.Set('NetActivity', value);
    }

    /**
    * * Field Name: TransactionCount
    * * Display Name: Transaction Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get TransactionCount(): number {
        return this.Get('TransactionCount');
    }
    set TransactionCount(value: number) {
        this.Set('TransactionCount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string {
        return this.Get('Company_Virtual');
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string {
        return this.Get('GLAccount');
    }
}


/**
 * Report Output Deferred Revenue By Schedules - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputDeferredRevenueBySchedule
 * * Base View: vwReportOutputDeferredRevenueBySchedules
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Deferred Revenue By Schedules')
export class soundpostReportOutputDeferredRevenueByScheduleEntity extends BaseEntity<soundpostReportOutputDeferredRevenueByScheduleEntityType> {
    /**
    * Loads the Report Output Deferred Revenue By Schedules record from the database
    * @param ID: string - primary key value to load the Report Output Deferred Revenue By Schedules record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputDeferredRevenueByScheduleEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReportRunID
    * * Display Name: Report Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Report Runs (vwReportRuns.ID)
    */
    get ReportRunID(): string {
        return this.Get('ReportRunID');
    }
    set ReportRunID(value: string) {
        this.Set('ReportRunID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: AccountNumber
    * * Display Name: Account Number
    * * SQL Data Type: nvarchar(50)
    */
    get AccountNumber(): string {
        return this.Get('AccountNumber');
    }
    set AccountNumber(value: string) {
        this.Set('AccountNumber', value);
    }

    /**
    * * Field Name: GLAccountName
    * * Display Name: GL Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get GLAccountName(): string {
        return this.Get('GLAccountName');
    }
    set GLAccountName(value: string) {
        this.Set('GLAccountName', value);
    }

    /**
    * * Field Name: AccountingScheduleID
    * * Display Name: Accounting Schedule ID
    * * SQL Data Type: uniqueidentifier
    */
    get AccountingScheduleID(): string | null {
        return this.Get('AccountingScheduleID');
    }
    set AccountingScheduleID(value: string | null) {
        this.Set('AccountingScheduleID', value);
    }

    /**
    * * Field Name: RecognitionMethod
    * * Display Name: Recognition Method
    * * SQL Data Type: nvarchar(50)
    */
    get RecognitionMethod(): string | null {
        return this.Get('RecognitionMethod');
    }
    set RecognitionMethod(value: string | null) {
        this.Set('RecognitionMethod', value);
    }

    /**
    * * Field Name: ScheduleStartDate
    * * Display Name: Schedule Start Date
    * * SQL Data Type: datetimeoffset
    */
    get ScheduleStartDate(): Date | null {
        return this.Get('ScheduleStartDate');
    }
    set ScheduleStartDate(value: Date | null) {
        this.Set('ScheduleStartDate', value);
    }

    /**
    * * Field Name: ScheduleEndDate
    * * Display Name: Schedule End Date
    * * SQL Data Type: datetimeoffset
    */
    get ScheduleEndDate(): Date | null {
        return this.Get('ScheduleEndDate');
    }
    set ScheduleEndDate(value: Date | null) {
        this.Set('ScheduleEndDate', value);
    }

    /**
    * * Field Name: ScheduleTotalAmount
    * * Display Name: Schedule Total Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get ScheduleTotalAmount(): number | null {
        return this.Get('ScheduleTotalAmount');
    }
    set ScheduleTotalAmount(value: number | null) {
        this.Set('ScheduleTotalAmount', value);
    }

    /**
    * * Field Name: DeferredBalance
    * * Display Name: Deferred Balance
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get DeferredBalance(): number {
        return this.Get('DeferredBalance');
    }
    set DeferredBalance(value: number) {
        this.Set('DeferredBalance', value);
    }

    /**
    * * Field Name: RecognizedAmount
    * * Display Name: Recognized Amount
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognizedAmount(): number {
        return this.Get('RecognizedAmount');
    }
    set RecognizedAmount(value: number) {
        this.Set('RecognizedAmount', value);
    }

    /**
    * * Field Name: UnpostedEntryCount
    * * Display Name: Unposted Entry Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get UnpostedEntryCount(): number {
        return this.Get('UnpostedEntryCount');
    }
    set UnpostedEntryCount(value: number) {
        this.Set('UnpostedEntryCount', value);
    }

    /**
    * * Field Name: OrderID
    * * Display Name: Order ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderID(): string | null {
        return this.Get('OrderID');
    }
    set OrderID(value: string | null) {
        this.Set('OrderID', value);
    }

    /**
    * * Field Name: OrderNumber
    * * Display Name: Order Number
    * * SQL Data Type: nvarchar(100)
    */
    get OrderNumber(): string | null {
        return this.Get('OrderNumber');
    }
    set OrderNumber(value: string | null) {
        this.Set('OrderNumber', value);
    }

    /**
    * * Field Name: OrderDate
    * * Display Name: Order Date
    * * SQL Data Type: datetimeoffset
    */
    get OrderDate(): Date | null {
        return this.Get('OrderDate');
    }
    set OrderDate(value: Date | null) {
        this.Set('OrderDate', value);
    }

    /**
    * * Field Name: CustomerName
    * * Display Name: Customer Name
    * * SQL Data Type: nvarchar(500)
    */
    get CustomerName(): string | null {
        return this.Get('CustomerName');
    }
    set CustomerName(value: string | null) {
        this.Set('CustomerName', value);
    }

    /**
    * * Field Name: ProductID
    * * Display Name: Product ID
    * * SQL Data Type: uniqueidentifier
    */
    get ProductID(): string | null {
        return this.Get('ProductID');
    }
    set ProductID(value: string | null) {
        this.Set('ProductID', value);
    }

    /**
    * * Field Name: ProductName
    * * Display Name: Product Name
    * * SQL Data Type: nvarchar(500)
    */
    get ProductName(): string | null {
        return this.Get('ProductName');
    }
    set ProductName(value: string | null) {
        this.Set('ProductName', value);
    }

    /**
    * * Field Name: ProductCategory
    * * Display Name: Product Category
    * * SQL Data Type: nvarchar(100)
    */
    get ProductCategory(): string | null {
        return this.Get('ProductCategory');
    }
    set ProductCategory(value: string | null) {
        this.Set('ProductCategory', value);
    }

    /**
    * * Field Name: LocationCode
    * * Display Name: Location Code
    * * SQL Data Type: nvarchar(50)
    */
    get LocationCode(): string | null {
        return this.Get('LocationCode');
    }
    set LocationCode(value: string | null) {
        this.Set('LocationCode', value);
    }

    /**
    * * Field Name: DepartmentCode
    * * Display Name: Department Code
    * * SQL Data Type: nvarchar(50)
    */
    get DepartmentCode(): string | null {
        return this.Get('DepartmentCode');
    }
    set DepartmentCode(value: string | null) {
        this.Set('DepartmentCode', value);
    }

    /**
    * * Field Name: ClassCode
    * * Display Name: Class Code
    * * SQL Data Type: nvarchar(50)
    */
    get ClassCode(): string | null {
        return this.Get('ClassCode');
    }
    set ClassCode(value: string | null) {
        this.Set('ClassCode', value);
    }

    /**
    * * Field Name: ProjectCode
    * * Display Name: Project Code
    * * SQL Data Type: nvarchar(50)
    */
    get ProjectCode(): string | null {
        return this.Get('ProjectCode');
    }
    set ProjectCode(value: string | null) {
        this.Set('ProjectCode', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
    set Company(value: string | null) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: RecognitionThisMonth
    * * Display Name: Recognition This Month
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionThisMonth(): number {
        return this.Get('RecognitionThisMonth');
    }
    set RecognitionThisMonth(value: number) {
        this.Set('RecognitionThisMonth', value);
    }

    /**
    * * Field Name: RecognitionRestOfYear
    * * Display Name: Recognition Rest Of Year
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionRestOfYear(): number {
        return this.Get('RecognitionRestOfYear');
    }
    set RecognitionRestOfYear(value: number) {
        this.Set('RecognitionRestOfYear', value);
    }

    /**
    * * Field Name: RecognitionBeyond
    * * Display Name: Recognition Beyond
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionBeyond(): number {
        return this.Get('RecognitionBeyond');
    }
    set RecognitionBeyond(value: number) {
        this.Set('RecognitionBeyond', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: RecognitionNextYear
    * * Display Name: Recognition Next Year
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionNextYear(): number {
        return this.Get('RecognitionNextYear');
    }
    set RecognitionNextYear(value: number) {
        this.Set('RecognitionNextYear', value);
    }

    /**
    * * Field Name: AgreementCode
    * * Display Name: Agreement Code
    * * SQL Data Type: nvarchar(100)
    */
    get AgreementCode(): string | null {
        return this.Get('AgreementCode');
    }
    set AgreementCode(value: string | null) {
        this.Set('AgreementCode', value);
    }

    /**
    * * Field Name: RecognitionUnscheduled
    * * Display Name: Recognition Unscheduled
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionUnscheduled(): number {
        return this.Get('RecognitionUnscheduled');
    }
    set RecognitionUnscheduled(value: number) {
        this.Set('RecognitionUnscheduled', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string | null {
        return this.Get('Company_Virtual');
    }
}


/**
 * Report Output Deferred Revenue Details - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputDeferredRevenueDetail
 * * Base View: vwReportOutputDeferredRevenueDetails
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Deferred Revenue Details')
export class soundpostReportOutputDeferredRevenueDetailEntity extends BaseEntity<soundpostReportOutputDeferredRevenueDetailEntityType> {
    /**
    * Loads the Report Output Deferred Revenue Details record from the database
    * @param ID: string - primary key value to load the Report Output Deferred Revenue Details record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputDeferredRevenueDetailEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReportRunID
    * * Display Name: Report Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Report Runs (vwReportRuns.ID)
    */
    get ReportRunID(): string {
        return this.Get('ReportRunID');
    }
    set ReportRunID(value: string) {
        this.Set('ReportRunID', value);
    }

    /**
    * * Field Name: AccountingEntryLineID
    * * Display Name: Accounting Entry Line ID
    * * SQL Data Type: uniqueidentifier
    */
    get AccountingEntryLineID(): string {
        return this.Get('AccountingEntryLineID');
    }
    set AccountingEntryLineID(value: string) {
        this.Set('AccountingEntryLineID', value);
    }

    /**
    * * Field Name: AccountingEntryID
    * * Display Name: Accounting Entry ID
    * * SQL Data Type: uniqueidentifier
    */
    get AccountingEntryID(): string {
        return this.Get('AccountingEntryID');
    }
    set AccountingEntryID(value: string) {
        this.Set('AccountingEntryID', value);
    }

    /**
    * * Field Name: EntryNumber
    * * Display Name: Entry Number
    * * SQL Data Type: nvarchar(50)
    */
    get EntryNumber(): string | null {
        return this.Get('EntryNumber');
    }
    set EntryNumber(value: string | null) {
        this.Set('EntryNumber', value);
    }

    /**
    * * Field Name: EntryDate
    * * Display Name: Entry Date
    * * SQL Data Type: datetimeoffset
    */
    get EntryDate(): Date | null {
        return this.Get('EntryDate');
    }
    set EntryDate(value: Date | null) {
        this.Set('EntryDate', value);
    }

    /**
    * * Field Name: EntryDescription
    * * Display Name: Entry Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get EntryDescription(): string | null {
        return this.Get('EntryDescription');
    }
    set EntryDescription(value: string | null) {
        this.Set('EntryDescription', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: AccountNumber
    * * Display Name: Account Number
    * * SQL Data Type: nvarchar(50)
    */
    get AccountNumber(): string {
        return this.Get('AccountNumber');
    }
    set AccountNumber(value: string) {
        this.Set('AccountNumber', value);
    }

    /**
    * * Field Name: GLAccountName
    * * Display Name: GL Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get GLAccountName(): string {
        return this.Get('GLAccountName');
    }
    set GLAccountName(value: string) {
        this.Set('GLAccountName', value);
    }

    /**
    * * Field Name: DebitAmount
    * * Display Name: Debit Amount
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get DebitAmount(): number {
        return this.Get('DebitAmount');
    }
    set DebitAmount(value: number) {
        this.Set('DebitAmount', value);
    }

    /**
    * * Field Name: CreditAmount
    * * Display Name: Credit Amount
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get CreditAmount(): number {
        return this.Get('CreditAmount');
    }
    set CreditAmount(value: number) {
        this.Set('CreditAmount', value);
    }

    /**
    * * Field Name: NetAmount
    * * Display Name: Net Amount
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get NetAmount(): number {
        return this.Get('NetAmount');
    }
    set NetAmount(value: number) {
        this.Set('NetAmount', value);
    }

    /**
    * * Field Name: AccountingScheduleID
    * * Display Name: Accounting Schedule ID
    * * SQL Data Type: uniqueidentifier
    */
    get AccountingScheduleID(): string | null {
        return this.Get('AccountingScheduleID');
    }
    set AccountingScheduleID(value: string | null) {
        this.Set('AccountingScheduleID', value);
    }

    /**
    * * Field Name: ScheduleStartDate
    * * Display Name: Schedule Start Date
    * * SQL Data Type: datetimeoffset
    */
    get ScheduleStartDate(): Date | null {
        return this.Get('ScheduleStartDate');
    }
    set ScheduleStartDate(value: Date | null) {
        this.Set('ScheduleStartDate', value);
    }

    /**
    * * Field Name: ScheduleEndDate
    * * Display Name: Schedule End Date
    * * SQL Data Type: datetimeoffset
    */
    get ScheduleEndDate(): Date | null {
        return this.Get('ScheduleEndDate');
    }
    set ScheduleEndDate(value: Date | null) {
        this.Set('ScheduleEndDate', value);
    }

    /**
    * * Field Name: ScheduleTotalAmount
    * * Display Name: Schedule Total Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get ScheduleTotalAmount(): number | null {
        return this.Get('ScheduleTotalAmount');
    }
    set ScheduleTotalAmount(value: number | null) {
        this.Set('ScheduleTotalAmount', value);
    }

    /**
    * * Field Name: OrderID
    * * Display Name: Order ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderID(): string | null {
        return this.Get('OrderID');
    }
    set OrderID(value: string | null) {
        this.Set('OrderID', value);
    }

    /**
    * * Field Name: OrderNumber
    * * Display Name: Order Number
    * * SQL Data Type: nvarchar(100)
    */
    get OrderNumber(): string | null {
        return this.Get('OrderNumber');
    }
    set OrderNumber(value: string | null) {
        this.Set('OrderNumber', value);
    }

    /**
    * * Field Name: OrderDate
    * * Display Name: Order Date
    * * SQL Data Type: datetimeoffset
    */
    get OrderDate(): Date | null {
        return this.Get('OrderDate');
    }
    set OrderDate(value: Date | null) {
        this.Set('OrderDate', value);
    }

    /**
    * * Field Name: CustomerName
    * * Display Name: Customer Name
    * * SQL Data Type: nvarchar(500)
    */
    get CustomerName(): string | null {
        return this.Get('CustomerName');
    }
    set CustomerName(value: string | null) {
        this.Set('CustomerName', value);
    }

    /**
    * * Field Name: ProductID
    * * Display Name: Product ID
    * * SQL Data Type: uniqueidentifier
    */
    get ProductID(): string | null {
        return this.Get('ProductID');
    }
    set ProductID(value: string | null) {
        this.Set('ProductID', value);
    }

    /**
    * * Field Name: ProductName
    * * Display Name: Product Name
    * * SQL Data Type: nvarchar(500)
    */
    get ProductName(): string | null {
        return this.Get('ProductName');
    }
    set ProductName(value: string | null) {
        this.Set('ProductName', value);
    }

    /**
    * * Field Name: LocationCode
    * * Display Name: Location Code
    * * SQL Data Type: nvarchar(50)
    */
    get LocationCode(): string | null {
        return this.Get('LocationCode');
    }
    set LocationCode(value: string | null) {
        this.Set('LocationCode', value);
    }

    /**
    * * Field Name: DepartmentCode
    * * Display Name: Department Code
    * * SQL Data Type: nvarchar(50)
    */
    get DepartmentCode(): string | null {
        return this.Get('DepartmentCode');
    }
    set DepartmentCode(value: string | null) {
        this.Set('DepartmentCode', value);
    }

    /**
    * * Field Name: ClassCode
    * * Display Name: Class Code
    * * SQL Data Type: nvarchar(50)
    */
    get ClassCode(): string | null {
        return this.Get('ClassCode');
    }
    set ClassCode(value: string | null) {
        this.Set('ClassCode', value);
    }

    /**
    * * Field Name: ProjectCode
    * * Display Name: Project Code
    * * SQL Data Type: nvarchar(50)
    */
    get ProjectCode(): string | null {
        return this.Get('ProjectCode');
    }
    set ProjectCode(value: string | null) {
        this.Set('ProjectCode', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
    set Company(value: string | null) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: ProductCategory
    * * Display Name: Product Category
    * * SQL Data Type: nvarchar(100)
    */
    get ProductCategory(): string | null {
        return this.Get('ProductCategory');
    }
    set ProductCategory(value: string | null) {
        this.Set('ProductCategory', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: OrderLinePostingGroupID
    * * Display Name: Order Line Posting Group ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderLinePostingGroupID(): string | null {
        return this.Get('OrderLinePostingGroupID');
    }
    set OrderLinePostingGroupID(value: string | null) {
        this.Set('OrderLinePostingGroupID', value);
    }

    /**
    * * Field Name: OrderLinePostingNumber
    * * Display Name: Order Line Posting Number
    * * SQL Data Type: nvarchar(50)
    */
    get OrderLinePostingNumber(): string | null {
        return this.Get('OrderLinePostingNumber');
    }
    set OrderLinePostingNumber(value: string | null) {
        this.Set('OrderLinePostingNumber', value);
    }

    /**
    * * Field Name: OrderLinePostingDate
    * * Display Name: Order Line Posting Date
    * * SQL Data Type: datetimeoffset
    */
    get OrderLinePostingDate(): Date | null {
        return this.Get('OrderLinePostingDate');
    }
    set OrderLinePostingDate(value: Date | null) {
        this.Set('OrderLinePostingDate', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string | null {
        return this.Get('Company_Virtual');
    }
}


/**
 * Report Output Deferred Revenue Summaries - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputDeferredRevenueSummary
 * * Base View: vwReportOutputDeferredRevenueSummaries
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Deferred Revenue Summaries')
export class soundpostReportOutputDeferredRevenueSummaryEntity extends BaseEntity<soundpostReportOutputDeferredRevenueSummaryEntityType> {
    /**
    * Loads the Report Output Deferred Revenue Summaries record from the database
    * @param ID: string - primary key value to load the Report Output Deferred Revenue Summaries record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputDeferredRevenueSummaryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReportRunID
    * * Display Name: Report Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Report Runs (vwReportRuns.ID)
    */
    get ReportRunID(): string {
        return this.Get('ReportRunID');
    }
    set ReportRunID(value: string) {
        this.Set('ReportRunID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: AccountNumber
    * * Display Name: Account Number
    * * SQL Data Type: nvarchar(50)
    */
    get AccountNumber(): string {
        return this.Get('AccountNumber');
    }
    set AccountNumber(value: string) {
        this.Set('AccountNumber', value);
    }

    /**
    * * Field Name: GLAccountName
    * * Display Name: GL Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get GLAccountName(): string {
        return this.Get('GLAccountName');
    }
    set GLAccountName(value: string) {
        this.Set('GLAccountName', value);
    }

    /**
    * * Field Name: DeferredBalance
    * * Display Name: Deferred Balance
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get DeferredBalance(): number {
        return this.Get('DeferredBalance');
    }
    set DeferredBalance(value: number) {
        this.Set('DeferredBalance', value);
    }

    /**
    * * Field Name: EntryCount
    * * Display Name: Entry Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get EntryCount(): number {
        return this.Get('EntryCount');
    }
    set EntryCount(value: number) {
        this.Set('EntryCount', value);
    }

    /**
    * * Field Name: ActiveScheduleCount
    * * Display Name: Active Schedule Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get ActiveScheduleCount(): number {
        return this.Get('ActiveScheduleCount');
    }
    set ActiveScheduleCount(value: number) {
        this.Set('ActiveScheduleCount', value);
    }

    /**
    * * Field Name: RecognitionThisMonth
    * * Display Name: Recognition This Month
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionThisMonth(): number {
        return this.Get('RecognitionThisMonth');
    }
    set RecognitionThisMonth(value: number) {
        this.Set('RecognitionThisMonth', value);
    }

    /**
    * * Field Name: RecognitionRestOfYear
    * * Display Name: Recognition Rest Of Year
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionRestOfYear(): number {
        return this.Get('RecognitionRestOfYear');
    }
    set RecognitionRestOfYear(value: number) {
        this.Set('RecognitionRestOfYear', value);
    }

    /**
    * * Field Name: RecognitionBeyond
    * * Display Name: Recognition Beyond
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionBeyond(): number {
        return this.Get('RecognitionBeyond');
    }
    set RecognitionBeyond(value: number) {
        this.Set('RecognitionBeyond', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: RecognitionNextYear
    * * Display Name: Recognition Next Year
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionNextYear(): number {
        return this.Get('RecognitionNextYear');
    }
    set RecognitionNextYear(value: number) {
        this.Set('RecognitionNextYear', value);
    }

    /**
    * * Field Name: RecognitionUnscheduled
    * * Display Name: Recognition Unscheduled
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get RecognitionUnscheduled(): number {
        return this.Get('RecognitionUnscheduled');
    }
    set RecognitionUnscheduled(value: number) {
        this.Set('RecognitionUnscheduled', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Report Output Order Payment Lines - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputOrderPaymentLine
 * * Base View: vwReportOutputOrderPaymentLines
 * * @description Denormalized reporting table for order and payment data. Pre-computes complex joins for fast dashboard queries. Refreshed via stored procedure.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Order Payment Lines')
export class soundpostReportOutputOrderPaymentLineEntity extends BaseEntity<soundpostReportOutputOrderPaymentLineEntityType> {
    /**
    * Loads the Report Output Order Payment Lines record from the database
    * @param ID: string - primary key value to load the Report Output Order Payment Lines record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputOrderPaymentLineEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrderID
    * * Display Name: Order ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderID(): string {
        return this.Get('OrderID');
    }
    set OrderID(value: string) {
        this.Set('OrderID', value);
    }

    /**
    * * Field Name: OrderLineID
    * * Display Name: Order Line ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderLineID(): string {
        return this.Get('OrderLineID');
    }
    set OrderLineID(value: string) {
        this.Set('OrderLineID', value);
    }

    /**
    * * Field Name: OrderNumber
    * * Display Name: Order Number
    * * SQL Data Type: nvarchar(50)
    */
    get OrderNumber(): string | null {
        return this.Get('OrderNumber');
    }
    set OrderNumber(value: string | null) {
        this.Set('OrderNumber', value);
    }

    /**
    * * Field Name: OrderExternalSystemRecordID
    * * Display Name: Order External System Record ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrderExternalSystemRecordID(): string | null {
        return this.Get('OrderExternalSystemRecordID');
    }
    set OrderExternalSystemRecordID(value: string | null) {
        this.Set('OrderExternalSystemRecordID', value);
    }

    /**
    * * Field Name: OrderDate
    * * Display Name: Order Date
    * * SQL Data Type: datetimeoffset
    */
    get OrderDate(): Date | null {
        return this.Get('OrderDate');
    }
    set OrderDate(value: Date | null) {
        this.Set('OrderDate', value);
    }

    /**
    * * Field Name: DueDate
    * * Display Name: Due Date
    * * SQL Data Type: datetimeoffset
    */
    get DueDate(): Date | null {
        return this.Get('DueDate');
    }
    set DueDate(value: Date | null) {
        this.Set('DueDate', value);
    }

    /**
    * * Field Name: OrderStatus
    * * Display Name: Order Status
    * * SQL Data Type: nvarchar(50)
    */
    get OrderStatus(): string | null {
        return this.Get('OrderStatus');
    }
    set OrderStatus(value: string | null) {
        this.Set('OrderStatus', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
    set Company(value: string | null) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: CustomerName
    * * Display Name: Customer Name
    * * SQL Data Type: nvarchar(255)
    */
    get CustomerName(): string | null {
        return this.Get('CustomerName');
    }
    set CustomerName(value: string | null) {
        this.Set('CustomerName', value);
    }

    /**
    * * Field Name: OrderPaymentMethod
    * * Display Name: Order Payment Method
    * * SQL Data Type: nvarchar(50)
    */
    get OrderPaymentMethod(): string | null {
        return this.Get('OrderPaymentMethod');
    }
    set OrderPaymentMethod(value: string | null) {
        this.Set('OrderPaymentMethod', value);
    }

    /**
    * * Field Name: AgreementCode
    * * Display Name: Agreement Code
    * * SQL Data Type: nvarchar(50)
    */
    get AgreementCode(): string | null {
        return this.Get('AgreementCode');
    }
    set AgreementCode(value: string | null) {
        this.Set('AgreementCode', value);
    }

    /**
    * * Field Name: TotalAmount
    * * Display Name: Total Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get TotalAmount(): number | null {
        return this.Get('TotalAmount');
    }
    set TotalAmount(value: number | null) {
        this.Set('TotalAmount', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
    set CompanyIntegration(value: string | null) {
        this.Set('CompanyIntegration', value);
    }

    /**
    * * Field Name: LineNumber
    * * Display Name: Line Number
    * * SQL Data Type: int
    */
    get LineNumber(): number | null {
        return this.Get('LineNumber');
    }
    set LineNumber(value: number | null) {
        this.Set('LineNumber', value);
    }

    /**
    * * Field Name: OrderLineExternalSystemRecordID
    * * Display Name: Order Line External System Record ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrderLineExternalSystemRecordID(): string | null {
        return this.Get('OrderLineExternalSystemRecordID');
    }
    set OrderLineExternalSystemRecordID(value: string | null) {
        this.Set('OrderLineExternalSystemRecordID', value);
    }

    /**
    * * Field Name: UnitPrice
    * * Display Name: Unit Price
    * * SQL Data Type: decimal(19, 4)
    */
    get UnitPrice(): number | null {
        return this.Get('UnitPrice');
    }
    set UnitPrice(value: number | null) {
        this.Set('UnitPrice', value);
    }

    /**
    * * Field Name: Quantity
    * * Display Name: Quantity
    * * SQL Data Type: decimal(19, 4)
    */
    get Quantity(): number | null {
        return this.Get('Quantity');
    }
    set Quantity(value: number | null) {
        this.Set('Quantity', value);
    }

    /**
    * * Field Name: OrderLineAmount
    * * Display Name: Order Line Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get OrderLineAmount(): number | null {
        return this.Get('OrderLineAmount');
    }
    set OrderLineAmount(value: number | null) {
        this.Set('OrderLineAmount', value);
    }

    /**
    * * Field Name: OrderLineDiscountAmount
    * * Display Name: Order Line Discount Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get OrderLineDiscountAmount(): number | null {
        return this.Get('OrderLineDiscountAmount');
    }
    set OrderLineDiscountAmount(value: number | null) {
        this.Set('OrderLineDiscountAmount', value);
    }

    /**
    * * Field Name: OrderLineTaxAmount
    * * Display Name: Order Line Tax Amount
    * * SQL Data Type: decimal(19, 4)
    */
    get OrderLineTaxAmount(): number | null {
        return this.Get('OrderLineTaxAmount');
    }
    set OrderLineTaxAmount(value: number | null) {
        this.Set('OrderLineTaxAmount', value);
    }

    /**
    * * Field Name: RecognitionStartDate
    * * Display Name: Recognition Start Date
    * * SQL Data Type: datetimeoffset
    */
    get RecognitionStartDate(): Date | null {
        return this.Get('RecognitionStartDate');
    }
    set RecognitionStartDate(value: Date | null) {
        this.Set('RecognitionStartDate', value);
    }

    /**
    * * Field Name: RecognitionEndDate
    * * Display Name: Recognition End Date
    * * SQL Data Type: datetimeoffset
    */
    get RecognitionEndDate(): Date | null {
        return this.Get('RecognitionEndDate');
    }
    set RecognitionEndDate(value: Date | null) {
        this.Set('RecognitionEndDate', value);
    }

    /**
    * * Field Name: ProductID
    * * Display Name: Product ID
    * * SQL Data Type: uniqueidentifier
    */
    get ProductID(): string | null {
        return this.Get('ProductID');
    }
    set ProductID(value: string | null) {
        this.Set('ProductID', value);
    }

    /**
    * * Field Name: ProductExternalSystemRecordID
    * * Display Name: Product External System Record ID
    * * SQL Data Type: nvarchar(255)
    */
    get ProductExternalSystemRecordID(): string | null {
        return this.Get('ProductExternalSystemRecordID');
    }
    set ProductExternalSystemRecordID(value: string | null) {
        this.Set('ProductExternalSystemRecordID', value);
    }

    /**
    * * Field Name: SKU
    * * Display Name: SKU
    * * SQL Data Type: nvarchar(100)
    */
    get SKU(): string | null {
        return this.Get('SKU');
    }
    set SKU(value: string | null) {
        this.Set('SKU', value);
    }

    /**
    * * Field Name: ProductName
    * * Display Name: Product Name
    * * SQL Data Type: nvarchar(255)
    */
    get ProductName(): string | null {
        return this.Get('ProductName');
    }
    set ProductName(value: string | null) {
        this.Set('ProductName', value);
    }

    /**
    * * Field Name: ProductCategory
    * * Display Name: Product Category
    * * SQL Data Type: nvarchar(100)
    */
    get ProductCategory(): string | null {
        return this.Get('ProductCategory');
    }
    set ProductCategory(value: string | null) {
        this.Set('ProductCategory', value);
    }

    /**
    * * Field Name: ProductType
    * * Display Name: Product Type
    * * SQL Data Type: nvarchar(100)
    */
    get ProductType(): string | null {
        return this.Get('ProductType');
    }
    set ProductType(value: string | null) {
        this.Set('ProductType', value);
    }

    /**
    * * Field Name: ProductRevenueRecognitionDate
    * * Display Name: Product Revenue Recognition Date
    * * SQL Data Type: datetimeoffset
    */
    get ProductRevenueRecognitionDate(): Date | null {
        return this.Get('ProductRevenueRecognitionDate');
    }
    set ProductRevenueRecognitionDate(value: Date | null) {
        this.Set('ProductRevenueRecognitionDate', value);
    }

    /**
    * * Field Name: ParentProductID
    * * Display Name: Parent Product ID
    * * SQL Data Type: uniqueidentifier
    */
    get ParentProductID(): string | null {
        return this.Get('ParentProductID');
    }
    set ParentProductID(value: string | null) {
        this.Set('ParentProductID', value);
    }

    /**
    * * Field Name: ParentProductExternalSystemRecordID
    * * Display Name: Parent Product External System Record ID
    * * SQL Data Type: nvarchar(255)
    */
    get ParentProductExternalSystemRecordID(): string | null {
        return this.Get('ParentProductExternalSystemRecordID');
    }
    set ParentProductExternalSystemRecordID(value: string | null) {
        this.Set('ParentProductExternalSystemRecordID', value);
    }

    /**
    * * Field Name: ParentProductSKU
    * * Display Name: Parent Product SKU
    * * SQL Data Type: nvarchar(100)
    */
    get ParentProductSKU(): string | null {
        return this.Get('ParentProductSKU');
    }
    set ParentProductSKU(value: string | null) {
        this.Set('ParentProductSKU', value);
    }

    /**
    * * Field Name: ParentProductName
    * * Display Name: Parent Product Name
    * * SQL Data Type: nvarchar(255)
    */
    get ParentProductName(): string | null {
        return this.Get('ParentProductName');
    }
    set ParentProductName(value: string | null) {
        this.Set('ParentProductName', value);
    }

    /**
    * * Field Name: ParentProductCategory
    * * Display Name: Parent Product Category
    * * SQL Data Type: nvarchar(100)
    */
    get ParentProductCategory(): string | null {
        return this.Get('ParentProductCategory');
    }
    set ParentProductCategory(value: string | null) {
        this.Set('ParentProductCategory', value);
    }

    /**
    * * Field Name: ParentProductType
    * * Display Name: Parent Product Type
    * * SQL Data Type: nvarchar(100)
    */
    get ParentProductType(): string | null {
        return this.Get('ParentProductType');
    }
    set ParentProductType(value: string | null) {
        this.Set('ParentProductType', value);
    }

    /**
    * * Field Name: PaymentID
    * * Display Name: Payment ID
    * * SQL Data Type: uniqueidentifier
    */
    get PaymentID(): string | null {
        return this.Get('PaymentID');
    }
    set PaymentID(value: string | null) {
        this.Set('PaymentID', value);
    }

    /**
    * * Field Name: PaymentLineID
    * * Display Name: Payment Line ID
    * * SQL Data Type: uniqueidentifier
    */
    get PaymentLineID(): string | null {
        return this.Get('PaymentLineID');
    }
    set PaymentLineID(value: string | null) {
        this.Set('PaymentLineID', value);
    }

    /**
    * * Field Name: PaymentNumber
    * * Display Name: Payment Number
    * * SQL Data Type: nvarchar(50)
    */
    get PaymentNumber(): string | null {
        return this.Get('PaymentNumber');
    }
    set PaymentNumber(value: string | null) {
        this.Set('PaymentNumber', value);
    }

    /**
    * * Field Name: PaymentDate
    * * Display Name: Payment Date
    * * SQL Data Type: datetimeoffset
    */
    get PaymentDate(): Date | null {
        return this.Get('PaymentDate');
    }
    set PaymentDate(value: Date | null) {
        this.Set('PaymentDate', value);
    }

    /**
    * * Field Name: PaymentStatus
    * * Display Name: Payment Status
    * * SQL Data Type: nvarchar(50)
    */
    get PaymentStatus(): string | null {
        return this.Get('PaymentStatus');
    }
    set PaymentStatus(value: string | null) {
        this.Set('PaymentStatus', value);
    }

    /**
    * * Field Name: PaymentMethod
    * * Display Name: Payment Method
    * * SQL Data Type: nvarchar(50)
    */
    get PaymentMethod(): string | null {
        return this.Get('PaymentMethod');
    }
    set PaymentMethod(value: string | null) {
        this.Set('PaymentMethod', value);
    }

    /**
    * * Field Name: AmountApplied
    * * Display Name: Amount Applied
    * * SQL Data Type: decimal(19, 4)
    */
    get AmountApplied(): number | null {
        return this.Get('AmountApplied');
    }
    set AmountApplied(value: number | null) {
        this.Set('AmountApplied', value);
    }

    /**
    * * Field Name: ProcessingFee
    * * Display Name: Processing Fee
    * * SQL Data Type: decimal(19, 4)
    */
    get ProcessingFee(): number | null {
        return this.Get('ProcessingFee');
    }
    set ProcessingFee(value: number | null) {
        this.Set('ProcessingFee', value);
    }

    /**
    * * Field Name: OrderLineAccountingEntryID
    * * Display Name: Order Line Accounting Entry ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderLineAccountingEntryID(): string | null {
        return this.Get('OrderLineAccountingEntryID');
    }
    set OrderLineAccountingEntryID(value: string | null) {
        this.Set('OrderLineAccountingEntryID', value);
    }

    /**
    * * Field Name: OrderLinePostingGroupID
    * * Display Name: Order Line Posting Group ID
    * * SQL Data Type: uniqueidentifier
    */
    get OrderLinePostingGroupID(): string | null {
        return this.Get('OrderLinePostingGroupID');
    }
    set OrderLinePostingGroupID(value: string | null) {
        this.Set('OrderLinePostingGroupID', value);
    }

    /**
    * * Field Name: OrderLinePostingNumber
    * * Display Name: Order Line Posting Number
    * * SQL Data Type: nvarchar(50)
    */
    get OrderLinePostingNumber(): string | null {
        return this.Get('OrderLinePostingNumber');
    }
    set OrderLinePostingNumber(value: string | null) {
        this.Set('OrderLinePostingNumber', value);
    }

    /**
    * * Field Name: OrderLinePostingDate
    * * Display Name: Order Line Posting Date
    * * SQL Data Type: datetimeoffset
    */
    get OrderLinePostingDate(): Date | null {
        return this.Get('OrderLinePostingDate');
    }
    set OrderLinePostingDate(value: Date | null) {
        this.Set('OrderLinePostingDate', value);
    }

    /**
    * * Field Name: AccountingPeriodID
    * * Display Name: Accounting Period ID
    * * SQL Data Type: uniqueidentifier
    * * Description: The accounting period this order line posting belongs to.
    */
    get AccountingPeriodID(): string | null {
        return this.Get('AccountingPeriodID');
    }
    set AccountingPeriodID(value: string | null) {
        this.Set('AccountingPeriodID', value);
    }

    /**
    * * Field Name: AccountingPeriodName
    * * Display Name: Accounting Period Name
    * * SQL Data Type: nvarchar(50)
    */
    get AccountingPeriodName(): string | null {
        return this.Get('AccountingPeriodName');
    }
    set AccountingPeriodName(value: string | null) {
        this.Set('AccountingPeriodName', value);
    }

    /**
    * * Field Name: AccountingPeriodStartDate
    * * Display Name: Accounting Period Start Date
    * * SQL Data Type: datetimeoffset
    */
    get AccountingPeriodStartDate(): Date | null {
        return this.Get('AccountingPeriodStartDate');
    }
    set AccountingPeriodStartDate(value: Date | null) {
        this.Set('AccountingPeriodStartDate', value);
    }

    /**
    * * Field Name: AccountingPeriodEndDate
    * * Display Name: Accounting Period End Date
    * * SQL Data Type: datetimeoffset
    */
    get AccountingPeriodEndDate(): Date | null {
        return this.Get('AccountingPeriodEndDate');
    }
    set AccountingPeriodEndDate(value: Date | null) {
        this.Set('AccountingPeriodEndDate', value);
    }

    /**
    * * Field Name: AccountingPeriodIsClosed
    * * Display Name: Accounting Period Is Closed
    * * SQL Data Type: bit
    */
    get AccountingPeriodIsClosed(): boolean | null {
        return this.Get('AccountingPeriodIsClosed');
    }
    set AccountingPeriodIsClosed(value: boolean | null) {
        this.Set('AccountingPeriodIsClosed', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: OrderLineIsReversal
    * * Display Name: Order Line Is Reversal
    * * SQL Data Type: bit
    */
    get OrderLineIsReversal(): boolean | null {
        return this.Get('OrderLineIsReversal');
    }
    set OrderLineIsReversal(value: boolean | null) {
        this.Set('OrderLineIsReversal', value);
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string | null {
        return this.Get('Company_Virtual');
    }
}


/**
 * Report Output Product GL Activity Summaries - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportOutputProductGLActivitySummary
 * * Base View: vwReportOutputProductGLActivitySummaries
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Output Product GL Activity Summaries')
export class soundpostReportOutputProductGLActivitySummaryEntity extends BaseEntity<soundpostReportOutputProductGLActivitySummaryEntityType> {
    /**
    * Loads the Report Output Product GL Activity Summaries record from the database
    * @param ID: string - primary key value to load the Report Output Product GL Activity Summaries record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportOutputProductGLActivitySummaryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: AccountingPeriodID
    * * Display Name: Accounting Period ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)
    */
    get AccountingPeriodID(): string {
        return this.Get('AccountingPeriodID');
    }
    set AccountingPeriodID(value: string) {
        this.Set('AccountingPeriodID', value);
    }

    /**
    * * Field Name: ProductExternalSystemRecordID
    * * Display Name: Product External System Record ID
    * * SQL Data Type: nvarchar(255)
    */
    get ProductExternalSystemRecordID(): string {
        return this.Get('ProductExternalSystemRecordID');
    }
    set ProductExternalSystemRecordID(value: string) {
        this.Set('ProductExternalSystemRecordID', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    */
    get CompanyIntegrationID(): string | null {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string | null) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: LocationDimensionCode
    * * Display Name: Location Dimension Code
    * * SQL Data Type: nvarchar(50)
    */
    get LocationDimensionCode(): string | null {
        return this.Get('LocationDimensionCode');
    }
    set LocationDimensionCode(value: string | null) {
        this.Set('LocationDimensionCode', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(255)
    */
    get Company(): string {
        return this.Get('Company');
    }
    set Company(value: string) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: AccountingPeriodName
    * * Display Name: Accounting Period Name
    * * SQL Data Type: nvarchar(50)
    */
    get AccountingPeriodName(): string {
        return this.Get('AccountingPeriodName');
    }
    set AccountingPeriodName(value: string) {
        this.Set('AccountingPeriodName', value);
    }

    /**
    * * Field Name: ProductName
    * * Display Name: Product Name
    * * SQL Data Type: nvarchar(255)
    */
    get ProductName(): string {
        return this.Get('ProductName');
    }
    set ProductName(value: string) {
        this.Set('ProductName', value);
    }

    /**
    * * Field Name: ProductCategory
    * * Display Name: Product Category
    * * SQL Data Type: nvarchar(100)
    */
    get ProductCategory(): string | null {
        return this.Get('ProductCategory');
    }
    set ProductCategory(value: string | null) {
        this.Set('ProductCategory', value);
    }

    /**
    * * Field Name: AccountNumber
    * * Display Name: Account Number
    * * SQL Data Type: nvarchar(50)
    */
    get AccountNumber(): string {
        return this.Get('AccountNumber');
    }
    set AccountNumber(value: string) {
        this.Set('AccountNumber', value);
    }

    /**
    * * Field Name: GLAccountName
    * * Display Name: GL Account Name
    * * SQL Data Type: nvarchar(255)
    */
    get GLAccountName(): string {
        return this.Get('GLAccountName');
    }
    set GLAccountName(value: string) {
        this.Set('GLAccountName', value);
    }

    /**
    * * Field Name: GLAccountType
    * * Display Name: GL Account Type
    * * SQL Data Type: nvarchar(50)
    */
    get GLAccountType(): string {
        return this.Get('GLAccountType');
    }
    set GLAccountType(value: string) {
        this.Set('GLAccountType', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string | null {
        return this.Get('CompanyIntegration');
    }
    set CompanyIntegration(value: string | null) {
        this.Set('CompanyIntegration', value);
    }

    /**
    * * Field Name: LocationDimensionName
    * * Display Name: Location Dimension Name
    * * SQL Data Type: nvarchar(255)
    */
    get LocationDimensionName(): string | null {
        return this.Get('LocationDimensionName');
    }
    set LocationDimensionName(value: string | null) {
        this.Set('LocationDimensionName', value);
    }

    /**
    * * Field Name: TotalDebits
    * * Display Name: Total Debits
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get TotalDebits(): number {
        return this.Get('TotalDebits');
    }
    set TotalDebits(value: number) {
        this.Set('TotalDebits', value);
    }

    /**
    * * Field Name: TotalCredits
    * * Display Name: Total Credits
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get TotalCredits(): number {
        return this.Get('TotalCredits');
    }
    set TotalCredits(value: number) {
        this.Set('TotalCredits', value);
    }

    /**
    * * Field Name: NetActivity
    * * Display Name: Net Activity
    * * SQL Data Type: decimal(19, 4)
    * * Default Value: 0
    */
    get NetActivity(): number {
        return this.Get('NetActivity');
    }
    set NetActivity(value: number) {
        this.Set('NetActivity', value);
    }

    /**
    * * Field Name: TransactionCount
    * * Display Name: Transaction Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get TransactionCount(): number {
        return this.Get('TransactionCount');
    }
    set TransactionCount(value: number) {
        this.Set('TransactionCount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Company_Virtual
    * * Display Name: Company Virtual
    * * SQL Data Type: nvarchar(50)
    */
    get Company_Virtual(): string {
        return this.Get('Company_Virtual');
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string {
        return this.Get('GLAccount');
    }
}


/**
 * Report Runs - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportRun
 * * Base View: vwReportRuns
 * * @description Execution metadata for report runs, including parameters, run time, and record counts.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Runs')
export class soundpostReportRunEntity extends BaseEntity<soundpostReportRunEntityType> {
    /**
    * Loads the Report Runs record from the database
    * @param ID: string - primary key value to load the Report Runs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportRunEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReportTypeID
    * * Display Name: Report Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Report Types (vwReportTypes.ID)
    */
    get ReportTypeID(): string {
        return this.Get('ReportTypeID');
    }
    set ReportTypeID(value: string) {
        this.Set('ReportTypeID', value);
    }

    /**
    * * Field Name: RunDate
    * * Display Name: Run Date
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get RunDate(): Date {
        return this.Get('RunDate');
    }
    set RunDate(value: Date) {
        this.Set('RunDate', value);
    }

    /**
    * * Field Name: NumRecords
    * * Display Name: Num Records
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get NumRecords(): number {
        return this.Get('NumRecords');
    }
    set NumRecords(value: number) {
        this.Set('NumRecords', value);
    }

    /**
    * * Field Name: AsOfDate
    * * Display Name: As Of Date
    * * SQL Data Type: datetimeoffset
    * * Description: As-of date used when generating the report.
    */
    get AsOfDate(): Date | null {
        return this.Get('AsOfDate');
    }
    set AsOfDate(value: Date | null) {
        this.Set('AsOfDate', value);
    }

    /**
    * * Field Name: StartDate
    * * Display Name: Start Date
    * * SQL Data Type: datetimeoffset
    */
    get StartDate(): Date | null {
        return this.Get('StartDate');
    }
    set StartDate(value: Date | null) {
        this.Set('StartDate', value);
    }

    /**
    * * Field Name: EndDate
    * * Display Name: End Date
    * * SQL Data Type: datetimeoffset
    */
    get EndDate(): Date | null {
        return this.Get('EndDate');
    }
    set EndDate(value: Date | null) {
        this.Set('EndDate', value);
    }

    /**
    * * Field Name: AccountingPeriodID
    * * Display Name: Accounting Period ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Accounting Periods (vwAccountingPeriods.ID)
    */
    get AccountingPeriodID(): string | null {
        return this.Get('AccountingPeriodID');
    }
    set AccountingPeriodID(value: string | null) {
        this.Set('AccountingPeriodID', value);
    }

    /**
    * * Field Name: ReportParametersJson
    * * Display Name: Report Parameters Json
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Additional report parameters serialized as JSON (e.g., filters, client-specific options).
    */
    get ReportParametersJson(): string | null {
        return this.Get('ReportParametersJson');
    }
    set ReportParametersJson(value: string | null) {
        this.Set('ReportParametersJson', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: ReportType
    * * Display Name: Report Type
    * * SQL Data Type: varchar(100)
    */
    get ReportType(): string {
        return this.Get('ReportType');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Report Types - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: ReportType
 * * Base View: vwReportTypes
 * * @description Defines available report types and their output tables.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Report Types')
export class soundpostReportTypeEntity extends BaseEntity<soundpostReportTypeEntityType> {
    /**
    * Loads the Report Types record from the database
    * @param ID: string - primary key value to load the Report Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostReportTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: varchar(100)
    * * Description: Name of the report (A/R Aging, Deferred Revenue, etc.).
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: TargetTable
    * * Display Name: Target Table
    * * SQL Data Type: varchar(128)
    * * Description: Name of the table where rows for this report are stored.
    */
    get TargetTable(): string {
        return this.Get('TargetTable');
    }
    set TargetTable(value: string) {
        this.Set('TargetTable', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Revenue Recognition Rules - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: RevenueRecognitionRule
 * * Base View: vwRevenueRecognitionRules
 * * @description Defines how revenue should be recognized for different products.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Revenue Recognition Rules')
export class soundpostRevenueRecognitionRuleEntity extends BaseEntity<soundpostRevenueRecognitionRuleEntityType> {
    /**
    * Loads the Revenue Recognition Rules record from the database
    * @param ID: string - primary key value to load the Revenue Recognition Rules record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostRevenueRecognitionRuleEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProductID
    * * Display Name: Product ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Products (vwProducts.ID)
    */
    get ProductID(): string {
        return this.Get('ProductID');
    }
    set ProductID(value: string) {
        this.Set('ProductID', value);
    }

    /**
    * * Field Name: RecognitionMethod
    * * Display Name: Recognition Method
    * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * fixed-date
    *   * immediate
    *   * milestone
    *   * percentage-completion
    *   * straight-line
    * * Description: Method of revenue recognition (immediate, straight-line, fixed-date, etc.).
    */
    get RecognitionMethod(): 'fixed-date' | 'immediate' | 'milestone' | 'percentage-completion' | 'straight-line' {
        return this.Get('RecognitionMethod');
    }
    set RecognitionMethod(value: 'fixed-date' | 'immediate' | 'milestone' | 'percentage-completion' | 'straight-line') {
        this.Set('RecognitionMethod', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: PeriodInMonths
    * * Display Name: Period In Months
    * * SQL Data Type: int
    * * Description: Number of months over which to recognize revenue (for straight-line).
    */
    get PeriodInMonths(): number | null {
        return this.Get('PeriodInMonths');
    }
    set PeriodInMonths(value: number | null) {
        this.Set('PeriodInMonths', value);
    }

    /**
    * * Field Name: StartDateOffset
    * * Display Name: Start Date Offset
    * * SQL Data Type: int
    * * Description: Days to offset from order date for recognition start.
    */
    get StartDateOffset(): number | null {
        return this.Get('StartDateOffset');
    }
    set StartDateOffset(value: number | null) {
        this.Set('StartDateOffset', value);
    }

    /**
    * * Field Name: RecognitionDate
    * * Display Name: Recognition Date
    * * SQL Data Type: datetimeoffset
    * * Description: Specific date when revenue should be recognized (for fixed-date).
    */
    get RecognitionDate(): Date {
        return this.Get('RecognitionDate');
    }
    set RecognitionDate(value: Date) {
        this.Set('RecognitionDate', value);
    }

    /**
    * * Field Name: MilestoneConditions
    * * Display Name: Milestone Conditions
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON defining milestone conditions for milestone-based recognition.
    */
    get MilestoneConditions(): string | null {
        return this.Get('MilestoneConditions');
    }
    set MilestoneConditions(value: string | null) {
        this.Set('MilestoneConditions', value);
    }

    /**
    * * Field Name: CompletionCriteria
    * * Display Name: Completion Criteria
    * * SQL Data Type: nvarchar(MAX)
    */
    get CompletionCriteria(): string | null {
        return this.Get('CompletionCriteria');
    }
    set CompletionCriteria(value: string | null) {
        this.Set('CompletionCriteria', value);
    }

    /**
    * * Field Name: Priority
    * * Display Name: Priority
    * * SQL Data Type: int
    * * Default Value: 100
    * * Description: Priority for rule evaluation (lower number = higher priority).
    */
    get Priority(): number {
        return this.Get('Priority');
    }
    set Priority(value: number) {
        this.Set('Priority', value);
    }

    /**
    * * Field Name: Condition
    * * Display Name: Condition
    * * SQL Data Type: nvarchar(500)
    */
    get Condition(): string | null {
        return this.Get('Condition');
    }
    set Condition(value: string | null) {
        this.Set('Condition', value);
    }

    /**
    * * Field Name: EffectiveStartDate
    * * Display Name: Effective Start Date
    * * SQL Data Type: datetimeoffset
    */
    get EffectiveStartDate(): Date | null {
        return this.Get('EffectiveStartDate');
    }
    set EffectiveStartDate(value: Date | null) {
        this.Set('EffectiveStartDate', value);
    }

    /**
    * * Field Name: EffectiveEndDate
    * * Display Name: Effective End Date
    * * SQL Data Type: datetimeoffset
    */
    get EffectiveEndDate(): Date | null {
        return this.Get('EffectiveEndDate');
    }
    set EffectiveEndDate(value: Date | null) {
        this.Set('EffectiveEndDate', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Sync Logs - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: SyncLog
 * * Base View: vwSyncLogs
 * * @description Tracks synchronization runs with source systems.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Sync Logs')
export class soundpostSyncLogEntity extends BaseEntity<soundpostSyncLogEntityType> {
    /**
    * Loads the Sync Logs record from the database
    * @param ID: string - primary key value to load the Sync Logs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostSyncLogEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SourceSystem
    * * Display Name: Source System
    * * SQL Data Type: varchar(50)
    * * Description: Source system being synchronized.
    */
    get SourceSystem(): string {
        return this.Get('SourceSystem');
    }
    set SourceSystem(value: string) {
        this.Set('SourceSystem', value);
    }

    /**
    * * Field Name: EntityType
    * * Display Name: Entity Type
    * * SQL Data Type: varchar(50)
    * * Description: Type of entity being synchronized.
    */
    get EntityType(): string {
        return this.Get('EntityType');
    }
    set EntityType(value: string) {
        this.Set('EntityType', value);
    }

    /**
    * * Field Name: SyncType
    * * Display Name: Sync Type
    * * SQL Data Type: varchar(20)
    * * Default Value: Full
    * * Description: Type of synchronization (Full, Incremental).
    */
    get SyncType(): string {
        return this.Get('SyncType');
    }
    set SyncType(value: string) {
        this.Set('SyncType', value);
    }

    /**
    * * Field Name: StartTime
    * * Display Name: Start Time
    * * SQL Data Type: datetime
    */
    get StartTime(): Date {
        return this.Get('StartTime');
    }
    set StartTime(value: Date) {
        this.Set('StartTime', value);
    }

    /**
    * * Field Name: EndTime
    * * Display Name: End Time
    * * SQL Data Type: datetime
    */
    get EndTime(): Date | null {
        return this.Get('EndTime');
    }
    set EndTime(value: Date | null) {
        this.Set('EndTime', value);
    }

    /**
    * * Field Name: LastSyncDate
    * * Display Name: Last Sync Date
    * * SQL Data Type: datetime
    * * Description: Last successful sync date for incremental syncs.
    */
    get LastSyncDate(): Date | null {
        return this.Get('LastSyncDate');
    }
    set LastSyncDate(value: Date | null) {
        this.Set('LastSyncDate', value);
    }

    /**
    * * Field Name: RecordsRead
    * * Display Name: Records Read
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsRead(): number {
        return this.Get('RecordsRead');
    }
    set RecordsRead(value: number) {
        this.Set('RecordsRead', value);
    }

    /**
    * * Field Name: RecordsCreated
    * * Display Name: Records Created
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsCreated(): number {
        return this.Get('RecordsCreated');
    }
    set RecordsCreated(value: number) {
        this.Set('RecordsCreated', value);
    }

    /**
    * * Field Name: RecordsUpdated
    * * Display Name: Records Updated
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsUpdated(): number {
        return this.Get('RecordsUpdated');
    }
    set RecordsUpdated(value: number) {
        this.Set('RecordsUpdated', value);
    }

    /**
    * * Field Name: RecordsDeleted
    * * Display Name: Records Deleted
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsDeleted(): number {
        return this.Get('RecordsDeleted');
    }
    set RecordsDeleted(value: number) {
        this.Set('RecordsDeleted', value);
    }

    /**
    * * Field Name: RecordsFailed
    * * Display Name: Records Failed
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get RecordsFailed(): number {
        return this.Get('RecordsFailed');
    }
    set RecordsFailed(value: number) {
        this.Set('RecordsFailed', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: varchar(20)
    * * Default Value: Running
    * * Description: Sync status (Running, Completed, Failed).
    */
    get Status(): string {
        return this.Get('Status');
    }
    set Status(value: string) {
        this.Set('Status', value);
    }

    /**
    * * Field Name: ErrorMessage
    * * Display Name: Error Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get ErrorMessage(): string | null {
        return this.Get('ErrorMessage');
    }
    set ErrorMessage(value: string | null) {
        this.Set('ErrorMessage', value);
    }

    /**
    * * Field Name: NextSyncDate
    * * Display Name: Next Sync Date
    * * SQL Data Type: datetime
    */
    get NextSyncDate(): Date | null {
        return this.Get('NextSyncDate');
    }
    set NextSyncDate(value: Date | null) {
        this.Set('NextSyncDate', value);
    }

    /**
    * * Field Name: CreatedDate
    * * Display Name: Created Date
    * * SQL Data Type: datetime
    * * Default Value: getdate()
    */
    get CreatedDate(): Date {
        return this.Get('CreatedDate');
    }
    set CreatedDate(value: Date) {
        this.Set('CreatedDate', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Trial Balances - strongly typed entity sub-class
 * * Schema: soundpost
 * * Base Table: TrialBalance
 * * Base View: vwTrialBalances
 * * @description Balances-only trial balance snapshot per run and integration (source-agnostic).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Trial Balances')
export class soundpostTrialBalanceEntity extends BaseEntity<soundpostTrialBalanceEntityType> {
    /**
    * Loads the Trial Balances record from the database
    * @param ID: string - primary key value to load the Trial Balances record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof soundpostTrialBalanceEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: RunID
    * * Display Name: Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Recon Runs (vwReconRuns.ID)
    * * Description: Reconciliation run ID.
    */
    get RunID(): string {
        return this.Get('RunID');
    }
    set RunID(value: string) {
        this.Set('RunID', value);
    }

    /**
    * * Field Name: CompanyIntegrationID
    * * Display Name: Company Integration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Company Integrations (vwCompanyIntegrations.ID)
    * * Description: Integration instance (e.g., Intacct company).
    */
    get CompanyIntegrationID(): string {
        return this.Get('CompanyIntegrationID');
    }
    set CompanyIntegrationID(value: string) {
        this.Set('CompanyIntegrationID', value);
    }

    /**
    * * Field Name: AsOfDate
    * * Display Name: As Of Date
    * * SQL Data Type: datetimeoffset
    * * Description: As-of date for the balance.
    */
    get AsOfDate(): Date {
        return this.Get('AsOfDate');
    }
    set AsOfDate(value: Date) {
        this.Set('AsOfDate', value);
    }

    /**
    * * Field Name: GLAccountID
    * * Display Name: GL Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: GL Accounts (vwGLAccounts.ID)
    * * Description: GL account reference.
    */
    get GLAccountID(): string {
        return this.Get('GLAccountID');
    }
    set GLAccountID(value: string) {
        this.Set('GLAccountID', value);
    }

    /**
    * * Field Name: EndingBalanceBase
    * * Display Name: Ending Balance Base
    * * SQL Data Type: decimal(18, 2)
    * * Description: Ending balance in base currency.
    */
    get EndingBalanceBase(): number {
        return this.Get('EndingBalanceBase');
    }
    set EndingBalanceBase(value: number) {
        this.Set('EndingBalanceBase', value);
    }

    /**
    * * Field Name: DebitsYTDBase
    * * Display Name: Debits YTD Base
    * * SQL Data Type: decimal(18, 2)
    * * Description: Sum of debits YTD (base).
    */
    get DebitsYTDBase(): number | null {
        return this.Get('DebitsYTDBase');
    }
    set DebitsYTDBase(value: number | null) {
        this.Set('DebitsYTDBase', value);
    }

    /**
    * * Field Name: CreditsYTDBase
    * * Display Name: Credits YTD Base
    * * SQL Data Type: decimal(18, 2)
    * * Description: Sum of credits YTD (base).
    */
    get CreditsYTDBase(): number | null {
        return this.Get('CreditsYTDBase');
    }
    set CreditsYTDBase(value: number | null) {
        this.Set('CreditsYTDBase', value);
    }

    /**
    * * Field Name: CurrencyID
    * * Display Name: Currency ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Currencies (vwCurrencies.ID)
    * * Description: Currency of the reported balance.
    */
    get CurrencyID(): string {
        return this.Get('CurrencyID');
    }
    set CurrencyID(value: string) {
        this.Set('CurrencyID', value);
    }

    /**
    * * Field Name: ExchangeRate
    * * Display Name: Exchange Rate
    * * SQL Data Type: decimal(12, 6)
    * * Default Value: 1.0
    * * Description: Rate to convert to base.
    */
    get ExchangeRate(): number {
        return this.Get('ExchangeRate');
    }
    set ExchangeRate(value: number) {
        this.Set('ExchangeRate', value);
    }

    /**
    * * Field Name: BookCode
    * * Display Name: Book Code
    * * SQL Data Type: varchar(20)
    * * Default Value: BASE
    * * Description: Accounting book code (e.g., BASE).
    */
    get BookCode(): string {
        return this.Get('BookCode');
    }
    set BookCode(value: string) {
        this.Set('BookCode', value);
    }

    /**
    * * Field Name: DataOrigin
    * * Display Name: Data Origin
    * * SQL Data Type: varchar(20)
    * * Default Value: ExternalGL
    * * Value List Type: List
    * * Possible Values 
    *   * ExternalGL
    *   * Manual
    *   * SoundPost
    *   * WhatIf
    * * Description: Origin of data (ExternalGL/SoundPost/Manual/WhatIf).
    */
    get DataOrigin(): 'ExternalGL' | 'Manual' | 'SoundPost' | 'WhatIf' {
        return this.Get('DataOrigin');
    }
    set DataOrigin(value: 'ExternalGL' | 'Manual' | 'SoundPost' | 'WhatIf') {
        this.Set('DataOrigin', value);
    }

    /**
    * * Field Name: SourceExtractJSON
    * * Display Name: Source Extract JSON
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Optional raw extract metadata.
    */
    get SourceExtractJSON(): string | null {
        return this.Get('SourceExtractJSON');
    }
    set SourceExtractJSON(value: string | null) {
        this.Set('SourceExtractJSON', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC created timestamp.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: UTC updated timestamp.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: PeriodStartDate
    * * Display Name: Period Start Date
    * * SQL Data Type: datetimeoffset
    * * Default Value: 1900-01-01
    * * Description: Start date used when fetching balances (e.g., Jan 1 of run year).
    */
    get PeriodStartDate(): Date {
        return this.Get('PeriodStartDate');
    }
    set PeriodStartDate(value: Date) {
        this.Set('PeriodStartDate', value);
    }

    /**
    * * Field Name: BeginningBalanceBase
    * * Display Name: Beginning Balance Base
    * * SQL Data Type: decimal(18, 2)
    * * Description: Beginning balance at PeriodStartDate in base currency (from Intacct startbalance).
    */
    get BeginningBalanceBase(): number | null {
        return this.Get('BeginningBalanceBase');
    }
    set BeginningBalanceBase(value: number | null) {
        this.Set('BeginningBalanceBase', value);
    }

    /**
    * * Field Name: PeriodNetActivityBase
    * * Display Name: Period Net Activity Base
    * * SQL Data Type: decimal(18, 2)
    * * Description: Net activity for the period [PeriodStartDate..AsOfDate] in base currency (from Intacct periodbalance).
    */
    get PeriodNetActivityBase(): number | null {
        return this.Get('PeriodNetActivityBase');
    }
    set PeriodNetActivityBase(value: number | null) {
        this.Set('PeriodNetActivityBase', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: CompanyIntegration
    * * Display Name: Company Integration
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIntegration(): string {
        return this.Get('CompanyIntegration');
    }

    /**
    * * Field Name: GLAccount
    * * Display Name: GL Account
    * * SQL Data Type: varchar(100)
    */
    get GLAccount(): string {
        return this.Get('GLAccount');
    }

    /**
    * * Field Name: Currency
    * * Display Name: Currency
    * * SQL Data Type: varchar(100)
    */
    get Currency(): string {
        return this.Get('Currency');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}
